import { useState, useRef, useCallback, useEffect } from 'react';
import 'fs';
import 'path';

// src/ApiClient.ts
// HTTP client for AsterMind backend API
/**
 * Map backend source format to client Source interface.
 * Handles both canonical (title/score) and legacy (heading/no-score) field names.
 */
function mapSource(raw) {
    return {
        title: raw.title || raw.heading || 'Document',
        snippet: raw.content || '',
        relevance: raw.score ?? 0,
        documentId: raw.documentId,
        fullContent: raw.fullContent,
        downloadUrl: raw.downloadUrl,
        documentName: raw.documentName,
        sourceType: raw.sourceType,
        chunkId: raw.chunkId
    };
}
/**
 * HTTP/SSE client for AsterMind backend
 */
class ApiClient {
    constructor(baseUrl, apiKey) {
        this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
        this.apiKey = apiKey;
    }
    /**
     * Send chat message and get complete response
     */
    async chat(message, options) {
        const response = await fetch(`${this.baseUrl}/api/external/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': this.apiKey
            },
            body: JSON.stringify({
                message,
                sessionId: options?.sessionId,
                context: options?.context
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        // Normalize response: accept both 'reply' (canonical) and 'answer' (legacy)
        return {
            ...data,
            reply: data.reply || data.answer || '',
            sources: (data.sources || []).map(mapSource)
        };
    }
    /**
     * Send chat message with streaming response via SSE
     */
    async chatStream(message, options) {
        const response = await fetch(`${this.baseUrl}/api/external/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': this.apiKey
            },
            body: JSON.stringify({
                message,
                sessionId: options?.sessionId,
                context: options?.context
            })
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.message || `HTTP ${response.status}`);
        }
        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Streaming not supported');
        }
        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';
        let sources = [];
        let sessionId;
        let messageId;
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        // Event type handled in data line
                    }
                    else if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.text !== undefined) {
                                // Token event
                                fullText += data.text;
                                options.onToken?.(data.text);
                            }
                            else if (data.sources !== undefined) {
                                // Sources event - map backend format to client format
                                sources = (data.sources || []).map(mapSource);
                                options.onSources?.(sources);
                            }
                            else if (data.sessionId !== undefined) {
                                // Done event
                                sessionId = data.sessionId;
                                messageId = data.messageId;
                            }
                        }
                        catch {
                            // Skip malformed JSON
                        }
                    }
                }
            }
            options.onComplete?.({ fullText, sessionId, messageId, sources });
        }
        catch (error) {
            options.onError?.(error);
        }
    }
    /**
     * Get general documents for caching.
     * Normalizes field names from both canonical (title) and legacy (name) formats.
     */
    async getGeneralDocs(since) {
        const params = new URLSearchParams();
        if (since) {
            params.set('since', since);
        }
        const url = `${this.baseUrl}/api/external/docs${params.toString() ? '?' + params : ''}`;
        const response = await fetch(url, {
            headers: {
                'X-API-Key': this.apiKey
            }
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        // Normalize: accept both 'title' (canonical) and 'name' (legacy on-prem)
        return (data.documents || []).map((doc) => ({
            id: doc.id,
            title: doc.title || doc.name || 'Untitled',
            updatedAt: doc.updatedAt || doc.updated_at || new Date().toISOString(),
        }));
    }
    /**
     * Get API status, quota, and system settings.
     * Normalizes response from both canonical and legacy backend formats.
     */
    async getStatus() {
        const response = await fetch(`${this.baseUrl}/api/external/status`, {
            headers: {
                'X-API-Key': this.apiKey
            }
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        // Normalize: accept both canonical (apiKey) and legacy (key) structures
        const apiKeyData = data.apiKey || data.key || {};
        const scopes = apiKeyData.scopes || apiKeyData.permissions || ['chat'];
        // Normalize quota: accept both canonical (perMinute/perDay) and legacy structures
        const quota = data.quota || {};
        const perMinute = quota.perMinute || {
            limit: apiKeyData.rateLimit?.limit || quota.rateLimitPerMinute || 30,
            remaining: apiKeyData.rateLimit?.remaining ?? quota.rateLimitPerMinute ?? 30,
        };
        const perDay = quota.perDay || {
            limit: quota.rateLimitPerDay || perMinute.limit * 60 * 24,
            remaining: quota.rateLimitPerDay || perMinute.limit * 60 * 24,
        };
        // Normalize systemSettings: accept both canonical and legacy (system) structures
        const settings = data.systemSettings || data.system || {};
        const cacheRetentionHours = settings.cacheRetentionHours ??
            (settings.cacheRetentionDays ? settings.cacheRetentionDays * 24 : undefined);
        return {
            status: data.status || 'active',
            apiKey: {
                id: apiKeyData.id || '',
                scopes,
            },
            quota: { perMinute, perDay },
            systemSettings: {
                cacheRetentionHours: cacheRetentionHours ?? 720,
                maintenanceMode: settings.maintenanceMode ?? false,
                maintenanceMessage: settings.maintenanceMessage,
                forceOfflineClients: settings.forceOfflineClients ?? false,
            },
        };
    }
    /**
     * Health check (no auth required)
     */
    async health() {
        const response = await fetch(`${this.baseUrl}/api/external/health`);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
    }
}

// src/SalesApiClient.ts
// API client for Sales Agent external endpoints (/api/external/sales/*)
/**
 * Client for the Sales Agent external API endpoints.
 * Handles visitor tracking, sales-aware chat, and lead capture.
 */
class SalesApiClient {
    constructor(apiUrl, apiKey) {
        // Normalize base URL (strip trailing slash)
        this.baseUrl = apiUrl.replace(/\/$/, '');
        this.apiKey = apiKey;
    }
    /**
     * Initialize or resume a visitor session.
     * Call on first page load to register the visitor with the backend.
     */
    async initVisitor(visitorId, pageUrl, referrer, utmParams) {
        return this.post('visitor/init', {
            visitorId,
            pageUrl,
            referrer,
            utmParams,
        });
    }
    /**
     * Track a page view event.
     * Call when the visitor navigates to a new page.
     */
    async trackPageView(visitorId, url, title, timeOnPageMs, scrollDepth) {
        return this.post('visitor/pageview', {
            visitorId,
            url,
            title,
            timeOnPageMs,
            scrollDepth,
        });
    }
    /**
     * Associate a visitor with identity data (email, name, etc.).
     * Call when the visitor provides identifying information.
     */
    async identifyVisitor(visitorId, data) {
        return this.post('visitor/identify', {
            visitorId,
            ...data,
        });
    }
    /**
     * Send a sales-aware chat message.
     * Returns the assistant reply along with sales context (lead score, CTA buttons, etc.).
     */
    async salesChat(message, options = {}) {
        return this.post('chat', {
            message,
            ...options,
        });
    }
    /**
     * Submit a lead capture form.
     * Creates or updates a lead record in the backend.
     */
    async captureLeadForm(data) {
        return this.post('lead/capture', data);
    }
    // ─── Private ─────────────────────────────────────────────────────────────────
    async post(path, body) {
        const url = `${this.baseUrl}/api/external/sales/${path}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Api-Key': this.apiKey,
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(err.error || `Sales API error: ${response.status}`);
        }
        return response.json();
    }
}

const instanceOfAny = (object, constructors) => constructors.some((c) => object instanceof c);

let idbProxyableTypes;
let cursorAdvanceMethods;
// This is a function to prevent it throwing up in node environments.
function getIdbProxyableTypes() {
    return (idbProxyableTypes ||
        (idbProxyableTypes = [
            IDBDatabase,
            IDBObjectStore,
            IDBIndex,
            IDBCursor,
            IDBTransaction,
        ]));
}
// This is a function to prevent it throwing up in node environments.
function getCursorAdvanceMethods() {
    return (cursorAdvanceMethods ||
        (cursorAdvanceMethods = [
            IDBCursor.prototype.advance,
            IDBCursor.prototype.continue,
            IDBCursor.prototype.continuePrimaryKey,
        ]));
}
const cursorRequestMap = new WeakMap();
const transactionDoneMap = new WeakMap();
const transactionStoreNamesMap = new WeakMap();
const transformCache = new WeakMap();
const reverseTransformCache = new WeakMap();
function promisifyRequest(request) {
    const promise = new Promise((resolve, reject) => {
        const unlisten = () => {
            request.removeEventListener('success', success);
            request.removeEventListener('error', error);
        };
        const success = () => {
            resolve(wrap(request.result));
            unlisten();
        };
        const error = () => {
            reject(request.error);
            unlisten();
        };
        request.addEventListener('success', success);
        request.addEventListener('error', error);
    });
    promise
        .then((value) => {
        // Since cursoring reuses the IDBRequest (*sigh*), we cache it for later retrieval
        // (see wrapFunction).
        if (value instanceof IDBCursor) {
            cursorRequestMap.set(value, request);
        }
        // Catching to avoid "Uncaught Promise exceptions"
    })
        .catch(() => { });
    // This mapping exists in reverseTransformCache but doesn't doesn't exist in transformCache. This
    // is because we create many promises from a single IDBRequest.
    reverseTransformCache.set(promise, request);
    return promise;
}
function cacheDonePromiseForTransaction(tx) {
    // Early bail if we've already created a done promise for this transaction.
    if (transactionDoneMap.has(tx))
        return;
    const done = new Promise((resolve, reject) => {
        const unlisten = () => {
            tx.removeEventListener('complete', complete);
            tx.removeEventListener('error', error);
            tx.removeEventListener('abort', error);
        };
        const complete = () => {
            resolve();
            unlisten();
        };
        const error = () => {
            reject(tx.error || new DOMException('AbortError', 'AbortError'));
            unlisten();
        };
        tx.addEventListener('complete', complete);
        tx.addEventListener('error', error);
        tx.addEventListener('abort', error);
    });
    // Cache it for later retrieval.
    transactionDoneMap.set(tx, done);
}
let idbProxyTraps = {
    get(target, prop, receiver) {
        if (target instanceof IDBTransaction) {
            // Special handling for transaction.done.
            if (prop === 'done')
                return transactionDoneMap.get(target);
            // Polyfill for objectStoreNames because of Edge.
            if (prop === 'objectStoreNames') {
                return target.objectStoreNames || transactionStoreNamesMap.get(target);
            }
            // Make tx.store return the only store in the transaction, or undefined if there are many.
            if (prop === 'store') {
                return receiver.objectStoreNames[1]
                    ? undefined
                    : receiver.objectStore(receiver.objectStoreNames[0]);
            }
        }
        // Else transform whatever we get back.
        return wrap(target[prop]);
    },
    set(target, prop, value) {
        target[prop] = value;
        return true;
    },
    has(target, prop) {
        if (target instanceof IDBTransaction &&
            (prop === 'done' || prop === 'store')) {
            return true;
        }
        return prop in target;
    },
};
function replaceTraps(callback) {
    idbProxyTraps = callback(idbProxyTraps);
}
function wrapFunction(func) {
    // Due to expected object equality (which is enforced by the caching in `wrap`), we
    // only create one new func per func.
    // Edge doesn't support objectStoreNames (booo), so we polyfill it here.
    if (func === IDBDatabase.prototype.transaction &&
        !('objectStoreNames' in IDBTransaction.prototype)) {
        return function (storeNames, ...args) {
            const tx = func.call(unwrap(this), storeNames, ...args);
            transactionStoreNamesMap.set(tx, storeNames.sort ? storeNames.sort() : [storeNames]);
            return wrap(tx);
        };
    }
    // Cursor methods are special, as the behaviour is a little more different to standard IDB. In
    // IDB, you advance the cursor and wait for a new 'success' on the IDBRequest that gave you the
    // cursor. It's kinda like a promise that can resolve with many values. That doesn't make sense
    // with real promises, so each advance methods returns a new promise for the cursor object, or
    // undefined if the end of the cursor has been reached.
    if (getCursorAdvanceMethods().includes(func)) {
        return function (...args) {
            // Calling the original function with the proxy as 'this' causes ILLEGAL INVOCATION, so we use
            // the original object.
            func.apply(unwrap(this), args);
            return wrap(cursorRequestMap.get(this));
        };
    }
    return function (...args) {
        // Calling the original function with the proxy as 'this' causes ILLEGAL INVOCATION, so we use
        // the original object.
        return wrap(func.apply(unwrap(this), args));
    };
}
function transformCachableValue(value) {
    if (typeof value === 'function')
        return wrapFunction(value);
    // This doesn't return, it just creates a 'done' promise for the transaction,
    // which is later returned for transaction.done (see idbObjectHandler).
    if (value instanceof IDBTransaction)
        cacheDonePromiseForTransaction(value);
    if (instanceOfAny(value, getIdbProxyableTypes()))
        return new Proxy(value, idbProxyTraps);
    // Return the same value back if we're not going to transform it.
    return value;
}
function wrap(value) {
    // We sometimes generate multiple promises from a single IDBRequest (eg when cursoring), because
    // IDB is weird and a single IDBRequest can yield many responses, so these can't be cached.
    if (value instanceof IDBRequest)
        return promisifyRequest(value);
    // If we've already transformed this value before, reuse the transformed value.
    // This is faster, but it also provides object equality.
    if (transformCache.has(value))
        return transformCache.get(value);
    const newValue = transformCachableValue(value);
    // Not all types are transformed.
    // These may be primitive types, so they can't be WeakMap keys.
    if (newValue !== value) {
        transformCache.set(value, newValue);
        reverseTransformCache.set(newValue, value);
    }
    return newValue;
}
const unwrap = (value) => reverseTransformCache.get(value);

/**
 * Open a database.
 *
 * @param name Name of the database.
 * @param version Schema version.
 * @param callbacks Additional callbacks.
 */
function openDB(name, version, { blocked, upgrade, blocking, terminated } = {}) {
    const request = indexedDB.open(name, version);
    const openPromise = wrap(request);
    if (upgrade) {
        request.addEventListener('upgradeneeded', (event) => {
            upgrade(wrap(request.result), event.oldVersion, event.newVersion, wrap(request.transaction), event);
        });
    }
    if (blocked) {
        request.addEventListener('blocked', (event) => blocked(
        // Casting due to https://github.com/microsoft/TypeScript-DOM-lib-generator/pull/1405
        event.oldVersion, event.newVersion, event));
    }
    openPromise
        .then((db) => {
        if (terminated)
            db.addEventListener('close', () => terminated());
        if (blocking) {
            db.addEventListener('versionchange', (event) => blocking(event.oldVersion, event.newVersion, event));
        }
    })
        .catch(() => { });
    return openPromise;
}

const readMethods = ['get', 'getKey', 'getAll', 'getAllKeys', 'count'];
const writeMethods = ['put', 'add', 'delete', 'clear'];
const cachedMethods = new Map();
function getMethod(target, prop) {
    if (!(target instanceof IDBDatabase &&
        !(prop in target) &&
        typeof prop === 'string')) {
        return;
    }
    if (cachedMethods.get(prop))
        return cachedMethods.get(prop);
    const targetFuncName = prop.replace(/FromIndex$/, '');
    const useIndex = prop !== targetFuncName;
    const isWrite = writeMethods.includes(targetFuncName);
    if (
    // Bail if the target doesn't exist on the target. Eg, getAll isn't in Edge.
    !(targetFuncName in (useIndex ? IDBIndex : IDBObjectStore).prototype) ||
        !(isWrite || readMethods.includes(targetFuncName))) {
        return;
    }
    const method = async function (storeName, ...args) {
        // isWrite ? 'readwrite' : undefined gzipps better, but fails in Edge :(
        const tx = this.transaction(storeName, isWrite ? 'readwrite' : 'readonly');
        let target = tx.store;
        if (useIndex)
            target = target.index(args.shift());
        // Must reject if op rejects.
        // If it's a write operation, must reject if tx.done rejects.
        // Must reject with op rejection first.
        // Must resolve with op value.
        // Must handle both promises (no unhandled rejections)
        return (await Promise.all([
            target[targetFuncName](...args),
            isWrite && tx.done,
        ]))[0];
    };
    cachedMethods.set(prop, method);
    return method;
}
replaceTraps((oldTraps) => ({
    ...oldTraps,
    get: (target, prop, receiver) => getMethod(target, prop) || oldTraps.get(target, prop, receiver),
    has: (target, prop) => !!getMethod(target, prop) || oldTraps.has(target, prop),
}));

// src/CyberneticCache.ts
// IndexedDB cache for offline document storage
/**
 * Document cache for offline RAG fallback
 */
class CyberneticCache {
    constructor(config) {
        this.db = null;
        this.dbPromise = null;
        this.documentCount = 0;
        this.lastSyncAt = null;
        this.config = config;
        if (config.storage === 'indexeddb' && typeof indexedDB !== 'undefined') {
            this.dbPromise = this.initDB();
        }
        else if (config.storage === 'localstorage') {
            // Load cached metadata from localStorage
            this.loadLocalStorageMetadata();
        }
    }
    /**
     * Load metadata from localStorage
     */
    loadLocalStorageMetadata() {
        try {
            const meta = localStorage.getItem('cybernetic-cache-meta');
            if (meta) {
                const parsed = JSON.parse(meta);
                this.documentCount = parsed.documentCount || 0;
                this.lastSyncAt = parsed.lastSyncAt || null;
            }
        }
        catch {
            // Ignore parse errors
        }
    }
    /**
     * Initialize IndexedDB
     */
    async initDB() {
        this.db = await openDB('cybernetic-cache', 1, {
            upgrade(db) {
                // Documents store
                if (!db.objectStoreNames.contains('documents')) {
                    const docStore = db.createObjectStore('documents', { keyPath: 'id' });
                    docStore.createIndex('by-updated', 'updatedAt');
                }
                // Metadata store
                if (!db.objectStoreNames.contains('metadata')) {
                    db.createObjectStore('metadata', { keyPath: 'key' });
                }
            }
        });
        // Load cached metadata
        await this.loadMetadata();
        return this.db;
    }
    /**
     * Get database instance
     */
    async getDB() {
        if (this.db)
            return this.db;
        if (this.dbPromise)
            return this.dbPromise;
        throw new Error('Database not available');
    }
    /**
     * Load cached metadata (count, last sync)
     */
    async loadMetadata() {
        if (!this.db)
            return;
        const countMeta = await this.db.get('metadata', 'documentCount');
        this.documentCount = countMeta?.value || 0;
        const syncMeta = await this.db.get('metadata', 'lastSyncAt');
        this.lastSyncAt = syncMeta?.value || null;
    }
    /**
     * Store documents in cache
     */
    async store(documents) {
        if (this.config.storage === 'localstorage') {
            return this.storeLocalStorage(documents);
        }
        const db = await this.getDB();
        const tx = db.transaction(['documents', 'metadata'], 'readwrite');
        // Store documents
        for (const doc of documents) {
            await tx.objectStore('documents').put(doc);
        }
        // Update metadata
        this.documentCount = await tx.objectStore('documents').count();
        this.lastSyncAt = new Date().toISOString();
        await tx.objectStore('metadata').put({
            key: 'documentCount',
            value: this.documentCount
        });
        await tx.objectStore('metadata').put({
            key: 'lastSyncAt',
            value: this.lastSyncAt
        });
        await tx.done;
    }
    /**
     * Retrieve all cached documents
     */
    async retrieve() {
        if (this.config.storage === 'localstorage') {
            return this.retrieveLocalStorage();
        }
        const db = await this.getDB();
        return db.getAll('documents');
    }
    /**
     * Get last sync timestamp
     */
    async getLastSync() {
        return this.lastSyncAt;
    }
    /**
     * Get cache status
     */
    getStatus() {
        const now = Date.now();
        const lastSync = this.lastSyncAt ? new Date(this.lastSyncAt).getTime() : 0;
        const isStale = now - lastSync > this.config.maxAge;
        return {
            documentCount: this.documentCount,
            lastSyncAt: this.lastSyncAt,
            cacheSize: this.documentCount * 5000, // Rough estimate: ~5KB per doc
            isStale
        };
    }
    /**
     * Clear all cached data
     */
    async clear() {
        if (this.config.storage === 'localstorage') {
            localStorage.removeItem('cybernetic-cache-docs');
            localStorage.removeItem('cybernetic-cache-meta');
            this.documentCount = 0;
            this.lastSyncAt = null;
            return;
        }
        const db = await this.getDB();
        const tx = db.transaction(['documents', 'metadata'], 'readwrite');
        await tx.objectStore('documents').clear();
        await tx.objectStore('metadata').clear();
        await tx.done;
        this.documentCount = 0;
        this.lastSyncAt = null;
    }
    // ==================== LocalStorage fallback ====================
    storeLocalStorage(documents) {
        try {
            localStorage.setItem('cybernetic-cache-docs', JSON.stringify(documents));
            this.documentCount = documents.length;
            this.lastSyncAt = new Date().toISOString();
            localStorage.setItem('cybernetic-cache-meta', JSON.stringify({
                documentCount: this.documentCount,
                lastSyncAt: this.lastSyncAt
            }));
        }
        catch {
            // LocalStorage full - clear and retry with limit
            localStorage.removeItem('cybernetic-cache-docs');
            const limited = documents.slice(0, 50); // Keep only 50 docs
            localStorage.setItem('cybernetic-cache-docs', JSON.stringify(limited));
            this.documentCount = limited.length;
        }
    }
    retrieveLocalStorage() {
        try {
            const data = localStorage.getItem('cybernetic-cache-docs');
            return data ? JSON.parse(data) : [];
        }
        catch {
            return [];
        }
    }
}

// src/CyberneticLocalRAG.ts
// Local RAG processing for offline fallback
class CyberneticLocalRAG {
    constructor() {
        this.documents = [];
        this.idf = new Map();
        this.indexed = false;
        this.loadedFromExport = false;
        this.vocabulary = new Map(); // term -> index for sparse vectors
    }
    /**
     * Check if documents are indexed
     */
    isIndexed() {
        return this.indexed && this.documents.length > 0;
    }
    /**
     * Index documents for local search
     */
    async index(documents) {
        this.documents = [];
        this.idf = new Map();
        // Tokenize and build document frequency
        const docFreq = new Map();
        for (const doc of documents) {
            // Skip documents without content
            if (!doc.content) {
                continue;
            }
            const tokens = this.tokenize(doc.content);
            const uniqueTokens = new Set(tokens);
            // Count document frequency
            for (const token of uniqueTokens) {
                docFreq.set(token, (docFreq.get(token) || 0) + 1);
            }
            // Store indexed document
            this.documents.push({
                id: doc.id,
                title: doc.title,
                content: doc.content,
                tokens,
                tfidf: new Map() // Computed after IDF is known
            });
        }
        // Compute IDF
        const N = documents.length;
        for (const [term, df] of docFreq) {
            this.idf.set(term, Math.log((N + 1) / (df + 1)) + 1);
        }
        // Compute TF-IDF for each document
        for (const doc of this.documents) {
            const termFreq = this.computeTermFrequency(doc.tokens);
            for (const [term, tf] of termFreq) {
                const idf = this.idf.get(term) || 1;
                doc.tfidf.set(term, tf * idf);
            }
        }
        this.indexed = true;
    }
    /**
     * Process query and generate response
     */
    async ask(query) {
        if (!this.indexed || this.documents.length === 0) {
            return {
                answer: 'I don\'t have any information available offline.',
                sources: [],
                topScore: 0
            };
        }
        // Tokenize query and compute TF-IDF
        const queryTokens = this.tokenize(query);
        const queryTf = this.computeTermFrequency(queryTokens);
        const queryTfidf = new Map();
        for (const [term, tf] of queryTf) {
            const idf = this.idf.get(term) || 1;
            queryTfidf.set(term, tf * idf);
        }
        // Compute similarity scores
        const scores = this.documents.map(doc => ({
            doc,
            score: this.cosineSimilarity(queryTfidf, doc.tfidf)
        }));
        // Sort by score descending
        scores.sort((a, b) => b.score - a.score);
        // Take top 3 results
        const topResults = scores.slice(0, 3).filter(r => r.score > 0.1);
        if (topResults.length === 0) {
            return {
                answer: 'I couldn\'t find relevant information for your question in my offline data.',
                sources: [],
                topScore: scores[0]?.score || 0
            };
        }
        // Generate simple answer from top result
        const topDoc = topResults[0].doc;
        const snippet = this.extractRelevantSnippet(topDoc.content, queryTokens);
        return {
            answer: snippet,
            sources: topResults.map(r => ({
                title: r.doc.title,
                snippet: r.doc.content.substring(0, 200) + '...',
                score: r.score
            })),
            topScore: topResults[0].score
        };
    }
    /**
     * Reset the index
     */
    reset() {
        this.documents = [];
        this.idf = new Map();
        this.vocabulary = new Map();
        this.indexed = false;
        this.loadedFromExport = false;
        this.exportVersion = undefined;
        this.exportedAt = undefined;
    }
    /**
     * Load pre-computed vectors from an export file
     * This skips client-side TF-IDF computation by using pre-computed values
     *
     * @param exportData - The offline vector export data
     */
    async loadFromExport(exportData) {
        this.reset();
        // Load pre-computed IDF from export
        this.idf = new Map(Object.entries(exportData.tfidfIndex.idf));
        // Build vocabulary mapping from IDF keys
        let vocabIndex = 0;
        for (const term of this.idf.keys()) {
            this.vocabulary.set(term, vocabIndex++);
        }
        // Load documents with pre-computed TF-IDF vectors
        for (const doc of exportData.documents) {
            // Convert sparse vector to Map
            const tfidfMap = new Map();
            for (const [termIndex, weight] of doc.tfidfVector) {
                // Find the term from vocabulary by index
                for (const [term, idx] of this.vocabulary) {
                    if (idx === termIndex) {
                        tfidfMap.set(term, weight);
                        break;
                    }
                }
            }
            this.documents.push({
                id: doc.id,
                title: doc.title,
                content: doc.content,
                tokens: [], // Not needed when loading from export
                tfidf: tfidfMap
            });
        }
        this.indexed = true;
        this.loadedFromExport = true;
        this.exportVersion = exportData.version;
        this.exportedAt = exportData.meta.exportedAt;
        // Debug: loaded ${this.documents.length} documents from export
    }
    /**
     * Get export status information
     */
    getExportStatus() {
        return {
            loaded: this.indexed,
            loadedFromExport: this.loadedFromExport,
            documentCount: this.documents.length,
            chunkCount: this.documents.length, // Each document is a chunk
            exportVersion: this.exportVersion,
            exportedAt: this.exportedAt
        };
    }
    // ==================== PRIVATE METHODS ====================
    /**
     * Tokenize text into words
     */
    tokenize(text) {
        if (!text || typeof text !== 'string') {
            return [];
        }
        return text
            .toLowerCase()
            .replace(/[^\w\s]/g, ' ')
            .split(/\s+/)
            .filter(token => token.length > 2 && !this.isStopWord(token));
    }
    /**
     * Compute term frequency
     */
    computeTermFrequency(tokens) {
        const freq = new Map();
        for (const token of tokens) {
            freq.set(token, (freq.get(token) || 0) + 1);
        }
        // Normalize by document length
        const maxFreq = Math.max(...freq.values());
        if (maxFreq > 0) {
            for (const [term, f] of freq) {
                freq.set(term, f / maxFreq);
            }
        }
        return freq;
    }
    /**
     * Compute cosine similarity between two TF-IDF vectors
     */
    cosineSimilarity(vec1, vec2) {
        let dotProduct = 0;
        let norm1 = 0;
        let norm2 = 0;
        for (const [term, val1] of vec1) {
            const val2 = vec2.get(term) || 0;
            dotProduct += val1 * val2;
            norm1 += val1 * val1;
        }
        for (const val of vec2.values()) {
            norm2 += val * val;
        }
        if (norm1 === 0 || norm2 === 0)
            return 0;
        return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
    }
    /**
     * Extract most relevant snippet from content
     */
    extractRelevantSnippet(content, queryTokens) {
        const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
        // Score sentences by query token overlap
        const scored = sentences.map(sentence => {
            const sentenceTokens = new Set(this.tokenize(sentence));
            let score = 0;
            for (const qt of queryTokens) {
                if (sentenceTokens.has(qt))
                    score++;
            }
            return { sentence: sentence.trim(), score };
        });
        scored.sort((a, b) => b.score - a.score);
        // Take top 2-3 sentences
        const topSentences = scored.slice(0, 3).filter(s => s.score > 0);
        if (topSentences.length === 0) {
            return content.substring(0, 300) + '...';
        }
        return topSentences.map(s => s.sentence).join('. ') + '.';
    }
    /**
     * Check if word is a stop word
     */
    isStopWord(word) {
        const stopWords = new Set([
            'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
            'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during',
            'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had',
            'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might',
            'this', 'that', 'these', 'those', 'it', 'its', 'they', 'them', 'their',
            'what', 'which', 'who', 'whom', 'when', 'where', 'why', 'how',
            'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some',
            'such', 'no', 'not', 'only', 'same', 'so', 'than', 'too', 'very'
        ]);
        return stopWords.has(word);
    }
}

// src/CyberneticOfflineStorage.ts
// IndexedDB storage for offline vector exports
/**
 * IndexedDB storage for offline vector exports
 * Provides persistent caching of pre-computed TF-IDF vectors
 */
class CyberneticOfflineStorage {
    constructor() {
        this.db = null;
        this.dbPromise = null;
        this.dbName = 'cybernetic-offline-vectors';
        this.dbVersion = 1;
    }
    /**
     * Initialize IndexedDB for vector storage
     */
    async initDB() {
        if (this.db) {
            return this.db;
        }
        if (this.dbPromise) {
            return this.dbPromise;
        }
        this.dbPromise = openDB(this.dbName, this.dbVersion, {
            upgrade(db) {
                if (!db.objectStoreNames.contains('vectors')) {
                    db.createObjectStore('vectors', { keyPath: 'id' });
                }
            }
        });
        this.db = await this.dbPromise;
        return this.db;
    }
    /**
     * Store vector export in IndexedDB
     */
    async store(id, exportData) {
        const db = await this.getDB();
        await db.put('vectors', {
            id,
            exportVersion: exportData.version,
            exportedAt: exportData.meta.exportedAt,
            type: exportData.type,
            data: exportData
        });
        // Debug: stored chunks
    }
    /**
     * Retrieve vector export from IndexedDB
     */
    async retrieve(id) {
        const db = await this.getDB();
        const record = await db.get('vectors', id);
        return record?.data ?? null;
    }
    /**
     * Check if cached vectors are valid (not expired)
     */
    async isValid(id, maxAge) {
        const db = await this.getDB();
        const record = await db.get('vectors', id);
        if (!record)
            return false;
        const exportTime = new Date(record.exportedAt).getTime();
        return Date.now() - exportTime < maxAge;
    }
    /**
     * Get cached export metadata without loading full data
     */
    async getMeta(id) {
        const db = await this.getDB();
        const record = await db.get('vectors', id);
        if (!record)
            return null;
        return {
            version: record.exportVersion,
            exportedAt: record.exportedAt,
            documentCount: record.data.meta.documentCount,
            chunkCount: record.data.meta.chunkCount,
            type: record.type
        };
    }
    /**
     * Check if any cached vectors exist
     */
    async hasData() {
        const db = await this.getDB();
        const count = await db.count('vectors');
        return count > 0;
    }
    /**
     * Get all cached export IDs
     */
    async getStoredIds() {
        const db = await this.getDB();
        return db.getAllKeys('vectors');
    }
    /**
     * Clear specific cached vectors
     */
    async delete(id) {
        const db = await this.getDB();
        await db.delete('vectors', id);
    }
    /**
     * Clear all cached vectors
     */
    async clear() {
        const db = await this.getDB();
        await db.clear('vectors');
        // Debug: cache cleared
    }
    /**
     * Get the database connection
     */
    async getDB() {
        if (this.db) {
            return this.db;
        }
        return this.initDB();
    }
    /**
     * Close database connection
     */
    close() {
        if (this.db) {
            this.db.close();
            this.db = null;
            this.dbPromise = null;
        }
    }
}

// src/WebSocketTransport.ts
// WebSocket transport for streaming chat via SaaS WebSocket API (chatws.astermind.ai)
/**
 * WebSocket transport for streaming chat.
 *
 * Maps the chat-handler Lambda WebSocket protocol to the client's StreamCallbacks:
 *   Server typing  → ignored (UI has its own indicator)
 *   Server chunk   → onToken(content)
 *   Server done    → onSources(sources) + onComplete(response)
 *   Server error   → onError(error)
 *
 * Connection is lazy — established on the first chatStream() call.
 * Reuses the same connection across multiple messages.
 * Reconnects with exponential backoff on close/error.
 */
class WebSocketTransport {
    constructor(wsUrl, apiKey, options) {
        this.ws = null;
        this.state = 'disconnected';
        this.reconnectAttempts = 0;
        this.connectionPromise = null;
        // Active streaming request tracking
        this.activeCallbacks = null;
        this.activeFullText = '';
        // Cleanup handler reference
        this.beforeUnloadHandler = null;
        // Promise callbacks for the active stream
        this._resolveStream = null;
        this._rejectStream = null;
        this.wsUrl = wsUrl;
        this.apiKey = apiKey;
        this.maxReconnectAttempts = options?.maxReconnectAttempts ?? 3;
        this.reconnectDelay = options?.reconnectDelay ?? 1000;
        this.connectionTimeout = options?.connectionTimeout ?? 10000;
    }
    /**
     * Current transport state
     */
    getState() {
        return this.state;
    }
    /**
     * Whether the WebSocket is currently connected
     */
    isConnected() {
        return this.state === 'connected' && this.ws?.readyState === WebSocket.OPEN;
    }
    /**
     * Connect to the WebSocket endpoint.
     * Appends the API key as a query parameter for authentication.
     * Returns a promise that resolves when the connection is open.
     */
    connect() {
        // Already connected
        if (this.isConnected()) {
            return Promise.resolve();
        }
        // Connection already in progress
        if (this.connectionPromise) {
            return this.connectionPromise;
        }
        this.state = 'connecting';
        this.connectionPromise = new Promise((resolve, reject) => {
            try {
                // Build URL with API key
                const separator = this.wsUrl.includes('?') ? '&' : '?';
                const fullUrl = `${this.wsUrl}${separator}apiKey=${encodeURIComponent(this.apiKey)}`;
                const ws = new WebSocket(fullUrl);
                this.ws = ws;
                // Connection timeout
                const timeout = setTimeout(() => {
                    if (ws.readyState !== WebSocket.OPEN) {
                        ws.close();
                        this.state = 'error';
                        this.connectionPromise = null;
                        reject(new Error('WebSocket connection timed out'));
                    }
                }, this.connectionTimeout);
                ws.onopen = () => {
                    clearTimeout(timeout);
                    this.state = 'connected';
                    this.reconnectAttempts = 0;
                    this.connectionPromise = null;
                    this.registerCleanup();
                    resolve();
                };
                ws.onmessage = (event) => {
                    this.handleMessage(event);
                };
                ws.onerror = () => {
                    clearTimeout(timeout);
                    this.state = 'error';
                    this.connectionPromise = null;
                    // The close event fires after error, which will trigger reconnect
                };
                ws.onclose = (event) => {
                    clearTimeout(timeout);
                    const wasConnected = this.state === 'connected';
                    this.state = 'disconnected';
                    this.connectionPromise = null;
                    this.unregisterCleanup();
                    // If we had an active request, report the error
                    if (this.activeCallbacks) {
                        this.activeCallbacks.onError?.({
                            code: 'WS_ERROR',
                            message: event.reason || `WebSocket closed (code: ${event.code})`
                        });
                        this.clearActiveRequest();
                    }
                    // Reject if we were trying to connect
                    if (!wasConnected) {
                        reject(new Error(event.reason || `WebSocket closed (code: ${event.code})`));
                    }
                };
            }
            catch (error) {
                this.state = 'error';
                this.connectionPromise = null;
                reject(error);
            }
        });
        return this.connectionPromise;
    }
    /**
     * Stream a chat message over WebSocket.
     *
     * Sends: { type: "sendMessage", message, sessionId?, wordLimit?, context? }
     * Receives: typing → chunk × N → done (with sources and metadata)
     */
    async chatStream(message, options) {
        const { sessionId, context, wordLimit, onToken, onSources, onComplete, onError } = options;
        // Ensure connection is established (lazy connect)
        try {
            await this.connect();
        }
        catch (error) {
            onError?.({
                code: 'WS_ERROR',
                message: `Failed to connect: ${error instanceof Error ? error.message : String(error)}`
            });
            throw error;
        }
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            const err = { code: 'WS_ERROR', message: 'WebSocket not connected' };
            onError?.(err);
            throw new Error(err.message);
        }
        // Set up active request tracking
        this.activeCallbacks = { onToken, onSources, onComplete, onError };
        this.activeFullText = '';
        this.activeSessionId = sessionId;
        // Build and send the message
        const payload = {
            type: 'sendMessage',
            message,
        };
        if (sessionId)
            payload.sessionId = sessionId;
        if (wordLimit)
            payload.wordLimit = wordLimit;
        if (context)
            payload.context = context;
        // Return a promise that resolves when done/error is received
        return new Promise((resolve, reject) => {
            // Store resolve/reject so handleMessage can call them
            this._resolveStream = resolve;
            this._rejectStream = reject;
            this.ws.send(JSON.stringify(payload));
        });
    }
    /**
     * Disconnect and clean up the WebSocket connection
     */
    disconnect() {
        this.unregisterCleanup();
        if (this.ws) {
            // Remove handlers to avoid triggering reconnect
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onmessage = null;
            this.ws.close();
            this.ws = null;
        }
        this.state = 'disconnected';
        this.connectionPromise = null;
        this.clearActiveRequest();
    }
    /**
     * Attempt reconnection with exponential backoff.
     * Used internally after connection loss.
     */
    async reconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.state = 'error';
            throw new Error(`Max reconnection attempts (${this.maxReconnectAttempts}) exceeded`);
        }
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
        this.reconnectAttempts++;
        // Reconnection is logged only in debug mode (via CyberneticClient)
        await new Promise(resolve => setTimeout(resolve, delay));
        // Ensure old connection is cleaned up
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.close();
            this.ws = null;
        }
        return this.connect();
    }
    // ==================== INTERNAL ====================
    /**
     * Handle incoming WebSocket messages from the chat-handler Lambda.
     *
     * Protocol:
     *   { type: 'typing', status: boolean }    → ignored
     *   { type: 'chunk', content: string }      → onToken(content)
     *   { type: 'done', sessionId, sources, metadata, messageId } → onSources + onComplete
     *   { type: 'error', error: string }        → onError
     */
    handleMessage(event) {
        if (!this.activeCallbacks)
            return;
        try {
            const data = JSON.parse(event.data);
            switch (data.type) {
                case 'typing':
                    // Ignored — the chatbot-template manages its own typing indicator
                    break;
                case 'chunk':
                    if (data.content) {
                        this.activeFullText += data.content;
                        this.activeCallbacks.onToken?.(data.content);
                    }
                    break;
                case 'done': {
                    // Map sources from chat-handler format to client Source format
                    const sources = this.mapSources(data.sources || []);
                    // Emit sources
                    this.activeCallbacks.onSources?.(sources);
                    // Build complete response
                    const response = {
                        reply: this.activeFullText,
                        confidence: 'high',
                        sources,
                        offline: false,
                        sessionId: data.sessionId || this.activeSessionId,
                    };
                    this.activeCallbacks.onComplete?.(response);
                    this._resolveStream?.();
                    this.clearActiveRequest();
                    break;
                }
                case 'error': {
                    const err = {
                        code: 'WS_ERROR',
                        message: data.error || 'Unknown WebSocket error'
                    };
                    this.activeCallbacks.onError?.(err);
                    this._rejectStream?.(new Error(err.message));
                    this.clearActiveRequest();
                    break;
                }
                default:
                    // Unknown message type — log and ignore
                    console.warn('[Cybernetic WS] Unknown message type:', data.type);
                    break;
            }
        }
        catch (error) {
            console.error('[Cybernetic WS] Failed to parse message:', error, event.data);
        }
    }
    /**
     * Map sources from the chat-handler WebSocket format to the client Source interface.
     *
     * Chat-handler sends: { heading, content, score, documentId, documentName }
     * Client expects:      { title, snippet, relevance, documentId, documentName }
     */
    mapSources(wsSources) {
        if (!Array.isArray(wsSources))
            return [];
        return wsSources.map((s) => ({
            title: s.title || s.heading || s.documentName || 'Document',
            snippet: s.content || '',
            relevance: s.score ?? 0,
            documentId: s.documentId,
            documentName: s.documentName,
            sourceType: s.sourceType || 'document',
            chunkId: s.chunkId,
            downloadUrl: s.downloadUrl,
        }));
    }
    /**
     * Clear active request state after completion or error
     */
    clearActiveRequest() {
        this.activeCallbacks = null;
        this.activeFullText = '';
        this._resolveStream = null;
        this._rejectStream = null;
    }
    /**
     * Register a beforeunload handler to close the WebSocket on page unload
     */
    registerCleanup() {
        if (typeof window !== 'undefined' && !this.beforeUnloadHandler) {
            this.beforeUnloadHandler = () => this.disconnect();
            window.addEventListener('beforeunload', this.beforeUnloadHandler);
        }
    }
    /**
     * Remove the beforeunload handler
     */
    unregisterCleanup() {
        if (typeof window !== 'undefined' && this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
            this.beforeUnloadHandler = null;
        }
    }
}

// src/CyberneticSessionStorage.ts
// Session storage manager for persisting chatbot session across page navigations
const DEFAULT_CONFIG = {
    enabled: true,
    storageKey: 'astermind_session',
    sessionTtl: 30 * 60 * 1000, // 30 minutes
    persistMessages: true,
    maxMessages: 50
};
/**
 * Session storage manager for persisting chatbot sessions
 *
 * Uses sessionStorage to maintain conversation continuity across
 * page navigations within the same browser session.
 */
class CyberneticSessionStorage {
    constructor(config) {
        this.cachedSession = null;
        this.config = { ...DEFAULT_CONFIG, ...config };
        // Load cached session on initialization
        if (this.config.enabled && this.isAvailable()) {
            this.cachedSession = this.loadFromStorage();
        }
    }
    /**
     * Check if sessionStorage is available
     */
    isAvailable() {
        if (typeof window === 'undefined')
            return false;
        try {
            const testKey = '__astermind_test__';
            window.sessionStorage.setItem(testKey, 'test');
            window.sessionStorage.removeItem(testKey);
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Get the current session ID (if valid)
     */
    getSessionId() {
        if (!this.config.enabled)
            return null;
        const session = this.getSession();
        return session?.sessionId ?? null;
    }
    /**
     * Get the full stored session (if valid)
     */
    getSession() {
        if (!this.config.enabled)
            return null;
        // Use cached session if available
        if (this.cachedSession) {
            if (this.isSessionValid(this.cachedSession)) {
                return this.cachedSession;
            }
            // Session expired, clear it
            this.clear();
            return null;
        }
        // Try loading from storage
        const session = this.loadFromStorage();
        if (session && this.isSessionValid(session)) {
            this.cachedSession = session;
            return session;
        }
        return null;
    }
    /**
     * Save/update session ID
     */
    saveSessionId(sessionId) {
        if (!this.config.enabled || !sessionId)
            return;
        const existingSession = this.getSession();
        const session = {
            sessionId,
            updatedAt: Date.now(),
            messages: existingSession?.messages ?? []
        };
        this.saveToStorage(session);
        this.cachedSession = session;
    }
    /**
     * Add a message to the conversation history
     */
    addMessage(message) {
        if (!this.config.enabled || !this.config.persistMessages)
            return;
        const session = this.getSession();
        if (!session)
            return;
        const newMessage = {
            ...message,
            id: this.generateMessageId(),
            timestamp: Date.now()
        };
        // Add new message and trim if necessary
        session.messages = session.messages ?? [];
        session.messages.push(newMessage);
        // Keep only the most recent messages
        if (session.messages.length > this.config.maxMessages) {
            session.messages = session.messages.slice(-this.config.maxMessages);
        }
        session.updatedAt = Date.now();
        this.saveToStorage(session);
        this.cachedSession = session;
    }
    /**
     * Get conversation messages
     */
    getMessages() {
        if (!this.config.enabled || !this.config.persistMessages)
            return [];
        const session = this.getSession();
        return session?.messages ?? [];
    }
    /**
     * Clear the stored session
     */
    clear() {
        this.cachedSession = null;
        if (!this.isAvailable())
            return;
        try {
            window.sessionStorage.removeItem(this.config.storageKey);
        }
        catch (error) {
            console.warn('[CyberneticSessionStorage] Failed to clear session:', error);
        }
    }
    /**
     * Start a new session (clears existing and optionally sets new ID)
     */
    startNewSession(sessionId) {
        this.clear();
        if (sessionId) {
            this.saveSessionId(sessionId);
        }
    }
    /**
     * Check if we have a valid stored session
     */
    hasValidSession() {
        return this.getSession() !== null;
    }
    /**
     * Get session info for debugging/status
     */
    getSessionInfo() {
        const session = this.getSession();
        if (!session) {
            return {
                hasSession: false,
                sessionId: null,
                messageCount: 0,
                lastUpdated: null,
                ttlRemaining: null
            };
        }
        const ttlRemaining = Math.max(0, this.config.sessionTtl - (Date.now() - session.updatedAt));
        return {
            hasSession: true,
            sessionId: session.sessionId,
            messageCount: session.messages?.length ?? 0,
            lastUpdated: new Date(session.updatedAt),
            ttlRemaining
        };
    }
    // ==================== PRIVATE METHODS ====================
    /**
     * Check if a session is still valid (not expired)
     */
    isSessionValid(session) {
        const age = Date.now() - session.updatedAt;
        return age < this.config.sessionTtl;
    }
    /**
     * Load session from sessionStorage
     */
    loadFromStorage() {
        if (!this.isAvailable())
            return null;
        try {
            const stored = window.sessionStorage.getItem(this.config.storageKey);
            if (!stored)
                return null;
            const session = JSON.parse(stored);
            // Validate structure
            if (!session.sessionId || typeof session.updatedAt !== 'number') {
                return null;
            }
            return session;
        }
        catch (error) {
            console.warn('[CyberneticSessionStorage] Failed to load session:', error);
            return null;
        }
    }
    /**
     * Save session to sessionStorage
     */
    saveToStorage(session) {
        if (!this.isAvailable())
            return;
        try {
            window.sessionStorage.setItem(this.config.storageKey, JSON.stringify(session));
        }
        catch (error) {
            console.warn('[CyberneticSessionStorage] Failed to save session:', error);
        }
    }
    /**
     * Generate a unique message ID
     */
    generateMessageId() {
        return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
}

// src/config.ts
// Configuration loading and validation
/** Default API URL when not specified */
const DEFAULT_API_URL = 'https://api.astermind.ai';
/**
 * Derive WebSocket URL from API URL for known SaaS domains.
 * Maps REST API domains to their WebSocket counterparts:
 *   chatapi.astermind.ai     → wss://chatws.astermind.ai
 *   chatapi-dev.astermind.ai → wss://chatws-dev.astermind.ai
 *   api.astermind.ai         → wss://chatws.astermind.ai
 *
 * Returns undefined for non-SaaS (on-prem) URLs.
 */
function deriveWsUrl(apiUrl) {
    try {
        const url = new URL(apiUrl);
        // chatapi[-env].astermind.ai → chatws[-env].astermind.ai
        const match = url.hostname.match(/^chatapi(-[\w]+)?\.astermind\.ai$/);
        if (match) {
            const envSuffix = match[1] || '';
            return `wss://chatws${envSuffix}.astermind.ai`;
        }
        // api.astermind.ai → chatws.astermind.ai (default SaaS)
        if (url.hostname === 'api.astermind.ai') {
            return 'wss://chatws.astermind.ai';
        }
    }
    catch {
        // Invalid URL, no WS derivation
    }
    return undefined;
}
/**
 * Try to load wsUrl from environment or globals.
 * Returns the first found wsUrl or undefined.
 */
function loadWsUrl() {
    // Vite env var
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const importMeta = globalThis.import?.meta?.env;
        if (importMeta?.VITE_ASTERMIND_RAG_WS_URL) {
            return importMeta.VITE_ASTERMIND_RAG_WS_URL;
        }
    }
    catch { /* not available */ }
    // Node.js / CRA env var
    if (typeof process !== 'undefined' && process.env) {
        if (process.env.ASTERMIND_RAG_WS_URL)
            return process.env.ASTERMIND_RAG_WS_URL;
        if (process.env.REACT_APP_ASTERMIND_RAG_WS_URL)
            return process.env.REACT_APP_ASTERMIND_RAG_WS_URL;
    }
    // SSR-injected config
    if (typeof window !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const injected = window.__ASTERMIND_CONFIG__;
        if (injected?.wsUrl)
            return injected.wsUrl;
        // Global config object
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const globalConfig = window.astermindConfig;
        if (globalConfig?.wsUrl)
            return globalConfig.wsUrl;
    }
    // Script data attribute
    if (typeof document !== 'undefined') {
        const script = document.querySelector('script[data-astermind-ws-url]');
        if (script) {
            const wsUrl = script.getAttribute('data-astermind-ws-url');
            if (wsUrl)
                return wsUrl;
        }
    }
    return undefined;
}
/**
 * Resolve the WebSocket URL for a given config.
 * Priority: explicit wsUrl → env var → auto-derived from apiUrl
 */
function resolveWsUrl(config) {
    // Explicit wsUrl takes priority
    if (config.wsUrl)
        return config.wsUrl;
    // Don't auto-detect if transport is forced to REST
    if (config.transport === 'rest')
        return undefined;
    // Try environment / globals
    const envWsUrl = loadWsUrl();
    if (envWsUrl)
        return envWsUrl;
    // Auto-derive from apiUrl for known SaaS domains
    return deriveWsUrl(config.apiUrl);
}
/**
 * Validate configuration
 */
function validateConfig(config) {
    if (!config || typeof config !== 'object') {
        throw new Error('Config must be an object');
    }
    const c = config;
    if (!c.apiUrl || typeof c.apiUrl !== 'string') {
        throw new Error('apiUrl is required and must be a string');
    }
    if (!c.apiKey || typeof c.apiKey !== 'string') {
        throw new Error('apiKey is required and must be a string');
    }
    if (!c.apiKey.startsWith('am_')) {
        throw new Error('apiKey must start with "am_"');
    }
    return true;
}
/**
 * Load config from Node.js process.env (for bundlers that replace process.env)
 */
function loadFromProcessEnv() {
    // Check if process.env exists (Node.js or bundler-injected)
    if (typeof process === 'undefined' || !process.env) {
        return null;
    }
    // Check for ASTERMIND_RAG_* env vars (non-prefixed, for Node.js/server environments)
    const apiKey = process.env.ASTERMIND_RAG_API_KEY;
    const apiUrl = process.env.ASTERMIND_RAG_API_SERVER_URL;
    if (apiKey) {
        return {
            apiKey,
            apiUrl: apiUrl || DEFAULT_API_URL,
            _source: 'env'
        };
    }
    // Check for CRA-style REACT_APP_* env vars
    const craApiKey = process.env.REACT_APP_ASTERMIND_RAG_API_KEY;
    const craApiUrl = process.env.REACT_APP_ASTERMIND_RAG_API_SERVER_URL;
    if (craApiKey) {
        return {
            apiKey: craApiKey,
            apiUrl: craApiUrl || DEFAULT_API_URL,
            _source: 'env'
        };
    }
    return null;
}
/**
 * Load config from Vite's import.meta.env (browser environment)
 * Note: This only works at build time when Vite replaces the variables
 */
function loadFromViteEnv() {
    // Check for window.__ASTERMIND_CONFIG__ (SSR-injected config)
    if (typeof window !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const injected = window.__ASTERMIND_CONFIG__;
        if (injected && typeof injected === 'object' && injected.apiKey) {
            return {
                ...injected,
                apiUrl: injected.apiUrl || DEFAULT_API_URL,
                _source: 'vite'
            };
        }
    }
    // Check for Vite's import.meta.env (replaced at build time)
    // Note: TypeScript doesn't know about import.meta.env, so we use a try-catch
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const importMeta = globalThis.import?.meta?.env;
        if (importMeta) {
            const apiKey = importMeta.VITE_ASTERMIND_RAG_API_KEY;
            const apiUrl = importMeta.VITE_ASTERMIND_RAG_API_SERVER_URL;
            if (apiKey) {
                return {
                    apiKey,
                    apiUrl: apiUrl || DEFAULT_API_URL,
                    _source: 'vite'
                };
            }
        }
    }
    catch {
        // import.meta not available in this environment
    }
    return null;
}
/**
 * Load config from window.astermindConfig global object
 */
function loadFromGlobalObject() {
    if (typeof window === 'undefined') {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const globalConfig = window.astermindConfig;
    if (globalConfig && typeof globalConfig === 'object' && globalConfig.apiKey) {
        return {
            ...globalConfig,
            apiUrl: globalConfig.apiUrl || DEFAULT_API_URL,
            _source: 'window'
        };
    }
    return null;
}
/**
 * Load config from script tag data attributes
 */
function loadFromScriptAttributes() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return null;
    }
    const script = document.querySelector('script[data-astermind-key]');
    if (script) {
        const apiKey = script.getAttribute('data-astermind-key');
        if (apiKey) {
            return {
                apiKey,
                apiUrl: script.getAttribute('data-astermind-url') || DEFAULT_API_URL,
                _source: 'data-attr'
            };
        }
    }
    return null;
}
/**
 * Load config using priority-based fallback chain:
 * 1. Environment variables (process.env - for bundlers/Node.js)
 * 2. Vite environment variables (import.meta.env or window.__ASTERMIND_CONFIG__)
 * 3. Global object (window.astermindConfig)
 * 4. Script data attributes (data-astermind-key, data-astermind-url)
 *
 * @param options - Configuration options
 * @param options.throwOnMissingKey - If true (default), throws when no API key found. If false, returns null and logs a warning.
 * @returns Configuration object or null if not found and throwOnMissingKey is false
 */
function loadConfig(options = {}) {
    const { throwOnMissingKey = true } = options;
    // Priority 1: Environment variables (Node.js/bundler)
    const envConfig = loadFromProcessEnv();
    if (envConfig) {
        return envConfig;
    }
    // Priority 2: Vite environment variables / SSR-injected config
    const viteConfig = loadFromViteEnv();
    if (viteConfig) {
        return viteConfig;
    }
    // Priority 3: Global object (window.astermindConfig)
    const globalConfig = loadFromGlobalObject();
    if (globalConfig) {
        validateConfig(globalConfig);
        return globalConfig;
    }
    // Priority 4: Script data attributes
    const scriptConfig = loadFromScriptAttributes();
    if (scriptConfig) {
        return scriptConfig;
    }
    // No config found
    if (throwOnMissingKey) {
        throw new Error('AsterMind API key is required. Configure using one of these methods:\n' +
            '  1. Set VITE_ASTERMIND_RAG_API_KEY environment variable (Vite)\n' +
            '  2. Set REACT_APP_ASTERMIND_RAG_API_KEY environment variable (CRA)\n' +
            '  3. Set window.astermindConfig = { apiKey: "am_...", apiUrl: "..." }\n' +
            '  4. Add data-astermind-key attribute to your script tag\n' +
            '  5. Pass apiKey directly to createClient() or CyberneticClient constructor');
    }
    else {
        console.warn('[AsterMind] No API key found. Chatbot will not function until configured.');
        return null;
    }
}
/**
 * Export individual loaders for testing and advanced use cases
 */
const configLoaders = {
    loadFromProcessEnv,
    loadFromViteEnv,
    loadFromGlobalObject,
    loadFromScriptAttributes
};

// src/license/base64url.ts
// Base64URL encoding/decoding utilities for JWT handling
/**
 * Decode a base64url string to Uint8Array
 * RFC 4648 §5 - Base64 URL-safe encoding
 */
function base64urlDecode(input) {
    // Convert base64url to standard base64
    let base64 = input
        .replace(/-/g, '+')
        .replace(/_/g, '/');
    // Add padding if needed
    const padding = base64.length % 4;
    if (padding) {
        base64 += '='.repeat(4 - padding);
    }
    // Decode base64
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}
/**
 * Decode base64url to UTF-8 string
 */
function base64urlDecodeString(input) {
    const bytes = base64urlDecode(input);
    return new TextDecoder().decode(bytes);
}
/**
 * Parse a JWT token into its parts (header, payload, signature)
 * Does NOT verify the signature
 */
function parseJWT(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        const [headerB64, payloadB64, signatureB64] = parts;
        return {
            header: JSON.parse(base64urlDecodeString(headerB64)),
            payload: JSON.parse(base64urlDecodeString(payloadB64)),
            signature: base64urlDecode(signatureB64),
            signedContent: `${headerB64}.${payloadB64}`
        };
    }
    catch {
        return null;
    }
}

// src/license/verifier.ts
// JWT verification for license tokens
/**
 * AsterMind public key for license verification (ES256 - ECDSA P-256)
 * This key is embedded for verification of official AsterMind licenses
 */
const ASTERMIND_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEhLW7MXHQ0Fuk1OKt9D5CbRw7fUMN
LJ5AZXvOhHaXdHzMuYKX5BpK4w7TqbPvJ6QPvKmLKvHh1VKcUJ6mJQgJJw==
-----END PUBLIC KEY-----`;
/**
 * Import a PEM-encoded public key for WebCrypto
 */
async function importPublicKey(pemKey) {
    // Remove PEM headers and whitespace
    const pemContents = pemKey
        .replace(/-----BEGIN PUBLIC KEY-----/, '')
        .replace(/-----END PUBLIC KEY-----/, '')
        .replace(/\s/g, '');
    // Decode base64 to ArrayBuffer
    const binaryString = atob(pemContents);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    // Import as ECDSA P-256 key
    return crypto.subtle.importKey('spki', bytes.buffer, {
        name: 'ECDSA',
        namedCurve: 'P-256'
    }, false, ['verify']);
}
/**
 * Convert JWT signature from compact format to DER format for WebCrypto
 * JWT uses raw R||S format (64 bytes for P-256)
 * WebCrypto expects raw format for ECDSA, not DER
 */
function convertJWTSignature(signature) {
    // For P-256, the signature should be 64 bytes (32 for R, 32 for S)
    // JWT compact serialization already uses this format
    return signature;
}
/**
 * Verify a JWT signature using WebCrypto
 */
async function verifySignature(signedContent, signature, publicKey) {
    try {
        const key = await importPublicKey(publicKey);
        const encoder = new TextEncoder();
        const data = encoder.encode(signedContent);
        const sig = convertJWTSignature(signature);
        // Create a new ArrayBuffer from the signature bytes to satisfy TypeScript
        const sigBuffer = new Uint8Array(sig).buffer;
        return await crypto.subtle.verify({
            name: 'ECDSA',
            hash: 'SHA-256'
        }, key, sigBuffer, data);
    }
    catch (error) {
        console.warn('[License] Signature verification failed:', error);
        return false;
    }
}
/**
 * Validate license payload structure
 */
function validatePayload(payload) {
    // Required fields
    if (typeof payload.iss !== 'string')
        return null;
    if (typeof payload.sub !== 'string')
        return null;
    if (typeof payload.aud !== 'string')
        return null;
    if (typeof payload.iat !== 'number')
        return null;
    if (typeof payload.exp !== 'number')
        return null;
    if (typeof payload.plan !== 'string')
        return null;
    if (typeof payload.seats !== 'number')
        return null;
    if (!Array.isArray(payload.features))
        return null;
    if (typeof payload.licenseVersion !== 'number')
        return null;
    return {
        iss: payload.iss,
        sub: payload.sub,
        aud: payload.aud,
        iat: payload.iat,
        exp: payload.exp,
        plan: payload.plan,
        org: typeof payload.org === 'string' ? payload.org : undefined,
        seats: payload.seats,
        features: payload.features,
        graceUntil: typeof payload.graceUntil === 'number' ? payload.graceUntil : undefined,
        licenseVersion: payload.licenseVersion
    };
}
/**
 * Calculate days remaining until expiration
 */
function calculateDaysRemaining(expTimestamp) {
    const now = Math.floor(Date.now() / 1000);
    const secondsRemaining = expTimestamp - now;
    return Math.floor(secondsRemaining / 86400); // 86400 = seconds in a day
}
/**
 * Verify a license token
 *
 * @param token - JWT license token
 * @param publicKey - Optional custom public key (defaults to AsterMind key)
 * @returns License state after verification
 */
async function verifyLicenseToken(token, publicKey) {
    // No token provided
    if (!token || token.trim() === '') {
        return {
            status: 'missing',
            payload: null,
            error: 'No license key provided',
            inGracePeriod: false,
            daysRemaining: null
        };
    }
    // Parse JWT
    const parsed = parseJWT(token);
    if (!parsed) {
        return {
            status: 'invalid',
            payload: null,
            error: 'Invalid license key format',
            inGracePeriod: false,
            daysRemaining: null
        };
    }
    // Validate payload structure
    const payload = validatePayload(parsed.payload);
    if (!payload) {
        return {
            status: 'invalid',
            payload: null,
            error: 'Invalid license payload structure',
            inGracePeriod: false,
            daysRemaining: null
        };
    }
    // Verify issuer
    if (payload.iss !== 'astermind') {
        return {
            status: 'invalid',
            payload: null,
            error: 'Invalid license issuer',
            inGracePeriod: false,
            daysRemaining: null
        };
    }
    // Verify audience (should be cybernetic-chatbot or wildcard)
    if (payload.aud !== 'cybernetic-chatbot' && payload.aud !== '*') {
        return {
            status: 'invalid',
            payload: null,
            error: 'License not valid for this product',
            inGracePeriod: false,
            daysRemaining: null
        };
    }
    // Verify signature (if WebCrypto is available)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
        const keyToUse = publicKey || ASTERMIND_PUBLIC_KEY;
        const signatureValid = await verifySignature(parsed.signedContent, parsed.signature, keyToUse);
        if (!signatureValid) {
            return {
                status: 'invalid',
                payload: null,
                error: 'License signature verification failed',
                inGracePeriod: false,
                daysRemaining: null
            };
        }
    }
    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    const daysRemaining = calculateDaysRemaining(payload.exp);
    if (payload.exp < now) {
        // Check for grace period
        if (payload.graceUntil && payload.graceUntil > now) {
            return {
                status: 'valid',
                payload,
                inGracePeriod: true,
                daysRemaining
            };
        }
        return {
            status: 'expired',
            payload,
            error: 'License has expired',
            inGracePeriod: false,
            daysRemaining
        };
    }
    // Check for evaluation license
    if (payload.plan === 'eval') {
        return {
            status: 'eval',
            payload,
            inGracePeriod: false,
            daysRemaining
        };
    }
    // License is valid
    return {
        status: 'valid',
        payload,
        inGracePeriod: false,
        daysRemaining
    };
}
/**
 * Quick check if a token looks like a valid JWT (does not verify)
 */
function isValidJWTFormat(token) {
    return parseJWT(token) !== null;
}
/**
 * Extract expiration date from a token without verification
 * Useful for quick checks
 */
function getTokenExpiration(token) {
    const parsed = parseJWT(token);
    if (!parsed || typeof parsed.payload.exp !== 'number') {
        return null;
    }
    return new Date(parsed.payload.exp * 1000);
}

// src/license/licenseManager.ts
// License management with environment-aware enforcement
/**
 * Required features for different capabilities
 */
const REQUIRED_FEATURES = {
    /** Feature required for basic client functionality */
    CLIENT: 'cybernetic-chatbot-client',
    /** Feature required for agentic capabilities (DOM automation, intent classification) */
    AGENTIC: 'agentic'
};
/**
 * License enforcement message shown in production when license is invalid
 */
// TODO: Re-enable when license key system is fully implemented
// const LICENSE_WARNING_MESSAGE =
//     '\n\n---\n⚠️ License Notice: Your AsterMind license key needs to be updated. ' +
//     'Please contact support@astermind.ai or visit https://astermind.ai/license to renew your license.';
/**
 * Detect current environment based on URL and other signals
 */
function detectEnvironment() {
    // Node.js environment
    if (typeof window === 'undefined') {
        return process?.env?.NODE_ENV === 'production' ? 'production' : 'development';
    }
    const hostname = window.location?.hostname || '';
    // Development indicators
    const devPatterns = [
        'localhost',
        '127.0.0.1',
        '0.0.0.0',
        '.local',
        '.dev',
        '.test',
        ':3000', // Common dev ports
        ':5173', // Vite
        ':8080', // Common dev port
        ':4200', // Angular
    ];
    // Check for development patterns
    for (const pattern of devPatterns) {
        if (hostname.includes(pattern) || window.location?.port === pattern.replace(':', '')) {
            return 'development';
        }
    }
    // Check for file:// protocol (local development)
    if (window.location?.protocol === 'file:') {
        return 'development';
    }
    // Check for staging/preview patterns
    const stagingPatterns = [
        'staging.',
        'preview.',
        '-preview.',
        '.vercel.app',
        '.netlify.app',
        '.pages.dev',
    ];
    for (const pattern of stagingPatterns) {
        if (hostname.includes(pattern)) {
            // Treat staging as production for license enforcement
            return 'production';
        }
    }
    // Default to production for safety
    return 'production';
}
/**
 * Determine enforcement mode based on environment
 */
function getEnforcementMode(environment) {
    return environment === 'development' ? 'soft' : 'hard';
}
/**
 * License Manager
 *
 * Manages license verification and enforcement for CyberneticClient.
 * - In development: Logs warnings to console (soft enforcement)
 * - In production: Appends warning to chatbot responses (hard enforcement)
 */
class LicenseManager {
    constructor(config = {}) {
        this.state = null;
        this.verificationPromise = null;
        // TODO: Re-enable when license key system is fully implemented
        // private hasLoggedWarning = false;
        this.loggedMissingFeatures = new Set();
        this.config = config;
        this.environment = config.environment || detectEnvironment();
        this.enforcementMode = getEnforcementMode(this.environment);
    }
    /**
     * Verify the license (async, caches result)
     */
    async verify() {
        // Return cached state if available
        if (this.state) {
            return this.state;
        }
        // Prevent concurrent verification calls
        if (this.verificationPromise) {
            return this.verificationPromise;
        }
        this.verificationPromise = verifyLicenseToken(this.config.licenseKey, this.config.publicKey);
        try {
            this.state = await this.verificationPromise;
            // Notify status change
            this.config.onStatusChange?.(this.state);
            // Log based on enforcement mode
            this.logLicenseStatus();
            return this.state;
        }
        finally {
            this.verificationPromise = null;
        }
    }
    /**
     * Get current license state (null if not yet verified)
     */
    getState() {
        return this.state;
    }
    /**
     * Check if license is valid or acceptable
     * Valid statuses: 'valid', 'eval'
     */
    isValid() {
        if (!this.state)
            return false;
        return this.state.status === 'valid' || this.state.status === 'eval';
    }
    /**
     * Check if license requires action (expired, missing, invalid)
     */
    requiresAction() {
        if (!this.state)
            return true;
        return this.state.status === 'expired' ||
            this.state.status === 'missing' ||
            this.state.status === 'invalid';
    }
    /**
     * Get current environment
     */
    getEnvironment() {
        return this.environment;
    }
    /**
     * Get current enforcement mode
     */
    getEnforcementMode() {
        return this.enforcementMode;
    }
    /**
     * Process a response based on license status
     * In production with invalid license: appends warning to response
     * In development: logs warning (once) and returns response unchanged
     *
     * @param response - The chatbot response text
     * @returns Modified response (or original if license valid)
     */
    processResponse(response) {
        // TODO: License enforcement disabled until license key system is fully implemented
        return response;
        // // License not verified yet - allow through
        // if (!this.state) {
        //     return response;
        // }
        //
        // // Valid license - no modification
        // if (this.isValid() && !this.state.inGracePeriod) {
        //     return response;
        // }
        //
        // // Handle based on enforcement mode
        // if (this.enforcementMode === 'soft') {
        //     // Development: just log warning (once)
        //     if (!this.hasLoggedWarning) {
        //         this.logDevelopmentWarning();
        //         this.hasLoggedWarning = true;
        //     }
        //     return response;
        // }
        //
        // // Production: append warning to response
        // return response + LICENSE_WARNING_MESSAGE;
    }
    /**
     * Get a human-readable license status message
     */
    getStatusMessage() {
        if (!this.state) {
            return 'License not verified';
        }
        switch (this.state.status) {
            case 'valid':
                if (this.state.inGracePeriod) {
                    return `License expired but in grace period (${Math.abs(this.state.daysRemaining || 0)} days overdue)`;
                }
                return `License valid (${this.state.daysRemaining} days remaining)`;
            case 'eval':
                return `Evaluation license (${this.state.daysRemaining} days remaining)`;
            case 'expired':
                return `License expired ${Math.abs(this.state.daysRemaining || 0)} days ago`;
            case 'missing':
                return 'No license key provided';
            case 'invalid':
                return `Invalid license: ${this.state.error}`;
            default:
                return 'Unknown license status';
        }
    }
    /**
     * Force re-verification (clears cached state)
     */
    async reVerify() {
        this.state = null;
        // this.hasLoggedWarning = false;
        this.loggedMissingFeatures.clear();
        return this.verify();
    }
    /**
     * Check if a specific feature is licensed
     *
     * @param feature - Feature name to check
     * @returns true if feature is present in license
     */
    hasFeature(feature) {
        if (!this.state?.payload)
            return false;
        return this.state.payload.features.includes(feature);
    }
    /**
     * Get all licensed features
     */
    getFeatures() {
        return this.state?.payload?.features || [];
    }
    /**
     * Check if client feature is licensed
     * Logs warning if not present (only once)
     *
     * @returns true if cybernetic-chatbot-client feature is present
     */
    checkClientFeature() {
        return this.checkFeatureWithLog(REQUIRED_FEATURES.CLIENT, 'CyberneticClient');
    }
    /**
     * Check if agentic feature is licensed
     * Logs warning if not present (only once)
     * Call this when attempting to use agentic capabilities
     *
     * @returns true if agentic feature is present
     */
    checkAgenticFeature() {
        return this.checkFeatureWithLog(REQUIRED_FEATURES.AGENTIC, 'Agentic capabilities');
    }
    /**
     * Check a feature and log if missing (only logs once per feature)
     */
    checkFeatureWithLog(feature, displayName) {
        // If license is missing/invalid, allow through (handled by other checks)
        if (!this.state?.payload) {
            return true;
        }
        const hasFeature = this.state.payload.features.includes(feature);
        if (!hasFeature && !this.loggedMissingFeatures.has(feature)) {
            this.loggedMissingFeatures.add(feature);
            this.logMissingFeature(feature, displayName);
        }
        return hasFeature;
    }
    /**
     * Log missing feature warning with available features
     */
    logMissingFeature(missingFeature, displayName) {
        const availableFeatures = this.getFeatures();
        const plan = this.state?.payload?.plan || 'unknown';
        console.warn(`[Cybernetic License] ${displayName} requires the '${missingFeature}' feature, ` +
            `which is not included in your license.`);
        console.info(`[Cybernetic License] Current license plan: ${plan}`);
        console.info(`[Cybernetic License] Licensed features: ${availableFeatures.length > 0
            ? availableFeatures.join(', ')
            : '(none)'}`);
        console.info(`[Cybernetic License] To add this feature, upgrade your license at https://astermind.ai/license`);
    }
    /**
     * Log license status based on enforcement mode
     */
    logLicenseStatus() {
        // TODO: Re-enable when license key system is fully implemented
        return;
        // if (!this.state) return;
        //
        // const prefix = '[Cybernetic License]';
        //
        // switch (this.state.status) {
        //     case 'valid':
        //         if (this.state.inGracePeriod) {
        //             console.warn(
        //                 `${prefix} License expired but in grace period. ` +
        //                 `Please renew at https://astermind.ai/license`
        //             );
        //         } else if (this.state.daysRemaining !== null && this.state.daysRemaining <= 30) {
        //             console.info(
        //                 `${prefix} License expires in ${this.state.daysRemaining} days`
        //             );
        //         }
        //         break;
        //
        //     case 'eval':
        //         console.info(
        //             `${prefix} Evaluation license - ${this.state.daysRemaining} days remaining`
        //         );
        //         break;
        //
        //     case 'expired':
        //         console.error(
        //             `${prefix} License expired. ` +
        //             `Enforcement mode: ${this.enforcementMode}. ` +
        //             `Renew at https://astermind.ai/license`
        //         );
        //         break;
        //
        //     case 'missing':
        //         console.warn(
        //             `${prefix} No license key provided. ` +
        //             `Enforcement mode: ${this.enforcementMode}. ` +
        //             `Get a license at https://astermind.ai/license`
        //         );
        //         break;
        //
        //     case 'invalid':
        //         console.error(
        //             `${prefix} Invalid license: ${this.state.error}. ` +
        //             `Enforcement mode: ${this.enforcementMode}`
        //         );
        //         break;
        // }
    }
}
/**
 * Create a pre-configured license manager and verify immediately
 */
async function createLicenseManager(config) {
    const manager = new LicenseManager(config);
    await manager.verify();
    return manager;
}

// src/CyberneticClient.ts
// Main client with API calls and offline fallback
/**
 * Cybernetic Chatbot Client
 *
 * Provides API access to AsterMind backend with offline fallback.
 * Always returns a response, never throws exceptions.
 */
class CyberneticClient {
    /** Debug logger — only outputs when config.debug is true */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    log(...args) {
        if (this.config.debug) {
            console.log(...args);
        }
    }
    constructor(config) {
        this.wsTransport = null;
        this.status = 'connecting';
        this.lastError = null;
        // Maintenance mode tracking (ADR-200)
        this.systemSettings = null;
        this.settingsLastChecked = 0;
        this.SETTINGS_CHECK_INTERVAL = 300000; // 5 minutes
        // Agentic capabilities (optional, registered separately via registerAgenticCapabilities)
        this.agenticCapabilities = null;
        // Offline vector storage
        this.offlineStorage = null;
        // Omega RAG (optional, loaded dynamically)
        this.omegaRAG = null;
        // Sales API client (optional, enabled via salesMode config)
        this.salesClient = null;
        // Track if offline warning has been shown
        this.offlineWarningShown = false;
        // Resolve WebSocket URL (explicit → env → auto-derived from apiUrl)
        const wsUrl = resolveWsUrl(config);
        const transport = config.transport ?? 'auto';
        // Apply defaults
        this.config = {
            apiUrl: config.apiUrl,
            apiKey: config.apiKey,
            wsUrl,
            transport,
            fallback: {
                enabled: config.fallback?.enabled ?? true,
                cacheMaxAge: config.fallback?.cacheMaxAge ?? 86400000, // 24 hours
                cacheOnConnect: config.fallback?.cacheOnConnect ?? true,
                cacheStorage: config.fallback?.cacheStorage ?? 'indexeddb'
            },
            retry: {
                maxRetries: config.retry?.maxRetries ?? 2,
                initialDelay: config.retry?.initialDelay ?? 1000,
                exponentialBackoff: config.retry?.exponentialBackoff ?? true
            },
            agentic: config.agentic ?? null,
            offline: config.offline ?? null,
            sitemap: config.sitemap ?? null,
            sources: {
                enabled: config.sources?.enabled ?? true,
                showSummary: config.sources?.showSummary ?? true,
                showDownload: config.sources?.showDownload ?? true,
                includeFullContent: config.sources?.includeFullContent ?? false,
                maxSources: config.sources?.maxSources ?? 5
            },
            onStatusChange: config.onStatusChange || (() => { }),
            onError: config.onError || (() => { }),
            debug: config.debug ?? false,
        };
        // Initialize components
        this.apiClient = new ApiClient(this.config.apiUrl, this.config.apiKey);
        this.cache = new CyberneticCache({
            storage: this.config.fallback.cacheStorage,
            maxAge: this.config.fallback.cacheMaxAge
        });
        this.localRAG = new CyberneticLocalRAG();
        // Initialize license manager
        this.licenseManager = new LicenseManager({
            licenseKey: config.licenseKey,
            environment: config.environment,
        });
        // Initialize session storage (auto-loads existing session from browser sessionStorage)
        this.sessionStorage = new CyberneticSessionStorage(config.session);
        // Verify license asynchronously (non-blocking)
        this.licenseManager.verify().then(() => {
            // Check client feature after verification
            this.licenseManager.checkClientFeature();
        }).catch(() => {
            // License verification errors are handled internally
        });
        // Initialize WebSocket transport if configured and not forced to REST
        if (wsUrl && transport !== 'rest' && typeof WebSocket !== 'undefined') {
            this.wsTransport = new WebSocketTransport(wsUrl, config.apiKey, config.websocket);
            this.log(`[Cybernetic] WebSocket transport enabled: ${wsUrl}`);
        }
        // Monitor connection status
        this.monitorConnection();
        // Pre-cache documents on init if enabled
        if (this.config.fallback.enabled && this.config.fallback.cacheOnConnect) {
            this.syncCache().catch(() => {
                // Silent failure on init - cache may be stale but usable
            });
        }
        // Initialize sales client if configured
        if (config.salesMode?.enabled) {
            this.salesClient = new SalesApiClient(config.apiUrl, config.apiKey);
        }
        // Initialize offline vector support if configured
        if (this.config.offline?.enabled) {
            this.initializeOffline(this.config.offline).catch((error) => {
                console.warn('[Cybernetic] Failed to initialize offline vectors:', error);
            });
        }
    }
    // ==================== AGENTIC CAPABILITIES ====================
    /**
     * Register agentic capabilities
     * Called by registerAgenticCapabilities() helper from agentic module
     *
     * @example
     * ```typescript
     * import { CyberneticClient, registerAgenticCapabilities } from '@astermind/cybernetic-chatbot-client';
     * const client = new CyberneticClient(config);
     * registerAgenticCapabilities(client);
     * ```
     */
    registerAgentic(capabilities) {
        this.agenticCapabilities = capabilities;
    }
    /**
     * Check if agentic capabilities are available and enabled
     */
    isAgenticEnabled() {
        if (this.agenticCapabilities === null || this.config.agentic?.enabled !== true) {
            return false;
        }
        // Check for agentic feature in license (logs warning if missing)
        this.licenseManager.checkAgenticFeature();
        return true;
    }
    /**
     * Get registered agentic capabilities (for advanced use)
     */
    getAgenticCapabilities() {
        return this.agenticCapabilities;
    }
    /**
     * Get agentic configuration
     */
    getAgenticConfig() {
        return this.config.agentic;
    }
    // ==================== SOURCES CONFIGURATION ====================
    /**
     * Get sources configuration
     * Returns merged configuration with all defaults applied
     */
    getSourcesConfig() {
        return { ...this.config.sources };
    }
    /**
     * Check if a specific source feature is available
     * Takes into account configuration, connection status, and requirements
     *
     * @param feature - The feature to check ('summary' or 'download')
     * @returns Whether the feature is available
     */
    isSourceFeatureAvailable(feature) {
        if (!this.config.sources.enabled) {
            return false;
        }
        if (feature === 'summary') {
            return this.config.sources.showSummary;
        }
        if (feature === 'download') {
            // Download only works when online
            return this.config.sources.showDownload && this.status === 'online';
        }
        return false;
    }
    /**
     * Classify user message intent for agentic action
     * Only works if agentic capabilities are registered and enabled
     *
     * @param message - User's message to classify
     * @returns Intent classification or null if agentic not available
     */
    classifyIntent(message) {
        if (!this.isAgenticEnabled() || !this.agenticCapabilities) {
            return null;
        }
        // Create agent instance if needed for classification
        const agentConfig = this.config.agentic;
        const AgentClass = this.agenticCapabilities.agent;
        const agent = new AgentClass(agentConfig);
        return agent.interpretIntent(message);
    }
    /**
     * Execute an agent action
     * Only works if agentic capabilities are registered and enabled
     *
     * @param action - Action to execute
     * @returns Action result or error
     */
    async executeAction(action) {
        if (!this.isAgenticEnabled() || !this.agenticCapabilities) {
            return {
                success: false,
                message: 'Agentic capabilities not enabled'
            };
        }
        const agentConfig = this.config.agentic;
        const AgentClass = this.agenticCapabilities.agent;
        const agent = new AgentClass(agentConfig);
        return agent.executeAction(action);
    }
    /**
     * Smart ask - checks for action intent first, then falls back to RAG
     * Combines agentic classification with standard RAG query
     *
     * @param message - User's message
     * @param options - Optional request configuration
     * @returns Object containing response, action, and/or action result
     */
    async smartAsk(message, options) {
        // Check for actionable intent if agentic is enabled
        const classification = this.classifyIntent(message);
        if (classification && classification.action && !classification.shouldEscalate) {
            // High confidence action detected
            if (this.config.agentic?.requireConfirmation !== false) {
                // Return action for UI confirmation without executing
                return { action: classification.action };
            }
            // Auto-execute if confirmation not required
            const result = await this.executeAction(classification.action);
            return {
                action: classification.action,
                actionResult: result
            };
        }
        // Low confidence or no action - proceed to RAG
        const response = await this.ask(message, options);
        return { response };
    }
    // ==================== OFFLINE VECTOR METHODS ====================
    /**
     * Initialize offline vector support
     * Loads pre-exported vectors from URL or inline data
     */
    async initializeOffline(config) {
        const storageMode = config.storageMode ?? 'hybrid';
        const maxAge = config.cacheMaxAge ?? 604800000; // 7 days default
        // Initialize IndexedDB storage if not memory-only
        if (storageMode !== 'memory') {
            this.offlineStorage = new CyberneticOfflineStorage();
        }
        // Check for cached vectors first
        if (this.offlineStorage && storageMode !== 'memory') {
            const cacheId = 'default';
            if (await this.offlineStorage.isValid(cacheId, maxAge)) {
                this.log('[Cybernetic] Loading vectors from IndexedDB cache');
                const cached = await this.offlineStorage.retrieve(cacheId);
                if (cached) {
                    await this.localRAG.loadFromExport(cached);
                    this.log('[Cybernetic] Loaded vectors from IndexedDB cache');
                    return;
                }
            }
        }
        // Try to load from URL
        if (config.vectorFileUrl) {
            try {
                const response = await fetch(config.vectorFileUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch vectors: ${response.status}`);
                }
                const exportData = await response.json();
                // Store in IndexedDB for future use
                if (this.offlineStorage && storageMode !== 'memory') {
                    await this.offlineStorage.store('default', exportData);
                }
                // Load into local RAG
                await this.localRAG.loadFromExport(exportData);
                // Initialize Omega if configured
                if (config.omega?.enabled) {
                    await this.initializeOmega(config);
                }
                this.log('[Cybernetic] Loaded vectors from URL:', config.vectorFileUrl);
                return;
            }
            catch (error) {
                console.error('[Cybernetic] Failed to load offline vectors from URL:', error);
            }
        }
        // Try inline vector data
        if (config.vectorData) {
            await this.localRAG.loadFromExport(config.vectorData);
            // Initialize Omega if configured
            if (config.omega?.enabled) {
                await this.initializeOmega(config);
            }
            this.log('[Cybernetic] Loaded inline vector data');
            return;
        }
        // No vectors available - log warning (one-time)
        if (!this.offlineWarningShown) {
            console.warn('[Cybernetic] Offline functionality is enabled but no vectors are loaded. ' +
                'Offline responses will be limited. To enable full offline functionality, ' +
                'export vectors from the AsterMind admin panel and configure the vectorFileUrl ' +
                'or vectorData option.');
            this.offlineWarningShown = true;
        }
    }
    /**
     * Initialize Omega RAG module
     */
    async initializeOmega(config) {
        if (!config.omega?.enabled)
            return;
        try {
            // Dynamic import to support tree-shaking
            const { OmegaOfflineRAG } = await Promise.resolve().then(function () { return OmegaOfflineRAG$1; });
            this.omegaRAG = new OmegaOfflineRAG({
                reranking: config.omega.reranking,
                topK: 5,
                verbose: false
            });
            // Load model from URL or inline data
            if (config.omega.modelUrl) {
                await this.omegaRAG.loadModelFromUrl(config.omega.modelUrl);
            }
            else if (config.omega.modelData) {
                // Convert our type to the astermind type
                await this.omegaRAG.loadModel(config.omega.modelData);
            }
            this.log('[Cybernetic] Omega RAG initialized');
        }
        catch (error) {
            console.warn('[Cybernetic] Failed to initialize Omega RAG:', error);
            this.omegaRAG = null;
        }
    }
    /**
     * Manually reload offline vectors
     * Clears cache and re-fetches from configured source
     */
    async reloadOfflineVectors() {
        if (!this.config.offline?.enabled) {
            console.warn('[Cybernetic] Offline mode is not enabled');
            return;
        }
        // Clear existing data
        this.localRAG.reset();
        if (this.offlineStorage) {
            await this.offlineStorage.clear();
        }
        if (this.omegaRAG) {
            this.omegaRAG.clear();
        }
        // Re-initialize
        await this.initializeOffline(this.config.offline);
    }
    /**
     * Check if Omega offline RAG is enabled and ready
     */
    isOmegaOfflineEnabled() {
        return this.omegaRAG?.isReady() ?? false;
    }
    /**
     * Get offline model info (if Omega is loaded)
     */
    getOfflineModelInfo() {
        return this.omegaRAG?.getModelInfo() ?? null;
    }
    /**
     * Get local RAG export status
     */
    getLocalRAGStatus() {
        return this.localRAG.getExportStatus();
    }
    /**
     * Get offline storage instance for advanced operations
     */
    getOfflineStorage() {
        return this.offlineStorage;
    }
    // ==================== SESSION PERSISTENCE METHODS (ADR-028) ====================
    /**
     * Get stored messages from a previous session for UI restoration.
     * Call after construction to check for resumable conversations.
     */
    getStoredMessages() {
        return this.sessionStorage.getMessages();
    }
    /**
     * Check if a valid stored session exists that can be resumed.
     */
    hasStoredSession() {
        return this.sessionStorage.hasValidSession();
    }
    /**
     * Get session info for debugging/status display.
     */
    getSessionInfo() {
        return this.sessionStorage.getSessionInfo();
    }
    /**
     * Clear the stored session and start fresh.
     * Use for "New Conversation" actions.
     */
    clearSession() {
        this.sessionStorage.clear();
    }
    /**
     * Start a new session, clearing any stored data.
     * Optionally sets a new sessionId immediately.
     */
    startNewSession(sessionId) {
        this.sessionStorage.startNewSession(sessionId);
    }
    // ==================== SALES API METHODS ====================
    /**
     * Check if sales mode is enabled
     */
    isSalesModeEnabled() {
        return this.salesClient !== null;
    }
    /**
     * Get the sales API client for direct access
     */
    getSalesClient() {
        return this.salesClient;
    }
    /**
     * Send a sales-aware chat message.
     * Returns the assistant reply with sales context (lead score, CTA buttons, etc.).
     * Requires salesMode to be enabled in config.
     */
    async salesChat(message, options = {}) {
        if (!this.salesClient) {
            throw new Error('Sales mode is not enabled. Set salesMode.enabled = true in config.');
        }
        return this.salesClient.salesChat(message, options);
    }
    /**
     * Initialize or resume a visitor session.
     * Call on first page load to register the visitor with the backend.
     * Requires salesMode to be enabled in config.
     */
    async initVisitor(visitorId, pageUrl, referrer, utmParams) {
        if (!this.salesClient) {
            throw new Error('Sales mode is not enabled. Set salesMode.enabled = true in config.');
        }
        return this.salesClient.initVisitor(visitorId, pageUrl, referrer, utmParams);
    }
    /**
     * Track a page view event for a visitor.
     * Call when the visitor navigates to a new page.
     * Requires salesMode to be enabled in config.
     */
    async trackPageView(visitorId, url, title, timeOnPageMs, scrollDepth) {
        if (!this.salesClient) {
            throw new Error('Sales mode is not enabled. Set salesMode.enabled = true in config.');
        }
        return this.salesClient.trackPageView(visitorId, url, title, timeOnPageMs, scrollDepth);
    }
    /**
     * Submit a lead capture form.
     * Creates or updates a lead record in the backend.
     * Requires salesMode to be enabled in config.
     */
    async captureLeadForm(data) {
        if (!this.salesClient) {
            throw new Error('Sales mode is not enabled. Set salesMode.enabled = true in config.');
        }
        return this.salesClient.captureLeadForm(data);
    }
    // ==================== CORE METHODS ====================
    /**
     * Send a message to the chatbot
     * Always returns a response, never throws
     *
     * @param message - User's message
     * @param options - Optional request configuration
     */
    async ask(message, options) {
        // Validate input
        if (!message || typeof message !== 'string') {
            return this.createErrorResponse('Message is required', 'none');
        }
        // Auto-resolve sessionId: explicit option > stored session > none (ADR-028)
        const resolvedSessionId = options?.sessionId ?? this.sessionStorage.getSessionId() ?? undefined;
        const resolvedOptions = resolvedSessionId
            ? { ...options, sessionId: resolvedSessionId }
            : options;
        // Check maintenance mode before API call (ADR-200)
        const settings = await this.checkSystemStatus();
        if (settings.maintenanceMode || settings.forceOfflineClients) {
            this.log('[Cybernetic] Maintenance mode active, using cached data');
            // Return maintenance response if no cached data
            if (!this.isCacheValid()) {
                return {
                    reply: settings.maintenanceMessage ||
                        'The service is currently under maintenance. Please try again later.',
                    confidence: 'none',
                    sources: [],
                    offline: true,
                    degradedReason: 'Maintenance mode active, no valid cache available'
                };
            }
            // Use local fallback during maintenance
            return await this.fallbackAsk(message);
        }
        // Try API first
        try {
            const response = await this.apiWithRetry(message, resolvedOptions);
            this.setStatus('online');
            // Process response through license manager (may add warning in production)
            const processedReply = this.licenseManager.processResponse(response.reply);
            // Persist session state (ADR-028)
            if (response.sessionId) {
                this.sessionStorage.saveSessionId(response.sessionId);
            }
            this.sessionStorage.addMessage({ role: 'user', content: message });
            this.sessionStorage.addMessage({ role: 'assistant', content: processedReply, confidence: 'high', sources: response.sources || [] });
            return {
                reply: processedReply,
                confidence: 'high',
                sources: response.sources || [],
                offline: false,
                sessionId: response.sessionId
            };
        }
        catch (error) {
            const omegaError = this.normalizeError(error);
            this.lastError = omegaError;
            this.config.onError(omegaError);
            // Rate limit - don't fallback, return error response
            if (omegaError.code === 'RATE_LIMIT') {
                return {
                    reply: 'I\'m receiving too many requests right now. Please try again in a moment.',
                    confidence: 'none',
                    sources: [],
                    offline: false,
                    retryAfter: omegaError.retryAfter
                };
            }
            // Try fallback if enabled and not explicitly skipped
            if (this.config.fallback.enabled && !options?.skipFallback) {
                return await this.fallbackAsk(message);
            }
            // No fallback - return error response
            return this.createErrorResponse('Unable to connect to the chatbot service. Please try again later.', 'none', omegaError.retryAfter);
        }
    }
    /**
     * Send a message with streaming response
     *
     * @param message - User's message
     * @param callbacks - Streaming event callbacks
     * @param options - Optional request configuration
     */
    async askStream(message, callbacks, options) {
        if (!message || typeof message !== 'string') {
            callbacks.onError?.({
                code: 'LOCAL_RAG_ERROR',
                message: 'Message is required'
            });
            return;
        }
        // Auto-resolve sessionId: explicit option > stored session > none (ADR-028)
        const resolvedSessionId = options?.sessionId ?? this.sessionStorage.getSessionId() ?? undefined;
        const resolvedOptions = resolvedSessionId
            ? { ...options, sessionId: resolvedSessionId }
            : options;
        // Save user message immediately (ADR-028)
        this.sessionStorage.addMessage({ role: 'user', content: message });
        // Check maintenance mode before API call (ADR-200)
        const settings = await this.checkSystemStatus();
        if (settings.maintenanceMode || settings.forceOfflineClients) {
            this.log('[Cybernetic] Maintenance mode active, falling back to offline');
            const response = await this.fallbackAsk(message);
            callbacks.onComplete?.(response);
            return;
        }
        // Try WebSocket transport first (if available and not forced to REST)
        if (this.wsTransport && this.config.transport !== 'rest') {
            try {
                await this.wsTransport.chatStream(message, {
                    sessionId: resolvedOptions?.sessionId,
                    context: resolvedOptions?.context,
                    onToken: callbacks.onToken,
                    onSources: callbacks.onSources,
                    onComplete: (response) => {
                        this.setStatus('online');
                        // Process through license manager
                        const processedReply = this.licenseManager.processResponse(response.reply);
                        // Persist session state (ADR-028)
                        if (response.sessionId) {
                            this.sessionStorage.saveSessionId(response.sessionId);
                        }
                        this.sessionStorage.addMessage({ role: 'assistant', content: processedReply, confidence: 'high', sources: response.sources });
                        callbacks.onComplete?.({
                            ...response,
                            reply: processedReply
                        });
                    },
                    onError: (error) => {
                        // In 'auto' mode, fall back to SSE on WS error
                        if (this.config.transport === 'auto') {
                            console.warn('[Cybernetic] WebSocket error, falling back to SSE:', error.message);
                            this.streamViaSSE(message, callbacks, resolvedOptions);
                        }
                        else {
                            // 'websocket' mode — no fallback
                            const normalizedError = this.normalizeError(error);
                            this.config.onError(normalizedError);
                            callbacks.onError?.(normalizedError);
                        }
                    }
                });
                return;
            }
            catch (error) {
                if (this.config.transport === 'websocket') {
                    // Forced WebSocket mode — don't fall back
                    const normalizedError = this.normalizeError(error);
                    this.lastError = normalizedError;
                    this.config.onError(normalizedError);
                    callbacks.onError?.(normalizedError);
                    return;
                }
                // 'auto' mode: fall through to SSE
                console.warn('[Cybernetic] WebSocket connect failed, using SSE fallback');
            }
        }
        // REST+SSE path (on-prem, or fallback from WebSocket)
        await this.streamViaSSE(message, callbacks, resolvedOptions);
    }
    /**
     * Stream chat via REST+SSE (original transport).
     * Used as the primary transport for on-prem, or as fallback for SaaS WebSocket failures.
     */
    async streamViaSSE(message, callbacks, options) {
        try {
            await this.apiClient.chatStream(message, {
                sessionId: options?.sessionId,
                context: options?.context,
                onToken: callbacks.onToken,
                onSources: callbacks.onSources,
                onComplete: (data) => {
                    this.setStatus('online');
                    // Process response through license manager (may add warning in production)
                    const processedReply = this.licenseManager.processResponse(data.fullText);
                    // Persist session state (ADR-028)
                    if (data.sessionId) {
                        this.sessionStorage.saveSessionId(data.sessionId);
                    }
                    this.sessionStorage.addMessage({ role: 'assistant', content: processedReply, confidence: 'high', sources: data.sources || [] });
                    callbacks.onComplete?.({
                        reply: processedReply,
                        confidence: 'high',
                        sources: data.sources || [],
                        offline: false,
                        sessionId: data.sessionId,
                        messageId: data.messageId
                    });
                },
                onError: (error) => {
                    const omegaError = this.normalizeError(error);
                    this.config.onError(omegaError);
                    callbacks.onError?.(omegaError);
                }
            });
        }
        catch (error) {
            const omegaError = this.normalizeError(error);
            this.lastError = omegaError;
            this.config.onError(omegaError);
            // Fall back to non-streaming if enabled
            if (this.config.fallback.enabled && !options?.skipFallback) {
                const response = await this.fallbackAsk(message);
                callbacks.onComplete?.(response);
            }
            else {
                callbacks.onError?.(omegaError);
            }
        }
    }
    /**
     * Sync documents to local cache for offline use
     */
    async syncCache() {
        try {
            const lastSync = await this.cache.getLastSync();
            const docs = await this.apiClient.getGeneralDocs(lastSync);
            if (docs.length > 0) {
                await this.cache.store(docs);
                await this.localRAG.index(docs);
            }
            this.setStatus('online');
        }
        catch (error) {
            console.warn('[Cybernetic] Cache sync failed:', error);
            // Don't change status - we may still have cached data
        }
    }
    /**
     * Get current connection status including system settings
     */
    getStatus() {
        return {
            connection: this.status,
            cache: this.cache.getStatus(),
            lastError: this.lastError,
            systemSettings: this.systemSettings,
            license: this.licenseManager.getState()
        };
    }
    /**
     * Get license manager for advanced license operations
     */
    getLicenseManager() {
        return this.licenseManager;
    }
    /**
     * Clear local cache
     */
    async clearCache() {
        await this.cache.clear();
        this.localRAG.reset();
    }
    /**
     * Clean up all resources (WebSocket connection, caches, event listeners).
     * Call this when the client is no longer needed to prevent memory leaks.
     */
    destroy() {
        if (this.wsTransport) {
            this.wsTransport.disconnect();
            this.wsTransport = null;
        }
    }
    /**
     * Manually check if backend is reachable
     */
    async checkConnection() {
        try {
            await this.apiClient.getStatus();
            this.setStatus('online');
            return true;
        }
        catch {
            this.setStatus('offline');
            return false;
        }
    }
    // ==================== MAINTENANCE MODE METHODS (ADR-200) ====================
    /**
     * Check system status including maintenance mode
     * Called periodically and before API calls
     */
    async checkSystemStatus() {
        // Use cached settings if recently checked
        if (this.systemSettings &&
            Date.now() - this.settingsLastChecked < this.SETTINGS_CHECK_INTERVAL) {
            return this.systemSettings;
        }
        try {
            const status = await this.apiClient.getStatus();
            // Extract system settings from status response
            this.systemSettings = {
                cacheRetentionHours: status.systemSettings?.cacheRetentionHours ?? 168,
                maintenanceMode: status.systemSettings?.maintenanceMode ?? false,
                maintenanceMessage: status.systemSettings?.maintenanceMessage,
                forceOfflineClients: status.systemSettings?.forceOfflineClients ?? false
            };
            this.settingsLastChecked = Date.now();
            return this.systemSettings;
        }
        catch {
            // On error, return cached settings or safe defaults
            return this.systemSettings || {
                cacheRetentionHours: 168,
                maintenanceMode: false,
                forceOfflineClients: false
            };
        }
    }
    /**
     * Fetch widget configuration (including persona) from the backend.
     * Requires apiKey and apiUrl to be configured.
     * Returns null on any error (network, auth, server) — callers should
     * fall back to static config when null is returned.
     */
    async fetchConfig() {
        try {
            const response = await fetch(`${this.config.apiUrl}/api/external/config`, {
                headers: {
                    'X-API-Key': this.config.apiKey,
                    'Accept': 'application/json',
                },
            });
            if (!response.ok)
                return null;
            return await response.json();
        }
        catch {
            return null;
        }
    }
    /**
     * Check if maintenance mode is active
     */
    isMaintenanceMode() {
        return this.systemSettings?.maintenanceMode ?? false;
    }
    /**
     * Get maintenance message if in maintenance mode
     */
    getMaintenanceMessage() {
        return this.systemSettings?.maintenanceMessage;
    }
    /**
     * Validate cache using server-configured retention hours
     */
    isCacheValid() {
        const cacheStatus = this.cache.getStatus();
        if (!cacheStatus.lastSyncAt)
            return false;
        // Use server-configured retention or default 168 hours (7 days)
        const retentionHours = this.systemSettings?.cacheRetentionHours || 168;
        const retentionMs = retentionHours * 60 * 60 * 1000;
        const lastSyncTime = new Date(cacheStatus.lastSyncAt).getTime();
        return Date.now() - lastSyncTime < retentionMs;
    }
    // ==================== PRIVATE METHODS ====================
    /**
     * API call with retry logic
     */
    async apiWithRetry(message, options) {
        const { maxRetries, initialDelay, exponentialBackoff } = this.config.retry;
        let lastError = null;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await this.apiClient.chat(message, {
                    sessionId: options?.sessionId,
                    context: options?.context
                });
            }
            catch (error) {
                lastError = error;
                // Don't retry on auth or rate limit errors
                const omegaError = this.normalizeError(error);
                if (omegaError.code === 'AUTH_ERROR' || omegaError.code === 'RATE_LIMIT') {
                    throw error;
                }
                // Wait before retry
                if (attempt < maxRetries) {
                    const delay = exponentialBackoff
                        ? initialDelay * Math.pow(2, attempt)
                        : initialDelay;
                    await this.sleep(delay);
                }
            }
        }
        throw lastError;
    }
    /**
     * Fallback to local RAG processing
     * Uses Omega RAG if available, falls back to simple TF-IDF
     */
    async fallbackAsk(message) {
        this.setStatus('offline');
        // Try Omega RAG first if available
        if (this.omegaRAG?.isReady()) {
            try {
                const omegaResult = await this.omegaRAG.ask(message);
                if (!omegaResult.degraded) {
                    // Process response through license manager
                    const processedReply = this.licenseManager.processResponse(omegaResult.answer);
                    return {
                        reply: processedReply,
                        confidence: omegaResult.confidence,
                        sources: omegaResult.sources.map(s => ({
                            title: s.heading,
                            snippet: s.content.substring(0, 200) + '...',
                            relevance: s.score
                        })),
                        offline: true,
                        degradedReason: 'Omega hybrid retrieval (offline)'
                    };
                }
                // If Omega degraded, fall through to simple RAG
            }
            catch (error) {
                console.warn('[Cybernetic] Omega RAG failed, falling back to TF-IDF:', error);
                // Fall through to simple RAG
            }
        }
        // Check if local RAG has pre-loaded vectors from export
        const localRAGStatus = this.localRAG.getExportStatus();
        if (localRAGStatus.loaded && localRAGStatus.documentCount > 0) {
            try {
                const result = await this.localRAG.ask(message);
                // Determine confidence based on match quality
                let confidence = 'medium';
                if (result.topScore < 0.3) {
                    confidence = 'low';
                }
                else if (result.topScore > 0.7) {
                    confidence = 'medium'; // Never 'high' for offline
                }
                // Process response through license manager
                const processedReply = this.licenseManager.processResponse(result.answer);
                return {
                    reply: processedReply,
                    confidence,
                    sources: result.sources.map(s => ({
                        title: s.title,
                        snippet: s.snippet,
                        relevance: s.score
                    })),
                    offline: true,
                    degradedReason: localRAGStatus.loadedFromExport
                        ? 'Using pre-exported vectors (offline)'
                        : 'Processed locally with TF-IDF'
                };
            }
            catch (error) {
                console.error('[Cybernetic] Local RAG error:', error);
                // Fall through to cache-based fallback
            }
        }
        // Check if we have cached documents (original sync-based fallback)
        const cacheStatus = this.cache.getStatus();
        if (cacheStatus.documentCount === 0 && !localRAGStatus.loaded) {
            return {
                reply: 'I\'m currently offline and don\'t have any cached information. Please check your connection and try again.',
                confidence: 'none',
                sources: [],
                offline: true,
                degradedReason: 'No cached documents or offline vectors available'
            };
        }
        try {
            // Get documents from cache
            const docs = await this.cache.retrieve();
            // Ensure local RAG is indexed
            if (!this.localRAG.isIndexed()) {
                await this.localRAG.index(docs);
            }
            // Process with local RAG
            const result = await this.localRAG.ask(message);
            // Determine confidence based on match quality
            let confidence = 'medium';
            if (result.topScore < 0.3) {
                confidence = 'low';
            }
            else if (result.topScore > 0.7) {
                confidence = 'medium'; // Never 'high' for offline
            }
            // Process response through license manager (may add warning in production)
            const processedReply = this.licenseManager.processResponse(result.answer);
            return {
                reply: processedReply,
                confidence,
                sources: result.sources.map(s => ({
                    title: s.title,
                    snippet: s.snippet,
                    relevance: s.score
                })),
                offline: true,
                degradedReason: cacheStatus.isStale
                    ? 'Using stale cached data'
                    : 'Processed locally without server'
            };
        }
        catch (error) {
            console.error('[Cybernetic] Local RAG error:', error);
            return {
                reply: 'I\'m having trouble processing your request offline. Please try again when you\'re back online.',
                confidence: 'none',
                sources: [],
                offline: true,
                degradedReason: 'Local RAG processing failed'
            };
        }
    }
    /**
     * Monitor browser online/offline events
     */
    monitorConnection() {
        if (typeof window !== 'undefined') {
            window.addEventListener('online', () => {
                this.syncCache().catch(() => { });
            });
            window.addEventListener('offline', () => {
                this.setStatus('offline');
            });
            // Set initial status
            if (!navigator.onLine) {
                this.setStatus('offline');
            }
        }
    }
    /**
     * Update connection status and notify listeners
     */
    setStatus(status) {
        if (this.status !== status) {
            this.status = status;
            this.config.onStatusChange(status);
        }
    }
    /**
     * Normalize various error types to CyberneticError
     */
    normalizeError(error) {
        if (error instanceof Error) {
            // Check for specific error types
            if (error.message.includes('401') || error.message.includes('Unauthorized')) {
                return {
                    code: 'AUTH_ERROR',
                    message: 'Invalid or expired API key',
                    originalError: error
                };
            }
            if (error.message.includes('429') || error.message.includes('Rate limit')) {
                const match = error.message.match(/retry after (\d+)/i);
                return {
                    code: 'RATE_LIMIT',
                    message: 'Rate limit exceeded',
                    retryAfter: match ? parseInt(match[1], 10) : 60,
                    originalError: error
                };
            }
            if (error.message.includes('5') || error.message.includes('Server')) {
                return {
                    code: 'SERVER_ERROR',
                    message: 'Server error occurred',
                    originalError: error
                };
            }
            return {
                code: 'NETWORK_ERROR',
                message: error.message || 'Network error',
                originalError: error
            };
        }
        return {
            code: 'NETWORK_ERROR',
            message: 'Unknown error occurred'
        };
    }
    /**
     * Create standardized error response
     */
    createErrorResponse(message, confidence, retryAfter) {
        return {
            reply: message,
            confidence,
            sources: [],
            offline: false,
            retryAfter
        };
    }
    /**
     * Async sleep utility
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// src/agentic/SiteMapDiscovery.ts
// Multi-source sitemap discovery and merging for agentic navigation
// Zero-config mode: automatically discovers routes on any site
// ==================== SMART DEFAULTS ====================
/**
 * Comprehensive default selectors that work on most sites
 * Covers semantic HTML, common CSS patterns, and framework conventions
 */
const DEFAULT_NAV_SELECTORS = [
    // Semantic HTML5 navigation
    'nav a[href]',
    '[role="navigation"] a[href]',
    'header a[href]',
    // Common CSS class patterns
    '.nav a[href]',
    '.navbar a[href]',
    '.navigation a[href]',
    '.sidebar a[href]',
    '.side-nav a[href]',
    '.sidenav a[href]',
    '.menu a[href]',
    '.main-menu a[href]',
    '.top-menu a[href]',
    // Data attributes
    '[data-nav] a[href]',
    '[data-menu] a[href]',
    '[data-navigation] a[href]',
    // ARIA patterns
    '[aria-label*="navigation" i] a[href]',
    '[aria-label*="menu" i] a[href]',
    // Breadcrumbs
    '[aria-label*="breadcrumb" i] a[href]',
    '.breadcrumb a[href]',
    '.breadcrumbs a[href]',
    // Generic internal links (fallback - caught last)
    'a[href^="/"]',
];
/**
 * Default paths to exclude from discovery
 * Covers common auth, API, and framework-internal routes
 */
const DEFAULT_EXCLUDE_PATTERNS = [
    // Authentication routes
    '/login', '/logout', '/signin', '/signout', '/sign-in', '/sign-out',
    '/register', '/signup', '/sign-up', '/forgot-password', '/reset-password',
    '/auth/*', '/oauth/*', '/callback/*', '/sso/*',
    // API and framework internals
    '/api/*', '/_next/*', '/_nuxt/*', '/__*',
    // Special links
    'javascript:*', 'mailto:*', 'tel:*', '#*',
];
/**
 * Default maximum number of links to scan from DOM
 * Prevents performance issues on sites with many links
 */
const DEFAULT_MAX_DOM_LINKS = 200;
/**
 * Default cache TTL for localStorage (5 minutes)
 */
const DEFAULT_CACHE_TTL = 5 * 60 * 1000;
/**
 * Cache version - increment when cache format changes
 */
const CACHE_VERSION = '1';
/**
 * Multi-source sitemap discovery and management
 *
 * Combines routes from three sources:
 * 1. Static props (explicit configuration)
 * 2. Framework auto-discovery (React Router, Vue Router, etc.)
 * 3. Backend API (tenant-specific sitemaps)
 *
 * Zero-config mode: When agentic is enabled but no siteMapConfig provided,
 * automatically discovers routes using smart defaults.
 */
class SiteMapDiscovery {
    constructor(config, apiUrl, apiKey) {
        this.staticEntries = [];
        this.discoveredEntries = [];
        this.backendEntries = [];
        this.backendCache = null;
        this.isInitialized = false;
        this.initPromise = null;
        this.detectedFramework = 'generic';
        this.popstateHandler = null;
        this.config = config;
        this.apiUrl = apiUrl;
        this.apiKey = apiKey;
        // Load static entries immediately (synchronous)
        if (config.static) {
            this.staticEntries = this.enhanceEntries(config.static, 'props');
        }
    }
    // ==================== INITIALIZATION ====================
    /**
     * Initialize all sitemap sources
     * Call this during widget initialization or first query based on loadStrategy
     */
    async initialize() {
        if (this.isInitialized)
            return;
        if (this.initPromise)
            return this.initPromise;
        this.initPromise = this.performInitialization();
        await this.initPromise;
        this.isInitialized = true;
    }
    async performInitialization() {
        // Wait for DOM to be ready before discovery
        await this.waitForDOMReady();
        // Try to load from localStorage cache first
        const cachedEntries = this.loadFromLocalStorage();
        if (cachedEntries) {
            this.discoveredEntries = cachedEntries;
            // Debug: [SiteMapDiscovery] Loaded ${cachedEntries.length} entries from cache`);
            // Still run discovery in background to refresh cache
            this.refreshInBackground();
            return;
        }
        const tasks = [];
        // Source 2: Framework auto-discovery (enabled by default)
        if (this.config.discovery?.enabled !== false) {
            tasks.push(this.discoverFromFramework());
        }
        // Source 3: Backend API
        if (this.config.backend?.enabled !== false && this.config.backend?.endpoint) {
            tasks.push(this.fetchFromBackend());
        }
        const results = await Promise.allSettled(tasks);
        for (const result of results) {
            if (result.status === 'fulfilled') {
                const { entries, source } = result.value;
                if (source === 'discovery') {
                    this.discoveredEntries = entries;
                    // Save to localStorage
                    this.saveToLocalStorage(entries);
                }
                else if (source === 'backend') {
                    this.backendEntries = entries;
                }
            }
            else {
                console.warn('[SiteMapDiscovery] Source failed:', result.reason);
            }
        }
        // Set up popstate listener for SPA navigation
        this.setupPopstateListener();
        // Debug: [SiteMapDiscovery] Initialized with ${this.staticEntries.length + this.discoveredEntries.length + this.backendEntries.length} total entries`);
    }
    /**
     * Wait for DOM to be ready before scanning
     */
    async waitForDOMReady() {
        if (typeof document === 'undefined')
            return;
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            // DOM is ready, but give frameworks a microtask to mount
            await new Promise(resolve => setTimeout(resolve, 0));
            return;
        }
        return new Promise((resolve) => {
            document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
        });
    }
    /**
     * Set up popstate listener to re-discover on SPA navigation
     */
    setupPopstateListener() {
        if (typeof window === 'undefined')
            return;
        this.popstateHandler = () => {
            // Debounce re-discovery
            setTimeout(() => {
                this.refreshInBackground();
            }, 100);
        };
        window.addEventListener('popstate', this.popstateHandler);
    }
    /**
     * Refresh discovery in background without blocking
     */
    async refreshInBackground() {
        try {
            const result = await this.discoverFromFramework();
            if (result.entries.length > 0) {
                this.discoveredEntries = result.entries;
                this.saveToLocalStorage(result.entries);
            }
        }
        catch (error) {
            // Silent fail for background refresh
            console.debug('[SiteMapDiscovery] Background refresh failed:', error);
        }
    }
    // ==================== LOCALSTORAGE CACHING ====================
    /**
     * Get localStorage cache key for current origin
     */
    getCacheKey() {
        if (typeof window === 'undefined')
            return 'astermind-sitemap';
        return `astermind-sitemap-${window.location.origin}`;
    }
    /**
     * Load discovered entries from localStorage cache
     */
    loadFromLocalStorage() {
        if (typeof localStorage === 'undefined')
            return null;
        try {
            const key = this.getCacheKey();
            const cached = localStorage.getItem(key);
            if (!cached)
                return null;
            const data = JSON.parse(cached);
            // Check version
            if (data.version !== CACHE_VERSION) {
                localStorage.removeItem(key);
                return null;
            }
            // Check TTL
            const ttl = this.config.discovery?.cacheTtl ?? DEFAULT_CACHE_TTL;
            if (Date.now() - data.timestamp > ttl) {
                localStorage.removeItem(key);
                return null;
            }
            this.detectedFramework = data.framework;
            return data.entries;
        }
        catch (error) {
            console.debug('[SiteMapDiscovery] Failed to load from cache:', error);
            return null;
        }
    }
    /**
     * Save discovered entries to localStorage cache
     */
    saveToLocalStorage(entries) {
        if (typeof localStorage === 'undefined')
            return;
        if (this.config.discovery?.cacheRoutes === false)
            return;
        try {
            const key = this.getCacheKey();
            const data = {
                entries,
                url: typeof window !== 'undefined' ? window.location.href : '',
                timestamp: Date.now(),
                framework: this.detectedFramework,
                version: CACHE_VERSION
            };
            localStorage.setItem(key, JSON.stringify(data));
        }
        catch (error) {
            // localStorage might be full or disabled
            console.debug('[SiteMapDiscovery] Failed to save to cache:', error);
        }
    }
    /**
     * Clear localStorage cache
     */
    clearCache() {
        if (typeof localStorage === 'undefined')
            return;
        localStorage.removeItem(this.getCacheKey());
    }
    // ==================== CLEANUP ====================
    /**
     * Dispose of resources (event listeners, etc.)
     */
    dispose() {
        if (this.popstateHandler && typeof window !== 'undefined') {
            window.removeEventListener('popstate', this.popstateHandler);
            this.popstateHandler = null;
        }
    }
    /**
     * Get detected framework name
     */
    getDetectedFramework() {
        return this.detectedFramework;
    }
    /**
     * Get merged sitemap entries with deduplication
     */
    getMergedEntries() {
        return this.mergeEntries(this.staticEntries, this.discoveredEntries, this.backendEntries);
    }
    /**
     * Convert to legacy AgenticSiteMapEntry format for classifier compatibility
     */
    getLegacyEntries() {
        return this.getMergedEntries().map(e => ({
            path: e.path,
            name: e.name,
            description: e.description,
            params: e.params,
            dynamicParams: e.dynamicParams,
            aliases: e.aliases
        }));
    }
    // ==================== SOURCE 2: FRAMEWORK DISCOVERY ====================
    async discoverFromFramework() {
        const config = this.config.discovery || {};
        const entries = [];
        try {
            // Detect framework if not specified
            const framework = config.framework || this.detectFramework();
            this.detectedFramework = framework;
            let routes = [];
            switch (framework) {
                case 'react-router':
                    routes = this.discoverReactRouter();
                    break;
                case 'vue-router':
                    routes = this.discoverVueRouter();
                    break;
                case 'next':
                    routes = this.discoverNextRoutes();
                    break;
                case 'angular':
                    routes = this.discoverAngularRoutes();
                    break;
                default:
                    if (config.domFallback !== false) {
                        routes = this.discoverFromDOM(config.navSelectors);
                    }
            }
            // Transform routes to sitemap entries
            for (const route of routes) {
                // Apply exclude patterns
                if (this.shouldExcludeRoute(route.path, config.excludePaths)) {
                    continue;
                }
                // Apply custom transform if provided
                let entry;
                if (config.transformRoute) {
                    entry = config.transformRoute(route);
                }
                else {
                    entry = this.defaultRouteTransform(route);
                }
                if (entry) {
                    entries.push(this.enhanceEntry(entry, 'discovery'));
                }
            }
            // Debug: [SiteMapDiscovery] Discovered ${entries.length} routes from ${framework}`);
        }
        catch (error) {
            console.warn('[SiteMapDiscovery] Framework discovery failed:', error);
            return {
                entries: [],
                source: 'discovery',
                timestamp: Date.now(),
                error: String(error)
            };
        }
        return {
            entries,
            source: 'discovery',
            timestamp: Date.now()
        };
    }
    /**
     * Detect JavaScript framework from global objects
     */
    detectFramework() {
        if (typeof window === 'undefined')
            return 'generic';
        const win = window;
        // React Router v6.4+ data router
        if (win.__REACT_ROUTER_DATA_ROUTER__) {
            return 'react-router';
        }
        // React Router v5 context
        if (win.__reactRouterContext) {
            return 'react-router';
        }
        // Vue Router
        if (win.__VUE_ROUTER__ || win.__VUE_APP__?.$router) {
            return 'vue-router';
        }
        // Next.js
        if (win.__NEXT_DATA__) {
            return 'next';
        }
        // Angular
        if (win.ng || document.querySelector('[ng-version]')) {
            return 'angular';
        }
        return 'generic';
    }
    /**
     * Discover routes from React Router
     */
    discoverReactRouter() {
        const routes = [];
        if (typeof window === 'undefined')
            return routes;
        const win = window;
        // Try React Router v6.4+ data router
        const dataRouter = win.__REACT_ROUTER_DATA_ROUTER__;
        if (dataRouter?.routes) {
            this.extractRoutesFromConfig(dataRouter.routes, routes, '');
            return routes;
        }
        // Fallback: Try to find routes from __REACT_DEVTOOLS_GLOBAL_HOOK__
        // This is less reliable but can work in some cases
        return routes;
    }
    /**
     * Extract routes recursively from React Router config
     */
    extractRoutesFromConfig(routeConfigs, routes, parentPath) {
        for (const config of routeConfigs) {
            const path = this.normalizePath(parentPath, config.path || '');
            // Extract route info from handle metadata
            const route = {
                path,
                name: config.handle?.name || config.handle?.title || config.id,
                meta: config.handle || {},
                children: []
            };
            // Only include routes with paths (skip layout routes without path)
            if (config.path || config.index) {
                routes.push(route);
            }
            // Recursively process children
            if (config.children) {
                this.extractRoutesFromConfig(config.children, routes, path);
            }
        }
    }
    /**
     * Discover routes from Vue Router
     */
    discoverVueRouter() {
        const routes = [];
        if (typeof window === 'undefined')
            return routes;
        const win = window;
        // Access Vue Router instance
        const app = win.__VUE_APP__;
        const router = app?.$router || win.__VUE_ROUTER__;
        if (router?.getRoutes) {
            const vueRoutes = router.getRoutes();
            for (const vueRoute of vueRoutes) {
                routes.push({
                    path: vueRoute.path,
                    name: vueRoute.name?.toString(),
                    meta: vueRoute.meta || {},
                    component: vueRoute.components?.default?.name
                });
            }
        }
        return routes;
    }
    /**
     * Discover routes from Next.js
     */
    discoverNextRoutes() {
        const routes = [];
        if (typeof window === 'undefined')
            return routes;
        const win = window;
        const nextData = win.__NEXT_DATA__;
        if (!nextData)
            return routes;
        // Next.js doesn't expose routes directly in client
        // We can extract from build manifest if available
        const pages = nextData.buildManifest?.pages || {};
        for (const pagePath of Object.keys(pages)) {
            if (pagePath.startsWith('/_'))
                continue; // Skip internal pages
            routes.push({
                path: pagePath,
                name: this.pathToName(pagePath)
            });
        }
        return routes;
    }
    /**
     * Discover routes from Angular Router
     */
    discoverAngularRoutes() {
        // Angular route discovery requires access to Router service
        // which is typically not exposed globally
        // Users should provide routes via props or backend
        return [];
    }
    /**
     * Discover routes by scanning DOM navigation elements
     * Uses smart defaults when no selectors provided
     */
    discoverFromDOM(selectors) {
        const routes = [];
        if (typeof document === 'undefined')
            return routes;
        // Use provided selectors or smart defaults
        const allSelectors = selectors || DEFAULT_NAV_SELECTORS;
        // Get max links limit from config or use default
        const maxLinks = this.config.discovery?.maxDomLinks ?? DEFAULT_MAX_DOM_LINKS;
        // Get exclude patterns (merge user-provided with defaults)
        const userExcludes = this.config.discovery?.excludePaths || [];
        const allExcludes = [...DEFAULT_EXCLUDE_PATTERNS, ...userExcludes];
        const seen = new Set();
        let linkCount = 0;
        for (const selector of allSelectors) {
            // Stop if we've hit the limit
            if (linkCount >= maxLinks)
                break;
            try {
                const links = document.querySelectorAll(selector);
                for (const link of links) {
                    // Stop if we've hit the limit
                    if (linkCount >= maxLinks)
                        break;
                    const href = link.getAttribute('href');
                    if (!href)
                        continue;
                    // Only process internal links
                    if (href.startsWith('http') && !href.startsWith(window.location.origin)) {
                        continue;
                    }
                    // Normalize path
                    let path = href;
                    if (href.startsWith('http')) {
                        try {
                            path = new URL(href).pathname;
                        }
                        catch {
                            continue;
                        }
                    }
                    else if (!href.startsWith('/')) {
                        path = `/${href}`;
                    }
                    // Clean path (remove query string and hash)
                    const cleanPath = path.split('?')[0].split('#')[0];
                    // Skip duplicates
                    if (seen.has(cleanPath))
                        continue;
                    // Skip root path
                    if (cleanPath === '/')
                        continue;
                    // Skip paths matching exclude patterns
                    if (this.shouldExcludeRoute(cleanPath, allExcludes))
                        continue;
                    seen.add(cleanPath);
                    linkCount++;
                    routes.push({
                        path: cleanPath,
                        name: link.textContent?.trim() || this.pathToName(cleanPath)
                    });
                }
            }
            catch (e) {
                // Invalid selector - skip
            }
        }
        // Debug: [SiteMapDiscovery] DOM scan found ${routes.length} routes (limit: ${maxLinks})`);
        return routes;
    }
    // ==================== SOURCE 3: BACKEND API ====================
    async fetchFromBackend() {
        const config = this.config.backend || {};
        const endpoint = config.endpoint || '/api/external/sitemap';
        // Check cache
        if (this.backendCache && Date.now() < this.backendCache.expiresAt) {
            return {
                entries: this.backendCache.entries,
                source: 'backend',
                timestamp: this.backendCache.fetchedAt
            };
        }
        try {
            const url = new URL(endpoint, this.apiUrl);
            // Add query params
            if (config.tenantScoped !== false) {
                url.searchParams.set('tenantScoped', 'true');
            }
            if (config.includeGeneral !== false) {
                url.searchParams.set('includeGeneral', 'true');
            }
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'X-API-Key': this.apiKey,
                    'Content-Type': 'application/json',
                    ...config.headers
                }
            });
            if (!response.ok) {
                throw new Error(`Backend sitemap fetch failed: ${response.status}`);
            }
            const data = await response.json();
            const entries = this.transformBackendResponse(data);
            // Update cache
            const ttl = config.cacheTtl || 300000; // 5 minutes default
            this.backendCache = {
                entries,
                fetchedAt: Date.now(),
                expiresAt: Date.now() + ttl
            };
            // Debug: [SiteMapDiscovery] Loaded ${entries.length} routes from backend`);
            return {
                entries,
                source: 'backend',
                timestamp: Date.now()
            };
        }
        catch (error) {
            console.warn('[SiteMapDiscovery] Backend fetch failed:', error);
            return {
                entries: this.backendCache?.entries || [],
                source: 'backend',
                timestamp: Date.now(),
                error: String(error)
            };
        }
    }
    /**
     * Transform backend response to enhanced entries
     */
    transformBackendResponse(data) {
        // Handle array or object with entries/sitemap key
        const rawEntries = Array.isArray(data)
            ? data
            : data.entries || data.sitemap || data.routes || [];
        return rawEntries.map((entry) => this.enhanceEntry({
            path: entry.url || entry.path,
            name: entry.title || entry.name,
            description: entry.description,
            aliases: entry.keywords || entry.aliases,
            params: entry.params,
            dynamicParams: entry.dynamicParams
        }, 'backend'));
    }
    // ==================== MERGE LOGIC ====================
    /**
     * Merge entries from all sources with deduplication
     */
    mergeEntries(...sources) {
        const mergeConfig = this.config.merge || {};
        const priorityOrder = mergeConfig.sourcePriority || ['props', 'backend', 'discovery'];
        const dedupeStrategy = mergeConfig.deduplication || 'path';
        // Create priority map (higher number = higher priority)
        const priorityMap = new Map();
        priorityOrder.forEach((source, index) => {
            priorityMap.set(source, priorityOrder.length - index);
        });
        // Flatten all entries with priority scores
        const allEntries = [];
        for (const sourceEntries of sources) {
            for (const entry of sourceEntries) {
                const priority = priorityMap.get(entry._source || 'props') || 0;
                allEntries.push({ ...entry, _priority: priority });
            }
        }
        // Sort by priority (higher first)
        allEntries.sort((a, b) => (b._priority || 0) - (a._priority || 0));
        // Deduplicate
        const seen = new Map();
        for (const entry of allEntries) {
            let key;
            switch (dedupeStrategy) {
                case 'name':
                    key = entry.name.toLowerCase();
                    break;
                case 'both':
                    key = `${entry.path}|${entry.name.toLowerCase()}`;
                    break;
                case 'path':
                default:
                    key = this.normalizePath('', entry.path);
            }
            if (!seen.has(key)) {
                seen.set(key, entry);
            }
            else if (mergeConfig.keepNonConflicting !== false) {
                // Merge non-conflicting fields from lower priority entry
                const existing = seen.get(key);
                if (!existing.description && entry.description) {
                    existing.description = entry.description;
                }
                if (!existing.aliases?.length && entry.aliases?.length) {
                    existing.aliases = entry.aliases;
                }
            }
        }
        return Array.from(seen.values());
    }
    // ==================== HELPERS ====================
    enhanceEntries(entries, source) {
        return entries.map(e => this.enhanceEntry(e, source));
    }
    enhanceEntry(entry, source) {
        return {
            ...entry,
            _source: source,
            _discoveredAt: Date.now()
        };
    }
    /**
     * Default transformation from DiscoveredRoute to AgenticSiteMapEntry
     */
    defaultRouteTransform(route) {
        // Skip routes without meaningful paths
        if (!route.path || route.path === '*' || route.path === '**') {
            return null;
        }
        return {
            path: route.path,
            name: route.name || route.meta?.title || this.pathToName(route.path),
            description: route.meta?.description,
            aliases: route.meta?.keywords || route.meta?.aliases,
            dynamicParams: route.path.includes(':') || route.path.includes('[')
        };
    }
    /**
     * Normalize path by combining parent and child paths
     */
    normalizePath(parent, path) {
        if (!path || path === '/')
            return parent || '/';
        if (path.startsWith('/'))
            return path;
        if (!parent || parent === '/')
            return `/${path}`;
        return `${parent}/${path}`.replace(/\/+/g, '/');
    }
    /**
     * Convert path to human-readable name
     */
    pathToName(path) {
        const segments = path.split('/').filter(Boolean);
        const last = segments[segments.length - 1] || 'Home';
        return last
            .replace(/[-_]/g, ' ')
            .replace(/\[.*?\]/g, '') // Remove Next.js dynamic segments
            .replace(/:/g, '') // Remove React Router params
            .split(' ')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ')
            .trim() || 'Page';
    }
    /**
     * Check if route should be excluded based on glob patterns
     */
    shouldExcludeRoute(path, patterns) {
        if (!patterns?.length)
            return false;
        for (const pattern of patterns) {
            // Simple glob matching (supports * wildcard)
            const regex = new RegExp('^' + pattern.replace(/\*/g, '.*').replace(/\?/g, '.') + '$');
            if (regex.test(path))
                return true;
        }
        return false;
    }
    /**
     * Force refresh from all sources
     */
    async refresh() {
        this.backendCache = null;
        this.isInitialized = false;
        this.initPromise = null;
        await this.initialize();
    }
    /**
     * Check if initialized
     */
    isReady() {
        return this.isInitialized;
    }
    /**
     * Get current entry counts by source
     */
    getStats() {
        return {
            static: this.staticEntries.length,
            discovered: this.discoveredEntries.length,
            backend: this.backendEntries.length,
            merged: this.getMergedEntries().length
        };
    }
}

// src/agentic/CyberneticIntentClassifier.ts
// Hybrid intent classification for agentic capabilities
/**
 * Action intent patterns with weights
 */
const ACTION_PATTERNS = {
    navigate: [
        { pattern: /^(go to|navigate to|take me to|open|show me|bring up)\s+(.+)/i, type: 'navigate', weight: 0.9 },
        { pattern: /^(i want to see|i need to go to|let me see)\s+(.+)/i, type: 'navigate', weight: 0.85 },
        { pattern: /^(where is|how do i get to)\s+(.+)/i, type: 'navigate', weight: 0.6 }, // Lower - might be question
    ],
    fillForm: [
        { pattern: /^(search for|find|look up|search)\s+(.+)/i, type: 'fillForm', weight: 0.88 },
        { pattern: /^(filter by|set .+ to|change .+ to)\s+(.+)/i, type: 'fillForm', weight: 0.85 },
        { pattern: /^(enter|type|put)\s+(.+)\s+(in|into)\s+(.+)/i, type: 'fillForm', weight: 0.9 },
    ],
    clickElement: [
        { pattern: /^(click|press|hit|tap)\s+(the\s+)?(.+)\s+(button|link)/i, type: 'clickElement', weight: 0.9 },
        { pattern: /^(submit|send|confirm|save|cancel|delete)/i, type: 'clickElement', weight: 0.85 },
    ],
    triggerModal: [
        { pattern: /^(open|show|display)\s+(the\s+)?(.+)\s+(modal|dialog|popup|window)/i, type: 'triggerModal', weight: 0.9 },
        { pattern: /^(open|show)\s+(help|settings|preferences|options)/i, type: 'triggerModal', weight: 0.85 },
    ],
    scroll: [
        { pattern: /^(scroll to|jump to|go to)\s+(the\s+)?(.+)\s+(section|area|part)/i, type: 'scroll', weight: 0.85 },
        { pattern: /^(show me|take me to)\s+(the\s+)?(bottom|top|end|beginning)/i, type: 'scroll', weight: 0.8 },
    ],
    highlight: [
        { pattern: /^(highlight|show me|point to|where is)\s+(the\s+)?(.+)/i, type: 'highlight', weight: 0.7 },
    ],
    custom: [
        { pattern: /^(export|download|refresh|reload|sync)/i, type: 'custom', weight: 0.85 },
    ]
};
/**
 * Intent classifier for agentic actions
 *
 * Classifies user messages to determine if they should trigger
 * a local DOM action or be sent to the backend RAG.
 */
class CyberneticIntentClassifier {
    /**
     * Create a new intent classifier
     *
     * @param config - Agent configuration
     * @param apiUrl - Backend API URL (for multi-source sitemap)
     * @param apiKey - API key for authentication
     */
    constructor(config, apiUrl = '', apiKey = '') {
        this.categories = [];
        this.topicKeywords = new Map(); // topic -> keywords
        /** Multi-source sitemap discovery instance */
        this.siteMapDiscovery = null;
        this.config = config;
        this.siteMapIndex = new Map();
        this.formIndex = new Map();
        this.modalIndex = new Map();
        // Build indexes for fast lookup
        this.buildIndexes();
        // Auto-enable sitemap discovery when agentic is enabled but no explicit config provided
        // This enables zero-config mode: just set enabled: true and discovery works automatically
        const siteMapConfig = config.siteMapConfig ?? (config.enabled && !config.siteMap?.length
            ? { discovery: { enabled: true } }
            : undefined);
        // Initialize multi-source sitemap discovery
        if (siteMapConfig) {
            this.siteMapDiscovery = new SiteMapDiscovery(siteMapConfig, apiUrl, apiKey);
        }
    }
    /**
     * Build search indexes from configuration
     */
    buildIndexes() {
        // Site map index
        if (this.config.siteMap) {
            for (const entry of this.config.siteMap) {
                // Index by name and aliases
                const keys = [
                    entry.name.toLowerCase(),
                    entry.path.toLowerCase(),
                    ...(entry.aliases || []).map(a => a.toLowerCase())
                ];
                for (const key of keys) {
                    this.siteMapIndex.set(key, entry);
                }
            }
        }
        // Form fields index
        if (this.config.forms) {
            for (const [key, field] of Object.entries(this.config.forms)) {
                const keys = [
                    key.toLowerCase(),
                    field.name.toLowerCase(),
                    ...(field.aliases || []).map(a => a.toLowerCase())
                ];
                for (const k of keys) {
                    this.formIndex.set(k, field);
                }
            }
        }
        // Modal triggers index
        if (this.config.modals) {
            for (const [key, modal] of Object.entries(this.config.modals)) {
                const keys = [
                    key.toLowerCase(),
                    modal.id.toLowerCase(),
                    ...(modal.aliases || []).map(a => a.toLowerCase())
                ];
                for (const k of keys) {
                    this.modalIndex.set(k, modal);
                }
            }
        }
    }
    /**
     * Classify user message intent
     *
     * @param message - User's message to classify
     * @returns Classification result with action and confidence
     */
    classify(message) {
        if (!this.config.enabled) {
            return {
                action: null,
                shouldEscalate: true,
                rawConfidence: 0
            };
        }
        const normalizedMessage = message.trim().toLowerCase();
        // Try each action type
        for (const [actionType, patterns] of Object.entries(ACTION_PATTERNS)) {
            for (const { pattern, weight } of patterns) {
                const match = message.match(pattern);
                if (match) {
                    const result = this.buildAction(actionType, match, weight);
                    if (result) {
                        return result;
                    }
                }
            }
        }
        // No pattern match - try fuzzy site map match
        const siteMapResult = this.fuzzyMatchSiteMap(normalizedMessage);
        if (siteMapResult) {
            return siteMapResult;
        }
        // Check for custom action keywords
        const customResult = this.matchCustomAction(normalizedMessage);
        if (customResult) {
            return customResult;
        }
        // No match - escalate to backend
        return {
            action: null,
            shouldEscalate: true,
            rawConfidence: 0,
            matchedPatterns: []
        };
    }
    /**
     * Build action from pattern match
     */
    buildAction(actionType, match, baseWeight) {
        const threshold = this.config.confidenceThreshold ?? 0.8;
        switch (actionType) {
            case 'navigate': {
                const target = this.extractTarget(match);
                const siteMatch = this.findSiteMapMatch(target);
                if (siteMatch) {
                    const confidence = baseWeight * siteMatch.similarity;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'navigate',
                            target: siteMatch.entry.path,
                            confidence,
                            explanation: `Navigate to ${siteMatch.entry.name}`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['navigate', siteMatch.entry.name]
                    };
                }
                break;
            }
            case 'fillForm': {
                const target = this.extractTarget(match);
                const { field, value } = this.parseFormIntent(match);
                if (field) {
                    const confidence = baseWeight * 0.95;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'fillForm',
                            target: field.selector,
                            params: { value },
                            confidence,
                            explanation: `Fill "${field.name}" with "${value}"`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['fillForm', field.name]
                    };
                }
                // Default to search field
                if (this.config.forms?.search) {
                    const confidence = baseWeight * 0.9;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'fillForm',
                            target: this.config.forms.search.selector,
                            params: { value: target },
                            confidence,
                            explanation: `Search for "${target}"`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['fillForm', 'search']
                    };
                }
                break;
            }
            case 'clickElement': {
                const buttonName = this.extractTarget(match);
                const selector = this.findButtonSelector(buttonName);
                if (selector) {
                    const confidence = baseWeight * 0.95;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'clickElement',
                            target: selector,
                            confidence,
                            explanation: `Click "${buttonName}" button`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['clickElement', buttonName]
                    };
                }
                break;
            }
            case 'triggerModal': {
                const modalName = this.extractTarget(match);
                const modal = this.findModalConfig(modalName);
                if (modal) {
                    const confidence = baseWeight * 0.95;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'triggerModal',
                            target: modal.trigger,
                            confidence,
                            explanation: `Open ${modal.id} modal`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['triggerModal', modal.id]
                    };
                }
                break;
            }
            case 'scroll': {
                const target = this.extractTarget(match);
                const selector = this.findScrollTarget(target);
                if (selector) {
                    const confidence = baseWeight * 0.9;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'scroll',
                            target: selector,
                            confidence,
                            explanation: `Scroll to ${target}`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['scroll', target]
                    };
                }
                break;
            }
            case 'highlight': {
                // Highlighting is typically lower confidence
                const target = this.extractTarget(match);
                const selector = this.findElementByDescription(target);
                if (selector) {
                    const confidence = baseWeight * 0.85;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'highlight',
                            target: selector,
                            confidence,
                            explanation: `Highlight ${target}`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['highlight', target]
                    };
                }
                break;
            }
            case 'custom': {
                const actionName = match[1]?.toLowerCase();
                if (this.config.customActions?.[actionName]) {
                    const confidence = baseWeight * 0.95;
                    return {
                        action: {
                            id: `action-${Date.now()}`,
                            type: 'custom',
                            target: actionName,
                            confidence,
                            explanation: `Execute ${actionName} action`
                        },
                        shouldEscalate: confidence < threshold,
                        rawConfidence: confidence,
                        matchedPatterns: ['custom', actionName]
                    };
                }
                break;
            }
        }
        return null;
    }
    /**
     * Extract target from regex match
     */
    extractTarget(match) {
        // Get the last non-empty capture group
        for (let i = match.length - 1; i >= 1; i--) {
            if (match[i]) {
                return match[i].trim();
            }
        }
        return '';
    }
    /**
     * Strip common articles and filler words for better matching
     */
    stripArticles(text) {
        return text
            .replace(/^(the|a|an|my|our|your|this|that)\s+/gi, '')
            .replace(/\s+(page|section|screen|view)$/gi, '')
            .trim();
    }
    /**
     * Find site map match for target string
     */
    findSiteMapMatch(target) {
        const normalizedTarget = target.toLowerCase().replace(/[^a-z0-9\s]/g, '');
        const strippedTarget = this.stripArticles(normalizedTarget);
        // Exact match (try both with and without articles)
        const exact = this.siteMapIndex.get(normalizedTarget) || this.siteMapIndex.get(strippedTarget);
        if (exact) {
            return { entry: exact, similarity: 1.0 };
        }
        // Fuzzy match using stripped version for better accuracy
        let bestMatch = null;
        let bestScore = 0;
        for (const [key, entry] of this.siteMapIndex) {
            // Compare both original and stripped versions, take the better score
            const score1 = this.calculateSimilarity(normalizedTarget, key);
            const score2 = this.calculateSimilarity(strippedTarget, key);
            const score = Math.max(score1, score2);
            if (score > bestScore && score > 0.5) {
                bestScore = score;
                bestMatch = entry;
            }
        }
        if (bestMatch) {
            return { entry: bestMatch, similarity: bestScore };
        }
        return null;
    }
    /**
     * Fuzzy match against site map for unstructured queries
     */
    fuzzyMatchSiteMap(message) {
        const threshold = this.config.confidenceThreshold ?? 0.8;
        // Look for page/site name mentions
        for (const [key, entry] of this.siteMapIndex) {
            if (message.includes(key)) {
                const confidence = 0.75; // Lower confidence for implicit matches
                return {
                    action: {
                        id: `action-${Date.now()}`,
                        type: 'navigate',
                        target: entry.path,
                        confidence,
                        explanation: `Navigate to ${entry.name}`
                    },
                    shouldEscalate: confidence < threshold,
                    rawConfidence: confidence,
                    matchedPatterns: ['fuzzy-sitemap', key]
                };
            }
        }
        return null;
    }
    /**
     * Match custom action keywords
     */
    matchCustomAction(message) {
        if (!this.config.customActions)
            return null;
        const threshold = this.config.confidenceThreshold ?? 0.8;
        for (const actionName of Object.keys(this.config.customActions)) {
            if (message.includes(actionName.toLowerCase())) {
                const confidence = 0.85;
                return {
                    action: {
                        id: `action-${Date.now()}`,
                        type: 'custom',
                        target: actionName,
                        confidence,
                        explanation: `Execute ${actionName}`
                    },
                    shouldEscalate: confidence < threshold,
                    rawConfidence: confidence,
                    matchedPatterns: ['custom', actionName]
                };
            }
        }
        return null;
    }
    /**
     * Parse form fill intent
     */
    parseFormIntent(match) {
        const fullText = match[0];
        // Try to extract field name and value
        for (const [, field] of this.formIndex) {
            if (fullText.toLowerCase().includes(field.name.toLowerCase())) {
                // Extract value - everything after the field name
                const valueMatch = fullText.match(new RegExp(`${field.name}[^a-z]*(.+)`, 'i'));
                return {
                    field,
                    value: valueMatch?.[1]?.trim() || ''
                };
            }
        }
        return { field: null, value: this.extractTarget(match) };
    }
    /**
     * Find button selector by name
     */
    findButtonSelector(name) {
        const normalizedName = name.toLowerCase();
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            // Return a generic selector for server-side contexts
            return `button:contains("${name}")`;
        }
        // Common button selectors
        const selectors = [
            `[data-action="${normalizedName}"]`,
            `[aria-label*="${normalizedName}" i]`,
            `.btn-${normalizedName}`,
            `#${normalizedName}-button`,
            `button.${normalizedName}`
        ];
        // Check if any selector matches
        for (const selector of selectors) {
            try {
                if (document.querySelector(selector)) {
                    return selector;
                }
            }
            catch {
                // Invalid selector, continue
            }
        }
        // Fallback - try to find any button with matching text
        const buttons = document.querySelectorAll('button, [role="button"], a.btn');
        for (const btn of buttons) {
            if (btn.textContent?.toLowerCase().includes(normalizedName)) {
                return this.generateUniqueSelector(btn);
            }
        }
        return null;
    }
    /**
     * Find modal config by name
     */
    findModalConfig(name) {
        return this.modalIndex.get(name.toLowerCase()) || null;
    }
    /**
     * Find scroll target selector
     */
    findScrollTarget(target) {
        const normalizedTarget = target.toLowerCase();
        // Special cases
        if (normalizedTarget === 'top' || normalizedTarget === 'beginning') {
            return 'body';
        }
        if (normalizedTarget === 'bottom' || normalizedTarget === 'end') {
            return 'body:last-child';
        }
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            return `#${normalizedTarget.replace(/\s+/g, '-')}`;
        }
        // Try to find section by id or class
        const selectors = [
            `#${normalizedTarget.replace(/\s+/g, '-')}`,
            `[data-section="${normalizedTarget}"]`,
            `.${normalizedTarget.replace(/\s+/g, '-')}-section`,
            `section[aria-label*="${normalizedTarget}" i]`
        ];
        for (const selector of selectors) {
            try {
                if (document.querySelector(selector)) {
                    return selector;
                }
            }
            catch {
                continue;
            }
        }
        return null;
    }
    /**
     * Find element by description
     */
    findElementByDescription(description) {
        const normalizedDesc = description.toLowerCase();
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            return `[aria-label*="${normalizedDesc}" i]`;
        }
        // Try common patterns
        const selectors = [
            `[aria-label*="${normalizedDesc}" i]`,
            `[data-testid*="${normalizedDesc}" i]`,
            `[title*="${normalizedDesc}" i]`
        ];
        for (const selector of selectors) {
            try {
                if (document.querySelector(selector)) {
                    return selector;
                }
            }
            catch {
                continue;
            }
        }
        return null;
    }
    /**
     * Generate unique selector for an element
     */
    generateUniqueSelector(element) {
        // Try ID first
        if (element.id) {
            return `#${element.id}`;
        }
        // Try data attributes
        if (element.getAttribute('data-testid')) {
            return `[data-testid="${element.getAttribute('data-testid')}"]`;
        }
        // Build path selector
        const path = [];
        let current = element;
        while (current && current !== document.body) {
            let selector = current.tagName.toLowerCase();
            if (current.className && typeof current.className === 'string') {
                const classes = current.className.split(' ').filter(c => c && !c.includes(' '));
                if (classes.length > 0) {
                    selector += `.${classes.slice(0, 2).join('.')}`;
                }
            }
            path.unshift(selector);
            current = current.parentElement;
        }
        return path.join(' > ');
    }
    /**
     * Calculate string similarity (Jaccard index)
     */
    calculateSimilarity(a, b) {
        const setA = new Set(a.split(/\s+/));
        const setB = new Set(b.split(/\s+/));
        const intersection = new Set([...setA].filter(x => setB.has(x)));
        const union = new Set([...setA, ...setB]);
        return intersection.size / union.size;
    }
    /**
     * Get configuration
     */
    getConfig() {
        return this.config;
    }
    // ==================== EXPORT/SITEMAP LOADING METHODS ====================
    /**
     * Train classifier from exported documents
     * Extracts topics and keywords for better intent classification
     *
     * @param exportData - The offline vector export data
     */
    async trainFromExport(exportData) {
        // Load explicit categories if provided
        if (exportData.categories) {
            this.categories = exportData.categories;
            for (const category of exportData.categories) {
                // Index category keywords for matching
                for (const keyword of category.keywords) {
                    const existing = this.topicKeywords.get(category.name) || [];
                    existing.push(keyword.toLowerCase());
                    this.topicKeywords.set(category.name, existing);
                }
            }
        }
        // Extract topics from documents
        const docTopics = this.extractTopicsFromDocuments(exportData.documents);
        for (const [topic, keywords] of docTopics) {
            const existing = this.topicKeywords.get(topic) || [];
            existing.push(...keywords);
            this.topicKeywords.set(topic, [...new Set(existing)]); // Dedupe
        }
        // Load sitemap from export if available
        if (exportData.sitemap) {
            this.loadSitemapEntries(exportData.sitemap);
        }
        // Debug: [CyberneticIntentClassifier] Trained with ${this.categories.length} categories, ${this.topicKeywords.size} topics`);
    }
    /**
     * Load sitemap from configuration
     *
     * @param config - Sitemap configuration with URL or inline data
     */
    async loadSitemap(config) {
        if (!config.enabled)
            return;
        // Load from inline data
        if (config.sitemapData) {
            this.loadSitemapEntries(config.sitemapData);
            return;
        }
        // Load from URL
        if (config.sitemapUrl) {
            try {
                const response = await fetch(config.sitemapUrl);
                if (!response.ok) {
                    throw new Error(`Failed to fetch sitemap: ${response.status}`);
                }
                const data = await response.json();
                this.loadSitemapEntries(data);
            }
            catch (error) {
                console.error('[CyberneticIntentClassifier] Failed to load sitemap:', error);
            }
        }
    }
    /**
     * Load sitemap entries into the index
     */
    loadSitemapEntries(entries) {
        for (const entry of entries) {
            // Convert GlobalSiteMapEntry to agentic SiteMapEntry format
            const siteMapEntry = {
                path: entry.url,
                name: entry.title,
                description: entry.description,
                aliases: entry.keywords
            };
            // Index by title, URL, description, and keywords
            const keys = [
                entry.title.toLowerCase(),
                entry.url.toLowerCase(),
                ...(entry.keywords || []).map(k => k.toLowerCase())
            ];
            if (entry.description) {
                // Also index significant words from description
                const descWords = entry.description
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(w => w.length > 4);
                keys.push(...descWords);
            }
            for (const key of keys) {
                this.siteMapIndex.set(key, siteMapEntry);
            }
        }
        // Debug: [CyberneticIntentClassifier] Loaded ${entries.length} sitemap entries`);
    }
    /**
     * Extract topics and keywords from document content
     */
    extractTopicsFromDocuments(docs) {
        const topics = new Map();
        for (const doc of docs) {
            // Use title as topic
            const topic = doc.title.toLowerCase();
            const keywords = [];
            // Extract keywords from metadata
            if (doc.metadata?.keywords) {
                keywords.push(...doc.metadata.keywords.map(k => k.toLowerCase()));
            }
            // Extract significant words from title
            const titleWords = doc.title
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .split(/\s+/)
                .filter(w => w.length > 3 && !this.isStopWord(w));
            keywords.push(...titleWords);
            // Extract frequent significant words from content (simple TF)
            const contentWords = doc.content
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .split(/\s+/)
                .filter(w => w.length > 4 && !this.isStopWord(w));
            const wordFreq = new Map();
            for (const word of contentWords) {
                wordFreq.set(word, (wordFreq.get(word) || 0) + 1);
            }
            // Get top 5 most frequent words
            const topWords = [...wordFreq.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([word]) => word);
            keywords.push(...topWords);
            topics.set(topic, [...new Set(keywords)]); // Dedupe
        }
        return topics;
    }
    /**
     * Check if word is a stop word
     */
    isStopWord(word) {
        const stopWords = new Set([
            'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
            'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during',
            'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had',
            'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might',
            'this', 'that', 'these', 'those', 'it', 'its', 'they', 'them', 'their',
            'what', 'which', 'who', 'whom', 'when', 'where', 'why', 'how',
            'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some',
            'such', 'no', 'not', 'only', 'same', 'so', 'than', 'too', 'very',
            'can', 'just', 'dont', 'now', 'here', 'there', 'also', 'your', 'you'
        ]);
        return stopWords.has(word);
    }
    /**
     * Get loaded categories
     */
    getCategories() {
        return this.categories;
    }
    /**
     * Get topic keywords
     */
    getTopicKeywords() {
        return this.topicKeywords;
    }
    /**
     * Check if classifier has been trained
     */
    isTrained() {
        return this.categories.length > 0 || this.topicKeywords.size > 0;
    }
    // ==================== MULTI-SOURCE SITEMAP DISCOVERY ====================
    /**
     * Initialize multi-source sitemap discovery
     * Call this after construction to load all sitemap sources
     *
     * @returns Promise that resolves when discovery is complete
     */
    async initializeDiscovery() {
        if (!this.siteMapDiscovery) {
            // Debug: [CyberneticIntentClassifier] No siteMapConfig provided, skipping discovery');
            return;
        }
        try {
            await this.siteMapDiscovery.initialize();
            this.rebuildIndexesFromDiscovery();
            // Debug: [CyberneticIntentClassifier] Discovery complete, indexed ${this.siteMapIndex.size} entries`);
        }
        catch (error) {
            console.error('[CyberneticIntentClassifier] Discovery initialization failed:', error);
        }
    }
    /**
     * Rebuild sitemap indexes from discovery results
     * Merges discovered entries with existing static config
     */
    rebuildIndexesFromDiscovery() {
        if (!this.siteMapDiscovery)
            return;
        // Get merged entries from discovery (includes static props)
        const mergedEntries = this.siteMapDiscovery.getLegacyEntries();
        // Clear existing sitemap index (keep form and modal indexes)
        this.siteMapIndex.clear();
        // Index all merged entries
        for (const entry of mergedEntries) {
            const siteMapEntry = {
                path: entry.path,
                name: entry.name,
                description: entry.description,
                params: entry.params,
                aliases: entry.aliases
            };
            // Index by name and aliases
            const keys = [
                entry.name.toLowerCase(),
                entry.path.toLowerCase(),
                ...(entry.aliases || []).map(a => a.toLowerCase())
            ];
            // Also index description words for fuzzy matching
            if (entry.description) {
                const descWords = entry.description
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(w => w.length > 4 && !this.isStopWord(w));
                keys.push(...descWords);
            }
            for (const key of keys) {
                this.siteMapIndex.set(key, siteMapEntry);
            }
        }
    }
    /**
     * Check if sitemap discovery is ready
     *
     * @returns true if discovery is complete and entries are loaded
     */
    isSiteMapReady() {
        if (!this.siteMapDiscovery) {
            // No discovery configured - check if static sitemap exists
            return this.siteMapIndex.size > 0 || (this.config.siteMap?.length ?? 0) > 0;
        }
        return this.siteMapDiscovery.isReady();
    }
    /**
     * Ensure sitemap is ready before classification
     * Initializes discovery if not already done
     *
     * @returns Promise that resolves when sitemap is ready
     */
    async ensureSiteMapReady() {
        if (this.isSiteMapReady()) {
            return;
        }
        await this.initializeDiscovery();
    }
    /**
     * Refresh sitemap from all sources
     * Forces reload of backend and re-discovery
     *
     * @returns Promise that resolves when refresh is complete
     */
    async refreshSiteMap() {
        if (!this.siteMapDiscovery) {
            // Debug: [CyberneticIntentClassifier] No siteMapConfig provided, cannot refresh');
            return;
        }
        await this.siteMapDiscovery.refresh();
        this.rebuildIndexesFromDiscovery();
    }
    /**
     * Get the SiteMapDiscovery instance for advanced use cases
     *
     * @returns The SiteMapDiscovery instance or null if not configured
     */
    getSiteMapDiscovery() {
        return this.siteMapDiscovery;
    }
    /**
     * Get current sitemap entries (merged from all sources)
     *
     * @returns Array of sitemap entries
     */
    getSiteMapEntries() {
        if (this.siteMapDiscovery) {
            return this.siteMapDiscovery.getLegacyEntries();
        }
        // Fall back to static config
        return this.config.siteMap || [];
    }
}

// src/agentic/CyberneticAgent.ts
// DOM interaction agent for agentic actions
/**
 * DOM interaction agent
 *
 * Executes actions on the host page based on classified intent.
 * Supports navigation, form filling, clicking, scrolling, and more.
 */
class CyberneticAgent {
    constructor(config) {
        this.highlightOverlay = null;
        this.actionCount = 0;
        this.lastActionReset = Date.now();
        this.config = config;
        this.classifier = new CyberneticIntentClassifier(config);
        // Inject highlight styles (only in browser)
        if (typeof document !== 'undefined') {
            this.injectStyles();
        }
    }
    /**
     * Interpret user message and determine action
     *
     * @param message - User's message to interpret
     * @returns Intent classification with suggested action
     */
    interpretIntent(message) {
        return this.classifier.classify(message);
    }
    /**
     * Execute an agent action
     *
     * @param action - The action to execute
     * @returns Result of action execution
     */
    async executeAction(action) {
        // Reset action count every minute
        const now = Date.now();
        if (now - this.lastActionReset > 60000) {
            this.actionCount = 0;
            this.lastActionReset = now;
        }
        // Check rate limit
        const maxActions = this.config.maxActionsPerTurn ?? 5;
        if (this.actionCount >= maxActions) {
            return {
                success: false,
                message: `Rate limit exceeded. Maximum ${maxActions} actions per minute.`
            };
        }
        // Validate action is allowed
        if (!this.isActionAllowed(action)) {
            return {
                success: false,
                message: `Action type "${action.type}" is not allowed`
            };
        }
        this.actionCount++;
        switch (action.type) {
            case 'navigate':
                return this.navigate(action.target, action.params);
            case 'fillForm':
                return this.fillForm(action.target, action.params?.value);
            case 'clickElement':
                return this.clickElement(action.target);
            case 'triggerModal':
                return this.triggerModal(action.target);
            case 'scroll':
                return this.scrollToElement(action.target);
            case 'highlight':
                return this.highlightElement(action.target);
            case 'custom':
                return this.executeCustomAction(action.target, action.params);
            default:
                return {
                    success: false,
                    message: `Unknown action type: ${action.type}`
                };
        }
    }
    /**
     * Check if an action type is allowed by configuration
     */
    isActionAllowed(action) {
        const allowedActions = this.config.allowedActions;
        if (!allowedActions)
            return true; // All allowed by default
        const actionTypeMap = {
            'navigate': 'navigate',
            'fillForm': 'fill',
            'clickElement': 'click',
            'scroll': 'scroll',
            'highlight': 'click', // Highlight is a form of interaction
            'triggerModal': 'click',
            'custom': 'click'
        };
        const mappedType = actionTypeMap[action.type];
        return allowedActions.includes(mappedType);
    }
    /**
     * Check if selector is allowed (not blocked)
     */
    isSelectorAllowed(selector) {
        // Check blocked selectors
        if (this.config.blockedSelectors) {
            for (const blocked of this.config.blockedSelectors) {
                if (selector.includes(blocked)) {
                    return false;
                }
            }
        }
        // Check allowed selectors (if specified, only those are allowed)
        if (this.config.allowedSelectors && this.config.allowedSelectors.length > 0) {
            return this.config.allowedSelectors.some(allowed => selector.includes(allowed));
        }
        return true;
    }
    // ==================== ACTION IMPLEMENTATIONS ====================
    /**
     * Navigate to a URL
     */
    async navigate(path, params) {
        try {
            // Validate URL
            if (!this.validateNavigationUrl(path)) {
                return {
                    success: false,
                    message: 'Invalid or blocked URL'
                };
            }
            // Build URL with params
            let url = path;
            if (params) {
                const searchParams = new URLSearchParams();
                for (const [key, value] of Object.entries(params)) {
                    if (value !== undefined && value !== null) {
                        searchParams.set(key, String(value));
                    }
                }
                const queryString = searchParams.toString();
                if (queryString) {
                    url += (url.includes('?') ? '&' : '?') + queryString;
                }
            }
            // Check if we're in a browser environment
            if (typeof window === 'undefined') {
                return {
                    success: true,
                    message: `Navigation to ${path} would be executed (server-side)`
                };
            }
            // Check if internal or external navigation
            if (this.isInternalUrl(url)) {
                // Try client-side navigation first (SPA support)
                if (this.tryClientSideNavigation(url)) {
                    return {
                        success: true,
                        message: `Navigated to ${path}`
                    };
                }
                // Fallback to full page navigation
                window.location.href = url;
                return {
                    success: true,
                    message: `Navigating to ${path}...`
                };
            }
            // External URL - open in new tab
            window.open(url, '_blank', 'noopener,noreferrer');
            return {
                success: true,
                message: `Opened ${url} in new tab`
            };
        }
        catch (error) {
            return {
                success: false,
                message: 'Navigation failed',
                error: String(error)
            };
        }
    }
    /**
     * Fill a form field
     */
    async fillForm(selector, value) {
        // Check selector is allowed
        if (!this.isSelectorAllowed(selector)) {
            return {
                success: false,
                message: `Selector "${selector}" is not allowed`
            };
        }
        // Sanitize selector
        const sanitizedSelector = this.sanitizeSelector(selector);
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            return {
                success: true,
                message: `Form fill would set "${sanitizedSelector}" to "${value}" (server-side)`
            };
        }
        try {
            const element = document.querySelector(sanitizedSelector);
            if (!element) {
                return {
                    success: false,
                    message: `Element not found: ${sanitizedSelector}`
                };
            }
            if (!(element instanceof HTMLInputElement ||
                element instanceof HTMLTextAreaElement ||
                element instanceof HTMLSelectElement)) {
                return {
                    success: false,
                    message: 'Element is not a form field'
                };
            }
            // Focus the element
            element.focus();
            // Set value
            if (element instanceof HTMLSelectElement) {
                // For select, find matching option
                const option = Array.from(element.options).find(opt => opt.value.toLowerCase() === value.toLowerCase() ||
                    opt.text.toLowerCase() === value.toLowerCase());
                if (option) {
                    element.value = option.value;
                }
                else {
                    return {
                        success: false,
                        message: `Option "${value}" not found`
                    };
                }
            }
            else {
                element.value = value;
            }
            // Dispatch events to trigger frameworks
            element.dispatchEvent(new Event('input', { bubbles: true }));
            element.dispatchEvent(new Event('change', { bubbles: true }));
            // For React - try to set value natively
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
            if (nativeInputValueSetter && element instanceof HTMLInputElement) {
                nativeInputValueSetter.call(element, value);
                element.dispatchEvent(new Event('input', { bubbles: true }));
            }
            return {
                success: true,
                message: `Filled field with "${value}"`
            };
        }
        catch (error) {
            return {
                success: false,
                message: 'Failed to fill form field',
                error: String(error)
            };
        }
    }
    /**
     * Click an element
     */
    async clickElement(selector) {
        // Check selector is allowed
        if (!this.isSelectorAllowed(selector)) {
            return {
                success: false,
                message: `Selector "${selector}" is not allowed`
            };
        }
        // Sanitize selector
        const sanitizedSelector = this.sanitizeSelector(selector);
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            return {
                success: true,
                message: `Click on "${sanitizedSelector}" would be executed (server-side)`
            };
        }
        try {
            const element = document.querySelector(sanitizedSelector);
            if (!element) {
                return {
                    success: false,
                    message: `Element not found: ${sanitizedSelector}`
                };
            }
            if (!(element instanceof HTMLElement)) {
                return {
                    success: false,
                    message: 'Element is not clickable'
                };
            }
            // Scroll into view
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Brief delay for scroll
            await this.sleep(300);
            // Click
            element.click();
            return {
                success: true,
                message: 'Element clicked'
            };
        }
        catch (error) {
            return {
                success: false,
                message: 'Failed to click element',
                error: String(error)
            };
        }
    }
    /**
     * Trigger a modal by clicking its trigger element
     */
    async triggerModal(triggerSelector) {
        return this.clickElement(triggerSelector);
    }
    /**
     * Scroll to an element
     */
    async scrollToElement(selector) {
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            return {
                success: true,
                message: `Scroll to "${selector}" would be executed (server-side)`
            };
        }
        try {
            // Special case for top/bottom
            if (selector === 'body') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return { success: true, message: 'Scrolled to top' };
            }
            if (selector === 'body:last-child') {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                return { success: true, message: 'Scrolled to bottom' };
            }
            const element = document.querySelector(selector);
            if (!element) {
                return {
                    success: false,
                    message: `Element not found: ${selector}`
                };
            }
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return {
                success: true,
                message: 'Scrolled to element'
            };
        }
        catch (error) {
            return {
                success: false,
                message: 'Failed to scroll',
                error: String(error)
            };
        }
    }
    /**
     * Highlight an element with animation
     */
    async highlightElement(selector) {
        // Check selector is allowed
        if (!this.isSelectorAllowed(selector)) {
            return {
                success: false,
                message: `Selector "${selector}" is not allowed`
            };
        }
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            return {
                success: true,
                message: `Highlight on "${selector}" would be shown (server-side)`
            };
        }
        try {
            const element = document.querySelector(selector);
            if (!element) {
                return {
                    success: false,
                    message: `Element not found: ${selector}`
                };
            }
            // Remove existing highlight
            this.removeHighlight();
            // Scroll element into view
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Brief delay for scroll
            await this.sleep(300);
            // Create highlight overlay
            const rect = element.getBoundingClientRect();
            const overlay = document.createElement('div');
            overlay.className = 'astermind-highlight-overlay';
            overlay.style.cssText = `
                position: fixed;
                top: ${rect.top - 4}px;
                left: ${rect.left - 4}px;
                width: ${rect.width + 8}px;
                height: ${rect.height + 8}px;
                border: 3px solid #4F46E5;
                border-radius: 4px;
                pointer-events: none;
                z-index: 999999;
                animation: astermind-highlight-pulse 1s ease-in-out 3;
            `;
            document.body.appendChild(overlay);
            this.highlightOverlay = overlay;
            // Remove after animation
            setTimeout(() => this.removeHighlight(), 3000);
            return {
                success: true,
                message: 'Element highlighted'
            };
        }
        catch (error) {
            return {
                success: false,
                message: 'Failed to highlight element',
                error: String(error)
            };
        }
    }
    /**
     * Execute a custom action callback
     */
    async executeCustomAction(actionName, params) {
        // Validate custom action is in whitelist
        if (!this.isAllowedCustomAction(actionName)) {
            return {
                success: false,
                message: `Custom action "${actionName}" not found`
            };
        }
        const callback = this.config.customActions[actionName];
        try {
            return await callback(params);
        }
        catch (error) {
            return {
                success: false,
                message: `Custom action "${actionName}" failed`,
                error: String(error)
            };
        }
    }
    // ==================== HELPER METHODS ====================
    /**
     * Check if URL is internal to the current site
     */
    isInternalUrl(url) {
        // Relative URLs are internal
        if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
            return true;
        }
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            return true;
        }
        // Check if same origin
        try {
            const urlObj = new URL(url, window.location.href);
            return urlObj.origin === window.location.origin;
        }
        catch {
            return true; // Assume internal on error
        }
    }
    /**
     * Try client-side navigation for SPAs
     */
    tryClientSideNavigation(url) {
        if (typeof window === 'undefined') {
            return false;
        }
        // Try History API pushState
        try {
            window.history.pushState({}, '', url);
            window.dispatchEvent(new PopStateEvent('popstate'));
            // Also dispatch hashchange for hash routing
            if (url.includes('#')) {
                window.dispatchEvent(new HashChangeEvent('hashchange'));
            }
            return true;
        }
        catch {
            return false;
        }
    }
    /**
     * Validate navigation URL
     */
    validateNavigationUrl(url) {
        // Reject javascript: and data: URLs
        if (/^(javascript|data):/i.test(url)) {
            return false;
        }
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            return true;
        }
        // Only allow relative URLs or same-origin
        try {
            const urlObj = new URL(url, window.location.href);
            return urlObj.origin === window.location.origin || url.startsWith('/');
        }
        catch {
            return url.startsWith('/');
        }
    }
    /**
     * Sanitize CSS selector to prevent injection
     */
    sanitizeSelector(selector) {
        // Remove potentially dangerous characters
        return selector
            .replace(/[<>'"]/g, '')
            .replace(/javascript:/gi, '')
            .replace(/data:/gi, '')
            .trim();
    }
    /**
     * Validate custom action is in whitelist
     */
    isAllowedCustomAction(actionName) {
        return this.config.customActions !== undefined &&
            Object.prototype.hasOwnProperty.call(this.config.customActions, actionName);
    }
    /**
     * Remove highlight overlay
     */
    removeHighlight() {
        if (this.highlightOverlay) {
            this.highlightOverlay.remove();
            this.highlightOverlay = null;
        }
    }
    /**
     * Inject required styles
     */
    injectStyles() {
        if (typeof document === 'undefined') {
            return;
        }
        if (document.getElementById('astermind-agent-styles')) {
            return;
        }
        const style = document.createElement('style');
        style.id = 'astermind-agent-styles';
        style.textContent = `
            @keyframes astermind-highlight-pulse {
                0%, 100% {
                    box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4);
                }
                50% {
                    box-shadow: 0 0 0 10px rgba(79, 70, 229, 0);
                }
            }

            .astermind-highlight-overlay {
                transition: all 0.2s ease-out;
            }
        `;
        document.head.appendChild(style);
    }
    /**
     * Sleep utility
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * Get agent configuration
     */
    getConfig() {
        return this.config;
    }
    /**
     * Get the intent classifier
     */
    getClassifier() {
        return this.classifier;
    }
    /**
     * Reset action count (for testing)
     */
    resetActionCount() {
        this.actionCount = 0;
        this.lastActionReset = Date.now();
    }
}

// src/agentic/register.ts
// Helper for registering agentic capabilities with CyberneticClient
/**
 * Register agentic capabilities with a CyberneticClient
 *
 * This function is the bridge between core and agentic modules.
 * It must be called after creating the client to enable agentic features.
 *
 * @example
 * ```typescript
 * import { CyberneticClient, registerAgenticCapabilities } from '@astermind/cybernetic-chatbot-client';
 *
 * const client = new CyberneticClient({
 *     apiUrl: 'https://api.example.com',
 *     apiKey: 'am_xxx_123',
 *     agentic: {
 *         enabled: true,
 *         allowedActions: ['click', 'fill', 'scroll'],
 *         confirmActions: true
 *     }
 * });
 *
 * // Register agentic capabilities
 * registerAgenticCapabilities(client);
 *
 * // Now client supports agentic responses
 * const response = await client.ask('Click the Add to Cart button');
 * ```
 *
 * @param client - The CyberneticClient instance to register capabilities with
 */
function registerAgenticCapabilities(client) {
    const capabilities = {
        agent: CyberneticAgent,
        intentClassifier: CyberneticIntentClassifier
    };
    client.registerAgentic(capabilities);
}

// src/hooks/useSiteMapDiscovery.ts
// React hook for sitemap discovery integration
// Note: React is a peer dependency - this hook is optional for non-React consumers
/**
 * Hook for discovering routes from React Router and other frameworks
 *
 * @example
 * ```tsx
 * function App() {
 *   const { entries, isDiscovering } = useSiteMapDiscovery({
 *     enabled: true,
 *     excludePaths: ['/admin/*', '/internal/*']
 *   });
 *
 *   return (
 *     <ChatbotWidget
 *       agent={{
 *         enabled: true,
 *         siteMap: entries
 *       }}
 *     />
 *   );
 * }
 * ```
 */
function useSiteMapDiscovery(options = {}) {
    const { enabled = true, transformRoute: _transformRoute, excludePaths: _excludePaths = [], navSelectors: _navSelectors, disableDomFallback = false } = options;
    const [state, setState] = useState({
        entries: [],
        isDiscovering: false,
        error: null,
        framework: null
    });
    const discoveredRef = useRef(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    /**
     * Detect JavaScript framework
     */
    const detectFramework = useCallback(() => {
        if (typeof window === 'undefined')
            return 'generic';
        const win = window;
        if (win.__REACT_ROUTER_DATA_ROUTER__)
            return 'react-router';
        if (win.__reactRouterContext)
            return 'react-router';
        if (win.__VUE_ROUTER__ || win.__VUE_APP__?.$router)
            return 'vue-router';
        if (win.__NEXT_DATA__)
            return 'next';
        if (win.ng || document.querySelector('[ng-version]'))
            return 'angular';
        return 'generic';
    }, []);
    /**
     * Check if path should be excluded
     */
    const shouldExclude = useCallback((path) => {
        const patterns = optionsRef.current.excludePaths || [];
        for (const pattern of patterns) {
            const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
            if (regex.test(path))
                return true;
        }
        return false;
    }, []);
    /**
     * Convert path to human-readable name
     */
    const pathToName = useCallback((path) => {
        const segments = path.split('/').filter(Boolean);
        const last = segments[segments.length - 1] || 'Home';
        return last
            .replace(/[-_:]/g, ' ')
            .replace(/\[.*?\]/g, '')
            .split(' ')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ')
            .trim() || 'Page';
    }, []);
    /**
     * Default route transform
     */
    const defaultTransform = useCallback((route) => {
        if (!route.path || route.path === '*' || route.path === '**')
            return null;
        return {
            path: route.path,
            name: route.name || route.meta?.title || pathToName(route.path),
            description: route.meta?.description,
            aliases: route.meta?.keywords || route.meta?.aliases,
            dynamicParams: route.path.includes(':') || route.path.includes('[')
        };
    }, [pathToName]);
    /**
     * Extract routes from React Router config
     */
    const extractReactRouterRoutes = useCallback((routeConfigs, routes, parentPath) => {
        for (const config of routeConfigs) {
            let path = config.path || '';
            if (path && !path.startsWith('/')) {
                path = parentPath ? `${parentPath}/${path}`.replace(/\/+/g, '/') : `/${path}`;
            }
            else if (!path && parentPath) {
                path = parentPath;
            }
            const route = {
                path,
                name: config.handle?.name || config.handle?.title || config.id,
                meta: config.handle || {}
            };
            if (config.path && !shouldExclude(path)) {
                routes.push(route);
            }
            if (config.children) {
                extractReactRouterRoutes(config.children, routes, path);
            }
        }
    }, [shouldExclude]);
    /**
     * Discover routes from DOM navigation
     */
    const discoverFromDOM = useCallback(() => {
        if (typeof document === 'undefined')
            return [];
        const defaultSelectors = [
            'nav a[href]',
            '[role="navigation"] a[href]',
            '.nav a[href]',
            '.sidebar a[href]',
            '.menu a[href]',
            'header a[href]'
        ];
        const selectors = optionsRef.current.navSelectors || defaultSelectors;
        const routes = [];
        const seen = new Set();
        for (const selector of selectors) {
            try {
                const links = document.querySelectorAll(selector);
                for (const link of links) {
                    const href = link.getAttribute('href');
                    if (!href || href.startsWith('http') || href.startsWith('javascript:'))
                        continue;
                    const path = href.startsWith('/') ? href : `/${href}`;
                    const cleanPath = path.split('?')[0].split('#')[0];
                    if (seen.has(cleanPath) || cleanPath === '/' || shouldExclude(cleanPath))
                        continue;
                    seen.add(cleanPath);
                    routes.push({
                        path: cleanPath,
                        name: link.textContent?.trim() || pathToName(cleanPath)
                    });
                }
            }
            catch (e) {
                // Invalid selector
            }
        }
        return routes;
    }, [shouldExclude, pathToName]);
    /**
     * Main discovery function
     */
    const discoverRoutes = useCallback(() => {
        if (!enabled || typeof window === 'undefined') {
            return;
        }
        setState((prev) => ({ ...prev, isDiscovering: true, error: null }));
        try {
            const framework = detectFramework();
            const routes = [];
            const win = window;
            // Try framework-specific discovery
            if (framework === 'react-router' && win.__REACT_ROUTER_DATA_ROUTER__?.routes) {
                extractReactRouterRoutes(win.__REACT_ROUTER_DATA_ROUTER__.routes, routes, '');
            }
            else if (framework === 'vue-router') {
                const router = win.__VUE_APP__?.$router || win.__VUE_ROUTER__;
                if (router?.getRoutes) {
                    for (const vueRoute of router.getRoutes()) {
                        if (!shouldExclude(vueRoute.path)) {
                            routes.push({
                                path: vueRoute.path,
                                name: vueRoute.name?.toString(),
                                meta: vueRoute.meta || {}
                            });
                        }
                    }
                }
            }
            else if (framework === 'next' && win.__NEXT_DATA__?.buildManifest?.pages) {
                for (const pagePath of Object.keys(win.__NEXT_DATA__.buildManifest.pages)) {
                    if (!pagePath.startsWith('/_') && !shouldExclude(pagePath)) {
                        routes.push({
                            path: pagePath,
                            name: pathToName(pagePath)
                        });
                    }
                }
            }
            // DOM fallback if no routes found and not disabled
            if (routes.length === 0 && !disableDomFallback) {
                routes.push(...discoverFromDOM());
            }
            // Transform routes to sitemap entries
            const transform = optionsRef.current.transformRoute || defaultTransform;
            const entries = [];
            for (const route of routes) {
                const entry = transform(route);
                if (entry) {
                    entries.push(entry);
                }
            }
            setState({
                entries,
                isDiscovering: false,
                error: null,
                framework
            });
            discoveredRef.current = true;
            console.log(`[useSiteMapDiscovery] Discovered ${entries.length} routes from ${framework}`);
        }
        catch (error) {
            console.warn('[useSiteMapDiscovery] Discovery failed:', error);
            setState((prev) => ({
                ...prev,
                isDiscovering: false,
                error: String(error)
            }));
        }
    }, [
        enabled,
        detectFramework,
        extractReactRouterRoutes,
        discoverFromDOM,
        defaultTransform,
        shouldExclude,
        pathToName,
        disableDomFallback
    ]);
    // Initial discovery with delay to ensure router is mounted
    useEffect(() => {
        if (!enabled || discoveredRef.current)
            return;
        const timer = setTimeout(discoverRoutes, 100);
        return () => clearTimeout(timer);
    }, [enabled, discoverRoutes]);
    // Re-discover on location changes (for SPAs)
    useEffect(() => {
        if (!enabled || typeof window === 'undefined')
            return;
        const handlePopState = () => {
            // Re-run discovery after navigation (routes might have changed)
            setTimeout(discoverRoutes, 50);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [enabled, discoverRoutes]);
    return {
        ...state,
        refresh: discoverRoutes
    };
}
/**
 * Create a FrameworkDiscoveryConfig from hook options
 * Useful for passing to CyberneticClient
 */
function createDiscoveryConfig(options) {
    return {
        enabled: options.enabled ?? true,
        domFallback: !options.disableDomFallback,
        navSelectors: options.navSelectors,
        excludePaths: options.excludePaths,
        transformRoute: options.transformRoute
    };
}

// © 2026 AsterMind AI Co. – All Rights Reserved.
// Patent Pending US 63/897,713
// ELMConfig.ts - Configuration interfaces, defaults, helpers for ELM-based models
/* =========== Defaults =========== */
const defaultBase = {
    hiddenUnits: 50,
    activation: 'relu',
    ridgeLambda: 1e-2,
    weightInit: 'xavier',
    seed: 1337,
    dropout: 0,
    log: { verbose: true, toFile: false, modelName: 'Unnamed ELM Model', level: 'info' },
};
Object.assign(Object.assign({}, defaultBase), { useTokenizer: false });
Object.assign(Object.assign({}, defaultBase), { useTokenizer: true, maxLen: 30, charSet: 'abcdefghijklmnopqrstuvwxyz', tokenizerDelimiter: /\s+/ });

function buildRFF(d, D, sigma = 1.0, rng = Math.random) {
    const W = new Float64Array(D * d);
    const b = new Float64Array(D);
    const s = 1 / Math.max(1e-12, sigma); // N(0, 1/sigma^2)
    for (let i = 0; i < D * d; i++)
        W[i] = gauss$1(rng) * s;
    for (let i = 0; i < D; i++)
        b[i] = rng() * 2 * Math.PI;
    return { W, b, D, d, sigma };
}
function mapRFF(rff, x) {
    const { W, b, D, d } = rff;
    const z = new Float64Array(2 * D);
    for (let k = 0; k < D; k++) {
        let dot = b[k];
        const off = k * d;
        for (let j = 0; j < d; j++)
            dot += W[off + j] * (x[j] || 0);
        z[k] = Math.cos(dot);
        z[D + k] = Math.sin(dot);
    }
    // L2 normalize block to keep ridge well-conditioned
    let s = 0;
    for (let i = 0; i < z.length; i++)
        s += z[i] * z[i];
    const inv = 1 / Math.sqrt(Math.max(s, 1e-12));
    for (let i = 0; i < z.length; i++)
        z[i] *= inv;
    return z;
}
// Box-Muller
function gauss$1(rng) {
    let u = 0, v = 0;
    while (u === 0)
        u = rng();
    while (v === 0)
        v = rng();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

// online_ridge.ts — maintain (Φ^T Φ + λI)^{-1} and β for linear ridge
class OnlineRidge {
    constructor(p, m, lambda = 1e-4) {
        this.p = p;
        this.m = m;
        this.lambda = lambda;
        this.Ainv = new Float64Array(p * p);
        this.Beta = new Float64Array(p * m);
        // Ainv = (λ I)^-1 = (1/λ) I
        const inv = 1 / Math.max(1e-12, lambda);
        for (let i = 0; i < p; i++)
            this.Ainv[i * p + i] = inv;
    }
    // rank-1 update with a single sample (φ, y)
    update(phi, y) {
        const { p, m, Ainv, Beta } = this;
        // u = Ainv * phi
        const u = new Float64Array(p);
        for (let i = 0; i < p; i++) {
            let s = 0, row = i * p;
            for (let j = 0; j < p; j++)
                s += Ainv[row + j] * phi[j];
            u[i] = s;
        }
        // denom = 1 + phi^T u
        let denom = 1;
        for (let j = 0; j < p; j++)
            denom += phi[j] * u[j];
        denom = Math.max(denom, 1e-12);
        const scale = 1 / denom;
        // Ainv <- Ainv - (u u^T) * scale
        for (let i = 0; i < p; i++) {
            const ui = u[i] * scale;
            for (let j = 0; j < p; j++)
                Ainv[i * p + j] -= ui * u[j];
        }
        // Beta <- Beta + Ainv * (phi * y^T)
        // compute t = Ainv * phi  (reuse u after Ainv update)
        for (let i = 0; i < p; i++) {
            let s = 0, row = i * p;
            for (let j = 0; j < p; j++)
                s += Ainv[row + j] * phi[j];
            u[i] = s; // reuse u as t
        }
        // Beta += outer(u, y)
        for (let i = 0; i < p; i++) {
            const ui = u[i];
            for (let c = 0; c < m; c++)
                Beta[i * m + c] += ui * y[c];
        }
    }
    // yhat = φ^T Beta
    predict(phi) {
        const { p, m, Beta } = this;
        const out = new Float64Array(m);
        for (let c = 0; c < m; c++) {
            let s = 0;
            for (let i = 0; i < p; i++)
                s += phi[i] * Beta[i * m + c];
            out[c] = s;
        }
        return out;
    }
}

// src/math/index.ts — production-grade numerics for Ω
// Backward compatible with previous exports; adds robust, stable helpers.
// ---------- Constants
const EPS = 1e-12; // general epsilon for divides/sqrt
// ---------- Norms / normalization
function l2$1(a) {
    // robust L2 (avoids NaN on weird input)
    let s = 0;
    for (let i = 0; i < a.length; i++)
        s += a[i] * a[i];
    return Math.sqrt(Math.max(0, s));
}
function normalizeL2(a, eps = EPS) {
    const nrm = l2$1(a);
    if (!(nrm > eps) || !Number.isFinite(nrm))
        return new Float64Array(a.length); // zero vec
    const o = new Float64Array(a.length);
    const inv = 1 / nrm;
    for (let i = 0; i < a.length; i++)
        o[i] = a[i] * inv;
    return o;
}
// ---------- Cosine (robust)
function cosine$2(a, b) {
    var _a, _b;
    const n = Math.min(a.length, b.length);
    if (n === 0)
        return 0;
    let dotv = 0, na = 0, nb = 0;
    for (let i = 0; i < n; i++) {
        const ai = ((_a = a[i]) !== null && _a !== void 0 ? _a : 0), bi = ((_b = b[i]) !== null && _b !== void 0 ? _b : 0);
        dotv += ai * bi;
        na += ai * ai;
        nb += bi * bi;
    }
    const denom = Math.sqrt(Math.max(na * nb, EPS));
    const v = dotv / denom;
    return Number.isFinite(v) ? v : 0;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

// Omega.ts v2 — improved local reasoning + summarization
// uses your math.ts, rff.ts, online_ridge.ts
// -------- sentence + text helpers ----------
function splitSentences$1(text) {
    return text
        .replace(/\s+/g, " ")
        .split(/(?<=[.?!])\s+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 8 && /\w/.test(s));
}
function clean(text) {
    return text
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`[^`]+`/g, " ")
        .replace(/\[[^\]]*\]\([^)]*\)/g, "") // strip markdown links
        .replace(/[-–>•→]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}
function isMetaSentence(s) {
    // simple heuristics for table-of-contents or chapter headings
    return (/^(\*|#)/.test(s) || // markdown markers
        /chapter/i.test(s) || // "Chapter 11", "Chapters 11–15"
        /part\s*\d+/i.test(s) || // "Part 3"
        /section/i.test(s) || // "Section 2.3"
        /^\s*[A-Z]\)\s*$/.test(s) || // single-letter outlines
        s.length < 15 // very short stray lines
    );
}
function rewrite(summary) {
    return summary
        .replace(/\s+[-–>•→]\s+/g, " ")
        .replace(/\s+\.\s+/g, ". ")
        .replace(/([a-z]) - ([a-z])/gi, "$1-$2")
        .replace(/\s{2,}/g, " ")
        .trim();
}
// ------------------------------------------------------------
function omegaComposeAnswer(question_1, items_1) {
    return __awaiter(this, arguments, void 0, function* (question, items, opts = {}) {
        // License check removed // Premium feature - requires valid license
        if (!(items === null || items === void 0 ? void 0 : items.length))
            return "No results found.";
        const { dim = 64, features = 32, sigma = 1.0, rounds = 3, topSentences = 8, personality = "neutral", } = opts;
        // ---------- 1. Clean + collect sentences ----------
        const allText = items.map((i) => clean(i.content)).join(" ");
        let sentences = splitSentences$1(allText)
            .filter(s => !isMetaSentence(s))
            .slice(0, 120);
        if (sentences.length === 0)
            return clean(items[0].content).slice(0, 400);
        // ---------- 2. Build encoder + ridge ----------
        const rff = buildRFF(dim, features, sigma);
        const ridge = new OnlineRidge(2 * features, 1, 1e-3);
        const encode = (s) => {
            const vec = new Float64Array(dim);
            const len = Math.min(s.length, dim);
            for (let i = 0; i < len; i++)
                vec[i] = s.charCodeAt(i) / 255;
            return mapRFF(rff, normalizeL2(vec));
        };
        const qVec = encode(question);
        const qTokens = question.toLowerCase().split(/\W+/).filter((t) => t.length > 2);
        // ---------- 3. Score + select top sentences ----------
        const scored = sentences.map((s) => {
            const v = encode(s);
            let w = cosine$2(v, qVec);
            // small lexical bonus for overlapping words
            const lower = s.toLowerCase();
            for (const t of qTokens)
                if (lower.includes(t))
                    w += 0.02;
            return { s, v, w };
        });
        scored.sort((a, b) => b.w - a.w);
        let top = scored.slice(0, topSentences);
        // ---------- 4. Recursive compression ----------
        let summary = top.map((t) => t.s).join(" ");
        let meanVec = new Float64Array(2 * features);
        for (let r = 0; r < rounds; r++) {
            const subs = splitSentences$1(summary).slice(0, topSentences);
            const embeds = subs.map((s) => encode(s));
            const weights = embeds.map((v) => cosine$2(v, qVec));
            for (let i = 0; i < embeds.length; i++) {
                ridge.update(embeds[i], new Float64Array([weights[i]]));
            }
            // weighted mean vector
            meanVec.fill(0);
            for (let i = 0; i < embeds.length; i++) {
                const v = embeds[i], w = weights[i];
                for (let j = 0; j < v.length; j++)
                    meanVec[j] += v[j] * w;
            }
            const norm = l2$1(meanVec) || 1;
            for (let j = 0; j < meanVec.length; j++)
                meanVec[j] /= norm;
            const rescored = subs.map((s) => ({
                s,
                w: cosine$2(encode(s), meanVec),
            }));
            rescored.sort((a, b) => b.w - a.w);
            summary = rescored
                .slice(0, Math.max(3, Math.floor(topSentences / 2)))
                .map((r) => r.s)
                .join(" ");
        }
        // ---------- 5. Compose readable answer ----------
        summary = rewrite(summary);
        const firstChar = summary.charAt(0).toUpperCase() + summary.slice(1);
        const title = items[0].heading || "Answer";
        const prefix = personality === "teacher"
            ? "Here’s a simple way to think about it:\n\n"
            : personality === "scientist"
                ? "From the retrieved material, we can infer:\n\n"
                : "";
        return `${prefix}${firstChar}\n\n(${title}, Ω-synthesized)`;
    });
}

// Vectorization utilities for sparse and dense vectors
// Extracted from workers for reuse
/**
 * Compute TF-IDF vector from tokens
 */
function toTfidf(tokens, idf, vmap, headingW = 1) {
    const counts = new Map();
    // crude heuristic: first 8 tokens considered heading-weighted
    for (let i = 0; i < tokens.length; i++) {
        const t = tokens[i];
        const id = vmap.get(t);
        if (id === undefined)
            continue;
        const w = (i < 8) ? headingW : 1;
        counts.set(id, (counts.get(id) || 0) + w);
    }
    const maxTf = Math.max(1, ...counts.values());
    const v = new Map();
    for (const [i, c] of counts) {
        const tf = 0.5 + 0.5 * (c / maxTf);
        v.set(i, tf * (idf[i] || 0));
    }
    return v;
}
/**
 * Cosine similarity between two sparse vectors
 */
function cosineSparse(a, b) {
    let dot = 0, na = 0, nb = 0;
    for (const [i, av] of a) {
        na += av * av;
        const bv = b.get(i);
        if (bv)
            dot += av * bv;
    }
    for (const [, bv] of b)
        nb += bv * bv;
    if (!na || !nb)
        return 0;
    return dot / (Math.sqrt(na) * Math.sqrt(nb));
}
/**
 * Convert sparse vector to dense Float64Array
 */
function sparseToDense(v, dim) {
    const x = new Float64Array(dim);
    for (const [i, val] of v)
        x[i] = val;
    return x;
}
/**
 * Dot product of two dense vectors
 */
function dotProd$1(a, b) {
    let s = 0;
    for (let i = 0; i < a.length; i++)
        s += a[i] * b[i];
    return s;
}
/**
 * Base kernel function (RBF, cosine, or poly2)
 */
function baseKernel$1(a, b, k, sigma) {
    if (k === 'cosine') {
        const dot = dotProd$1(a, b), na = Math.hypot(...a), nb = Math.hypot(...b);
        return (na && nb) ? (dot / (na * nb)) : 0;
    }
    else if (k === 'poly2') {
        const dot = dotProd$1(a, b);
        return Math.pow((dot + 1), 2);
    }
    else {
        let s = 0;
        for (let i = 0; i < a.length; i++) {
            const d = a[i] - b[i];
            s += d * d;
        }
        return Math.exp(-s / Math.max(1e-9, 2 * sigma * sigma));
    }
}
/**
 * Kernel similarity between two dense vectors
 */
function kernelSim(a, b, k, sigma) {
    if (k === 'cosine') {
        const dot = dotProd$1(a, b), na = Math.hypot(...a), nb = Math.hypot(...b);
        return (na && nb) ? (dot / (na * nb)) : 0;
    }
    else if (k === 'poly2') {
        const dot = dotProd$1(a, b);
        return Math.pow((dot + 1), 2);
    }
    else {
        let s = 0;
        for (let i = 0; i < a.length; i++) {
            const d = a[i] - b[i];
            s += d * d;
        }
        return Math.exp(-s / Math.max(1e-9, 2 * sigma * sigma));
    }
}
/**
 * Project sparse vector to dense using Nyström landmarks
 */
function projectToDense(v, vocabSize, landmarkMat, kernel, sigma) {
    const x = sparseToDense(v, vocabSize);
    const feats = new Float64Array(landmarkMat.length);
    for (let j = 0; j < landmarkMat.length; j++) {
        const l = landmarkMat[j];
        feats[j] = baseKernel$1(x, l, kernel, sigma);
    }
    const n = Math.hypot(...feats);
    if (n > 0)
        for (let i = 0; i < feats.length; i++)
            feats[i] /= n;
    return feats;
}

// Tokenization and stemming utilities
// Extracted from workers for reuse
// Memo for speed
const STEM_CACHE = new Map();
function normalizeWord(raw) {
    const k = raw;
    const cached = STEM_CACHE.get(k);
    if (cached)
        return cached;
    let w = raw.toLowerCase();
    w = w.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
    if (w.length <= 2) {
        STEM_CACHE.set(k, w);
        return w;
    }
    // plural → singular
    if (w.endsWith('ies') && w.length > 4) {
        w = w.slice(0, -3) + 'y';
    }
    else if (/(xes|ches|shes|zes|sses)$/.test(w) && w.length > 4) {
        w = w.replace(/(xes|ches|shes|zes|sses)$/, (m) => (m === 'sses' ? 'ss' : m.replace(/es$/, '')));
    }
    else if (w.endsWith('s') && !/(ss|us)$/.test(w) && w.length > 3) {
        w = w.slice(0, -1);
    }
    // conservative suffix trimming
    const rules = [
        [/ization$|isation$/, 'ize'],
        [/ational$/, 'ate'],
        [/fulness$/, 'ful'],
        [/ousness$/, 'ous'],
        [/iveness$/, 'ive'],
        [/ability$/, 'able'],
        [/ness$/, ''],
        [/ment$/, ''],
        [/ations?$/, 'ate'],
        [/izer$|iser$/, 'ize'],
        [/ally$/, 'al'],
        [/ically$/, 'ic'],
        [/ingly$|edly$/, ''],
        [/ing$|ed$/, ''],
    ];
    for (const [re, rep] of rules) {
        if (re.test(w) && w.length - rep.length >= 4) {
            w = w.replace(re, rep);
            break;
        }
    }
    STEM_CACHE.set(k, w);
    return w;
}
function tokenize$1(text, doStem) {
    const base = text.toLowerCase()
        .replace(/[`*_>~]/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .split(/\s+/)
        .filter(Boolean);
    if (!doStem)
        return base;
    const out = [];
    for (const t of base) {
        const n = normalizeWord(t);
        if (n && n.length > 1)
            out.push(n);
    }
    return out;
}
function expandQuery(q) {
    const adds = [];
    if (/\bmap\b/.test(q))
        adds.push('dict key value make');
    if (/\bchan|channel\b/.test(q))
        adds.push('goroutine concurrency select buffer');
    if (/\berror\b/.test(q))
        adds.push('fmt wrap unwrap sentinel try catch');
    if (/\bstruct\b/.test(q))
        adds.push('field method receiver init zero value');
    return q + ' ' + adds.join(' ');
}

// Hybrid retrieval system (sparse + dense + keyword bonus)
// Extracted from workers for reuse
// License removed - all features are now free!
/**
 * Compute keyword bonus scores for chunks
 */
function keywordBonus(chunks, query) {
    const kws = Array.from(new Set(query.toLowerCase().split(/\W+/).filter(t => t.length > 2)));
    const syntaxBoost = /\b(define|declare|syntax|example|function|struct|map|interface)\b/i.test(query);
    return chunks.map(c => {
        const text = c.rich || c.content || '';
        const lc = text.toLowerCase();
        let hit = 0;
        for (const k of kws)
            if (lc.includes(k))
                hit++;
        if (syntaxBoost && /```/.test(text))
            hit += 5; // strong bonus for code presence
        return Math.min(1.0, hit * 0.03);
    });
}
/**
 * Get top K indices from scores
 */
function topKIndices(arr, k) {
    const idx = Array.from(arr, (_, i) => i);
    idx.sort((i, j) => (arr[j] - arr[i]));
    return idx.slice(0, k);
}
/**
 * Clamp value between min and max
 */
function clamp$1(x, a, b) {
    return Math.max(a, Math.min(b, x));
}
/**
 * Perform hybrid retrieval (sparse + dense + keyword bonus)
 */
function hybridRetrieve(opts) {
    // License check removed // Premium feature - requires valid license
    const { query, chunks, vocabMap, idf, tfidfDocs, denseDocs, landmarksIdx, landmarkMat, vocabSize, kernel, sigma, alpha, beta, ridge, headingW, useStem, expandQuery: shouldExpand, topK: k, prefilter, } = opts;
    // Expand query if needed
    const qexp = shouldExpand ? expandQuery(query) : query;
    const toks = tokenize$1(qexp, useStem);
    const qvec = toTfidf(toks, idf, vocabMap, headingW);
    const qdense = projectToDense(qvec, vocabSize, landmarkMat, kernel, sigma);
    // Compute sparse (TF-IDF) scores
    const tfidfScores = tfidfDocs.map(v => cosineSparse(v, qvec));
    // Compute dense (kernel) scores
    const denseScores = denseDocs.map((v) => kernelSim(v, qdense, kernel, sigma));
    // Compute keyword bonus
    const bonus = keywordBonus(chunks, query);
    // Hybrid scoring with ridge regularization
    const alphaClamped = clamp$1(alpha, 0, 1);
    const lambda = ridge !== null && ridge !== void 0 ? ridge : 0.08;
    const scores = denseScores.map((d, i) => {
        const t = tfidfScores[i];
        const b = beta * bonus[i];
        // Ridge damping on ALL components (dense, tfidf, and keyword bonus)
        const reg = 1 / (1 + lambda * (d * d + t * t + 0.5 * b * b));
        const s = reg * (alphaClamped * d + (1 - alphaClamped) * t + b);
        // soft clip extremes; helps prevent a single noisy dimension from dominating
        return Math.tanh(s);
    });
    // Pre-filter then final topK (retrieval stage)
    const pre = Math.max(k, prefilter !== null && prefilter !== void 0 ? prefilter : 0);
    const idxs = topKIndices(scores, pre);
    const finalIdxs = topKIndices(idxs.map(i => scores[i]), k).map(k => idxs[k]);
    // Build result items
    const items = finalIdxs.map(i => {
        const c = chunks[i];
        const body = (c.rich && c.rich.trim()) || (c.content && c.content.trim()) || '(see subsections)';
        return {
            score: scores[i],
            heading: c.heading,
            content: body,
            index: i,
        };
    });
    return {
        items,
        scores: finalIdxs.map(i => scores[i]),
        indices: finalIdxs,
        tfidfScores: finalIdxs.map(i => tfidfScores[i]),
        denseScores: finalIdxs.map(i => denseScores[i]),
    };
}

// OmegaRR.ts
// Reranker + Reducer for AsterMind docs
// - Extracts rich query–chunk features (sparse text + structural signals)
// - Trains a tiny ridge model on-the-fly with weak supervision (per query)
// - Produces score_rr and p_relevant
// - Filters with threshold + MMR coverage under a character budget
// - (v2) Optionally exposes engineered features (values + names) for TE/diagnostics
/* ====================== Tokenization ======================= */
const STOP$1 = new Set([
    "a", "an", "the", "and", "or", "but", "if", "then", "else", "for", "to", "of", "in", "on", "at", "by", "with",
    "is", "are", "was", "were", "be", "been", "being", "as", "from", "that", "this", "it", "its", "you", "your",
    "i", "we", "they", "he", "she", "them", "his", "her", "our", "us", "do", "does", "did", "done", "not", "no",
    "yes", "can", "could", "should", "would", "may", "might", "into", "about", "over", "under", "between"
]);
function tokenize(s) {
    return s
        .toLowerCase()
        .replace(/[`*_#>~=\[\]{}()!?.:,;'"<>|/\\+-]+/g, " ")
        .split(/\s+/)
        .filter(t => t && !STOP$1.has(t));
}
function unique(arr) { return Array.from(new Set(arr)); }
function buildCorpusStats(docs) {
    const vocab = new Map();
    const tfs = [];
    const docLens = [];
    let nextId = 0;
    for (const d of docs) {
        const toks = tokenize(d);
        docLens.push(toks.length);
        const tf = new Map();
        for (const w of toks) {
            let id = vocab.get(w);
            if (id === undefined) {
                id = nextId++;
                vocab.set(w, id);
            }
            tf.set(id, (tf.get(id) || 0) + 1);
        }
        tfs.push(tf);
    }
    const N = docs.length;
    const df = Array(nextId).fill(0);
    for (const tf of tfs)
        for (const id of tf.keys())
            df[id] += 1;
    const idf = df.map(df_i => Math.log((N + 1) / (df_i + 1)) + 1);
    const avgLen = docLens.reduce((a, b) => a + b, 0) / Math.max(1, N);
    return { stats: { vocab, idf, avgLen, df }, tf: tfs, docLens };
}
function tfidfVector(tf, idf) {
    const out = new Map();
    let norm2 = 0;
    for (const [i, f] of tf) {
        const val = (f) * (idf[i] || 0);
        out.set(i, val);
        norm2 += val * val;
    }
    const norm = Math.sqrt(norm2) || 1e-12;
    for (const [i, v] of out)
        out.set(i, v / norm);
    return out;
}
function cosine$1(a, b) {
    const [small, large] = a.size < b.size ? [a, b] : [b, a];
    let dot = 0;
    for (const [i, v] of small) {
        const u = large.get(i);
        if (u !== undefined)
            dot += v * u;
    }
    return dot;
}
function bm25Score(qTf, dTf, stats, dLen, k1 = 1.5, b = 0.75) {
    let score = 0;
    for (const [i] of qTf) {
        const f = dTf.get(i) || 0;
        if (f <= 0)
            continue;
        const idf = Math.log(((stats.df[i] || 0) + 0.5) / ((stats.idf.length - (stats.df[i] || 0)) + 0.5) + 1);
        const denom = f + k1 * (1 - b + b * (dLen / (stats.avgLen || 1)));
        score += idf * ((f * (k1 + 1)) / (denom || 1e-12));
    }
    return score;
}
/* ========== Light Random Projection from TF-IDF (dense hint) ========== */
function projectSparse(vec, dim, seed = 1337) {
    // deterministic per (feature, j) hash: simple LCG/xorshift mix
    const out = new Float64Array(dim);
    for (const [i, v] of vec) {
        let s = (i * 2654435761) >>> 0;
        for (let j = 0; j < dim; j++) {
            s ^= s << 13;
            s ^= s >>> 17;
            s ^= s << 5;
            const r = ((s >>> 0) / 4294967296) * 2 - 1; // [-1,1]
            out[j] += v * r;
        }
    }
    let n2 = 0;
    for (let j = 0; j < dim; j++)
        n2 += out[j] * out[j];
    const n = Math.sqrt(n2) || 1e-12;
    for (let j = 0; j < dim; j++)
        out[j] /= n;
    return out;
}
/* ===================== Structural Signals ===================== */
function containsGoCodeBlock(s) {
    return /```+\s*go([\s\S]*?)```/i.test(s) || /\bfunc\s+\w+\s*\(.*\)\s*\w*\s*{/.test(s);
}
function containsCodeBlock(s) {
    return /```+/.test(s) || /{[^}]*}/.test(s);
}
function headingQueryMatch(head, q) {
    const ht = unique(tokenize(head));
    const qt = new Set(tokenize(q));
    if (ht.length === 0 || qt.size === 0)
        return 0;
    let hit = 0;
    for (const t of ht)
        if (qt.has(t))
            hit++;
    return hit / ht.length;
}
function jaccard$1(a, b) {
    const A = new Set(tokenize(a));
    const B = new Set(tokenize(b));
    let inter = 0;
    for (const t of A)
        if (B.has(t))
            inter++;
    const uni = A.size + B.size - inter;
    return uni === 0 ? 0 : inter / uni;
}
function golangSpecFlag(s) {
    return /(golang\.org|go\.dev|pkg\.go\.dev)/i.test(s) ? 1 : 0;
}
function buildFeatures$1(q, chunk, qTfIdf, cTfIdf, qTfRaw, cTfRaw, stats, cLen, projQ, projC) {
    var _a;
    const f = [];
    const names = [];
    // 1) Sparse sims
    const cos = cosine$1(qTfIdf, cTfIdf);
    f.push(cos);
    names.push("cosine_tfidf");
    const bm25 = bm25Score(qTfRaw, cTfRaw, stats, cLen);
    f.push(bm25);
    names.push("bm25");
    // 2) Heading & lexical overlaps
    const hMatch = headingQueryMatch(chunk.heading || "", q);
    f.push(hMatch);
    names.push("heading_match_frac");
    const jac = jaccard$1(q, chunk.content || "");
    f.push(jac);
    names.push("jaccard_tokens");
    // 3) Structural flags
    const hasGo = containsGoCodeBlock(chunk.rich || chunk.content || "");
    const hasCode = containsCodeBlock(chunk.rich || chunk.content || "");
    f.push(hasGo ? 1 : 0);
    names.push("flag_go_code");
    f.push(hasCode ? 1 : 0);
    names.push("flag_any_code");
    // 4) Source cues
    f.push(golangSpecFlag(chunk.content || "") ? 1 : 0);
    names.push("flag_go_spec_link");
    // 5) Prior score (baseline)
    f.push(((_a = chunk.score_base) !== null && _a !== void 0 ? _a : 0));
    names.push("prior_score_base");
    // 6) Length heuristics (prefer concise answers)
    const lenChars = (chunk.content || "").length;
    f.push(1 / Math.sqrt(1 + lenChars));
    names.push("len_inv_sqrt");
    // 7) Dense hint from projection
    if (projQ && projC) {
        let dot = 0, l1 = 0;
        for (let i = 0; i < projQ.length; i++) {
            dot += projQ[i] * projC[i];
            l1 += Math.abs(projQ[i] - projC[i]);
        }
        f.push(dot);
        names.push("proj_dot");
        f.push(l1 / projQ.length);
        names.push("proj_l1mean");
    }
    return { names, values: f };
}
/* ======================== Ridge Model ======================== */
class Ridge {
    constructor() {
        this.w = null;
        this.mu = null;
        this.sigma = null;
    }
    fit(X, y, lambda = 1e-2) {
        var _a;
        const n = X.length;
        const d = ((_a = X[0]) === null || _a === void 0 ? void 0 : _a.length) || 0;
        if (n === 0 || d === 0) {
            this.w = new Float64Array(d);
            return;
        }
        // standardize
        const mu = new Float64Array(d);
        const sig = new Float64Array(d);
        for (let j = 0; j < d; j++) {
            let m = 0;
            for (let i = 0; i < n; i++)
                m += X[i][j];
            m /= n;
            mu[j] = m;
            let v = 0;
            for (let i = 0; i < n; i++) {
                const z = X[i][j] - m;
                v += z * z;
            }
            sig[j] = Math.sqrt(v / n) || 1;
        }
        const Z = Array.from({ length: n }, (_, i) => new Float64Array(d));
        for (let i = 0; i < n; i++)
            for (let j = 0; j < d; j++)
                Z[i][j] = (X[i][j] - mu[j]) / sig[j];
        // A = Z^T Z + λI, Zy = Z^T y
        const A = Array.from({ length: d }, () => new Float64Array(d));
        const Zy = new Float64Array(d);
        for (let i = 0; i < n; i++) {
            const zi = Z[i];
            const yi = y[i];
            for (let j = 0; j < d; j++) {
                Zy[j] += zi[j] * yi;
                const zij = zi[j];
                for (let k = 0; k <= j; k++)
                    A[j][k] += zij * zi[k];
            }
        }
        for (let j = 0; j < d; j++) {
            for (let k = 0; k < j; k++)
                A[k][j] = A[j][k];
            A[j][j] += lambda;
        }
        // Cholesky solve
        const L = Array.from({ length: d }, () => new Float64Array(d));
        for (let i = 0; i < d; i++) {
            for (let j = 0; j <= i; j++) {
                let sum = A[i][j];
                for (let k = 0; k < j; k++)
                    sum -= L[i][k] * L[j][k];
                L[i][j] = (i === j) ? Math.sqrt(Math.max(sum, 1e-12)) : (sum / (L[j][j] || 1e-12));
            }
        }
        const z = new Float64Array(d);
        for (let i = 0; i < d; i++) {
            let s = Zy[i];
            for (let k = 0; k < i; k++)
                s -= L[i][k] * z[k];
            z[i] = s / (L[i][i] || 1e-12);
        }
        const w = new Float64Array(d);
        for (let i = d - 1; i >= 0; i--) {
            let s = z[i];
            for (let k = i + 1; k < d; k++)
                s -= L[k][i] * w[k];
            w[i] = s / (L[i][i] || 1e-12);
        }
        this.w = w;
        this.mu = mu;
        this.sigma = sig;
    }
    predict(x) {
        if (!this.w || !this.mu || !this.sigma)
            return 0;
        let s = 0;
        for (let j = 0; j < this.w.length; j++) {
            const z = (x[j] - this.mu[j]) / this.sigma[j];
            s += this.w[j] * z;
        }
        return s;
    }
}
/* ===================== Weak Supervision ===================== */
function generateWeakLabel(q, chunk, feats) {
    var _a;
    const txt = (chunk.rich || chunk.content || "");
    let y = 0;
    const qIsGoFunc = /\bgo\b/.test(q.toLowerCase()) && /(define|declare|function|func)/i.test(q);
    if (qIsGoFunc && containsGoCodeBlock(txt))
        y = Math.max(y, 1.0);
    const headHit = headingQueryMatch(chunk.heading || "", q);
    if (headHit >= 0.34 && containsCodeBlock(txt))
        y = Math.max(y, 0.8);
    const cosIdx = feats.names.indexOf("cosine_tfidf");
    const bm25Idx = feats.names.indexOf("bm25");
    const cos = cosIdx >= 0 ? feats.values[cosIdx] : 0;
    const bm = bm25Idx >= 0 ? feats.values[bm25Idx] : 0;
    if (cos > 0.25)
        y = Math.max(y, 0.6);
    if (bm > 1.0)
        y = Math.max(y, 0.6);
    const priorIdx = feats.names.indexOf("prior_score_base");
    const prior = priorIdx >= 0 ? feats.values[priorIdx] : 0;
    if (((_a = chunk.score_base) !== null && _a !== void 0 ? _a : 0) > 0)
        y = Math.max(y, Math.min(0.6, 0.2 + 0.5 * prior));
    return y;
}
function sigmoid(x) {
    if (x >= 0) {
        const z = Math.exp(-x);
        return 1 / (1 + z);
    }
    else {
        const z = Math.exp(x);
        return z / (1 + z);
    }
}
/* ========================= MMR Filter ========================= */
function mmrFilter(scored, lambda = 0.7, budgetChars = 1200) {
    const sel = [];
    const docs = scored.map(s => s.content || "");
    const { stats, tf: tfList } = buildCorpusStats(docs);
    const tfidf = tfList.map(tf => tfidfVector(tf, stats.idf));
    const selectedIdx = new Set();
    let used = 0;
    while (selectedIdx.size < scored.length) {
        let bestIdx = -1, bestVal = -Infinity;
        for (let i = 0; i < scored.length; i++) {
            if (selectedIdx.has(i))
                continue;
            const cand = scored[i];
            let red = 0;
            for (const j of selectedIdx) {
                const sim = cosine$1(tfidf[i], tfidf[j]);
                if (sim > red)
                    red = sim;
            }
            const val = lambda * cand.score_rr - (1 - lambda) * red;
            if (val > bestVal) {
                bestVal = val;
                bestIdx = i;
            }
        }
        if (bestIdx < 0)
            break;
        const chosen = scored[bestIdx];
        const addLen = (chosen.content || "").length;
        if (used + addLen > budgetChars && sel.length > 0)
            break;
        sel.push(chosen);
        used += addLen;
        selectedIdx.add(bestIdx);
    }
    return sel;
}
/* ========================= Public API ========================= */
/** Train per-query ridge model and score chunks. */
function rerank(query, chunks, opts = {}) {
    var _a, _b;
    // License check removed // Premium feature - requires valid license
    const { lambdaRidge = 1e-2, randomProjDim = 32, exposeFeatures = true, attachFeatureNames = false, } = opts;
    const docs = [query, ...chunks.map(c => c.content || "")];
    const { stats, tf: tfRaw, docLens } = buildCorpusStats(docs);
    const tfidfAll = tfRaw.map(tf => tfidfVector(tf, stats.idf));
    const qTfRaw = tfRaw[0];
    const qTfIdf = tfidfAll[0];
    const projQ = randomProjDim > 0 ? projectSparse(qTfIdf, randomProjDim) : undefined;
    const X = [];
    const y = [];
    const featPacks = [];
    for (let i = 0; i < chunks.length; i++) {
        const c = chunks[i];
        const cTfRaw = tfRaw[i + 1];
        const cTfIdf = tfidfAll[i + 1];
        const projC = randomProjDim > 0 ? projectSparse(cTfIdf, randomProjDim, 1337 + i) : undefined;
        const feats = buildFeatures$1(query, c, qTfIdf, cTfIdf, qTfRaw, cTfRaw, stats, docLens[i + 1] || 1, projQ, projC);
        featPacks.push(feats);
        X.push(feats.values);
        const label = generateWeakLabel(query, c, feats);
        y.push(label);
    }
    const allSame = y.every(v => Math.abs(v - y[0]) < 1e-9);
    if (allSame) {
        const cosIdx = featPacks[0].names.indexOf("cosine_tfidf");
        if (cosIdx >= 0) {
            for (let i = 0; i < y.length; i++)
                y[i] = Math.max(0, Math.min(1, 0.2 + 0.6 * X[i][cosIdx]));
        }
    }
    const rr = new Ridge();
    rr.fit(X, y, lambdaRidge);
    let minS = Infinity, maxS = -Infinity;
    const rawScores = X.map(x => rr.predict(x));
    for (const s of rawScores) {
        if (s < minS)
            minS = s;
        if (s > maxS)
            maxS = s;
    }
    const range = Math.max(1e-9, maxS - minS);
    const featureNames = attachFeatureNames ? (_b = (_a = featPacks[0]) === null || _a === void 0 ? void 0 : _a.names) !== null && _b !== void 0 ? _b : [] : undefined;
    const scored = chunks.map((c, i) => {
        const s01 = (rawScores[i] - minS) / range;
        const p = sigmoid((rawScores[i] - 0.5 * (minS + maxS)) / (0.2 * range + 1e-6));
        const base = Object.assign(Object.assign({}, c), { score_rr: s01, p_relevant: p });
        if (exposeFeatures)
            base._features = X[i];
        if (featureNames)
            base._feature_names = featureNames;
        return base;
    });
    scored.sort((a, b) => b.score_rr - a.score_rr);
    return scored;
}
/** Filter scored chunks using probability/near-top thresholds and MMR coverage. */
function filterMMR(scored, opts = {}) {
    // License check removed // Premium feature - requires valid license
    const { probThresh = 0.45, epsilonTop = 0.05, useMMR = true, mmrLambda = 0.7, budgetChars = 1200 } = opts;
    if (scored.length === 0)
        return [];
    const top = scored[0].score_rr;
    const bandKept = scored.filter(s => s.p_relevant >= probThresh && s.score_rr >= (top - epsilonTop));
    const seed = bandKept.length > 0 ? bandKept : [scored[0]];
    if (!useMMR) {
        const out = [];
        let used = 0;
        for (const s of seed) {
            const add = (s.content || "").length;
            if (used + add > budgetChars && out.length > 0)
                break;
            out.push(s);
            used += add;
        }
        return out;
    }
    const boosted = scored.map(s => (Object.assign(Object.assign({}, s), { score_rr: seed.includes(s) ? s.score_rr + 0.01 : s.score_rr })));
    return mmrFilter(boosted, mmrLambda, budgetChars);
}
/** Convenience: run rerank then filter. */
function rerankAndFilter(query, chunks, opts = {}) {
    // License check removed // Premium feature - requires valid license
    const scored = rerank(query, chunks, opts);
    return filterMMR(scored, opts);
}
function importModel(model, opts) {
    // License check removed // Premium feature - requires valid license
    if (model.version !== 'astermind-pro-v1' && model.version !== 'astermind-elm-v1') {
        throw new Error(`Unsupported model version: ${model.version}. Expected 'astermind-pro-v1' or 'astermind-elm-v1'`);
    }
    // 1) restore settings
    const settings = JSON.parse(JSON.stringify(model.settings || {}));
    // 2) vocab & idf
    const vocabMap = new Map(model.vocab);
    const idf = Float64Array.from(model.idf); // keep as number[] for compatibility
    // 3) chunks
    const chunks = model.chunks.map(c => ({
        heading: c.heading,
        content: c.content || '',
        rich: c.rich,
        level: c.level,
        secId: c.secId
    }));
    // 4) tfidfDocs from pairs
    const tfidfDocs = model.tfidfDocs.map(row => {
        const m = new Map();
        for (const [i, v] of row)
            m.set(i, v);
        return m;
    });
    // 5) Nyström landmarks
    const landmarksIdx = Array.from(model.landmarksIdx);
    const landmarkMat = model.landmarkMat.map(a => Float64Array.from(a));
    // 6) denseDocs: use stored or recompute
    const needRecompute = !model.denseDocs || model.denseDocs.length !== tfidfDocs.length;
    let denseDocs;
    if (needRecompute) {
        throw new Error('recomputeDense=true but buildDense function not provided');
    }
    else {
        denseDocs = model.denseDocs.map(a => Float64Array.from(a));
    }
    return {
        settings,
        vocabMap,
        idf,
        chunks,
        tfidfDocs,
        landmarksIdx,
        landmarkMat,
        denseDocs,
    };
}

// src/omega/OmegaOfflineRAG.ts
// Omega-powered offline RAG using @astermind/astermind-community
/**
 * Omega-powered offline RAG class
 *
 * Uses @astermind/astermind-community for hybrid retrieval and reranking.
 * Provides enhanced offline capabilities over simple TF-IDF fallback.
 */
class OmegaOfflineRAG {
    constructor(config) {
        this.modelState = null;
        this.model = null;
        this.config = {
            reranking: {
                enabled: config?.reranking?.enabled ?? true,
                lambdaRidge: config?.reranking?.lambdaRidge ?? 0.1,
                useMMR: config?.reranking?.useMMR ?? true,
                mmrLambda: config?.reranking?.mmrLambda ?? 0.5,
                probThresh: config?.reranking?.probThresh ?? 0.1
            },
            topK: config?.topK ?? 5,
            verbose: config?.verbose ?? false
        };
    }
    /**
     * Load a pre-exported model from SerializedModel object
     */
    async loadModel(model) {
        const startTime = performance.now();
        this.model = model;
        this.modelState = importModel(model);
        if (this.config.verbose) {
            const loadTime = performance.now() - startTime;
            console.log(`[OmegaOfflineRAG] Model loaded in ${loadTime.toFixed(0)}ms`);
            console.log(`[OmegaOfflineRAG] ${this.modelState.chunks.length} chunks, ${this.modelState.vocabMap.size} vocab`);
        }
    }
    /**
     * Load model from a URL
     */
    async loadModelFromUrl(url) {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch model from ${url}: ${response.status}`);
        }
        const model = await response.json();
        await this.loadModel(model);
    }
    /**
     * Check if model is loaded and ready
     */
    isReady() {
        return this.modelState !== null;
    }
    /**
     * Get model information
     */
    getModelInfo() {
        if (!this.model || !this.modelState)
            return null;
        return {
            version: this.model.version,
            savedAt: this.model.savedAt,
            documentCount: this.modelState.chunks.length,
            vocabularySize: this.modelState.vocabMap.size,
            landmarkCount: this.modelState.landmarksIdx.length,
            hasDenseVectors: this.modelState.denseDocs.length > 0,
            settings: this.model.settings
        };
    }
    /**
     * Perform RAG query and return answer with sources
     */
    async ask(query) {
        const startTime = performance.now();
        if (!this.modelState || !this.model) {
            return {
                answer: 'Omega model not loaded. Please load a model first.',
                confidence: 'none',
                sources: [],
                processingTime: 0,
                degraded: true,
                degradedReason: 'Model not loaded'
            };
        }
        try {
            // Extract settings with proper type assertions
            const settings = this.model.settings;
            const kernel = settings.kernel ?? 'rbf';
            const sigma = settings.sigma ?? 1.0;
            const alpha = settings.alpha ?? 0.5;
            const beta = settings.beta ?? 0.3;
            const ridge = settings.ridge ?? 0.001;
            const headingW = settings.headingW ?? 0.2;
            const useStem = settings.useStem ?? true;
            const expandQuery = settings.expandQuery ?? false;
            const prefilter = settings.prefilter ?? 100;
            // Perform hybrid retrieval
            const retrievalResult = hybridRetrieve({
                query,
                chunks: this.modelState.chunks,
                vocabMap: this.modelState.vocabMap,
                idf: this.modelState.idf,
                tfidfDocs: this.modelState.tfidfDocs,
                denseDocs: this.modelState.denseDocs,
                landmarksIdx: this.modelState.landmarksIdx,
                landmarkMat: this.modelState.landmarkMat,
                vocabSize: this.modelState.vocabMap.size,
                kernel: kernel,
                sigma,
                alpha,
                beta,
                ridge,
                headingW,
                useStem,
                expandQuery,
                topK: this.config.topK * 2, // Get more for reranking
                prefilter
            });
            let sources;
            // Apply reranking if enabled
            if (this.config.reranking.enabled && retrievalResult.items.length > 0) {
                const chunksForRerank = retrievalResult.items.map((item, idx) => ({
                    heading: item.heading,
                    content: item.content,
                    rich: item.rich,
                    score_base: retrievalResult.scores[idx]
                }));
                const reranked = rerankAndFilter(query, chunksForRerank, {
                    lambdaRidge: this.config.reranking.lambdaRidge,
                    useMMR: this.config.reranking.useMMR,
                    mmrLambda: this.config.reranking.mmrLambda,
                    probThresh: this.config.reranking.probThresh
                });
                sources = reranked.slice(0, this.config.topK).map((chunk) => ({
                    heading: chunk.heading,
                    content: chunk.content,
                    rich: chunk.rich,
                    score: chunk.score_base ?? 0,
                    rerankScore: chunk.score_rr,
                    probability: chunk.p_relevant
                }));
            }
            else {
                sources = retrievalResult.items.slice(0, this.config.topK).map((item, idx) => ({
                    heading: item.heading,
                    content: item.content,
                    rich: item.rich,
                    score: retrievalResult.scores[idx]
                }));
            }
            // Compose answer using Omega
            const answer = await omegaComposeAnswer(query, sources.map(s => ({ heading: s.heading, content: s.content, score: s.score })));
            const processingTime = performance.now() - startTime;
            // Determine confidence based on scores
            const topScore = sources[0]?.score ?? 0;
            const confidence = this.determineConfidence(topScore, sources.length);
            // Classify query type
            const queryClassification = this.classifyQuery(query);
            return {
                answer,
                confidence,
                sources,
                processingTime,
                degraded: false,
                queryClassification
            };
        }
        catch (error) {
            const processingTime = performance.now() - startTime;
            console.error('[OmegaOfflineRAG] Error during retrieval:', error);
            return {
                answer: 'An error occurred while processing your question offline.',
                confidence: 'none',
                sources: [],
                processingTime,
                degraded: true,
                degradedReason: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }
    /**
     * Retrieve relevant chunks without generating an answer
     */
    async retrieve(query, topK) {
        if (!this.modelState || !this.model) {
            return [];
        }
        const k = topK ?? this.config.topK;
        // Extract settings with proper type assertions
        const settings = this.model.settings;
        const kernel = settings.kernel ?? 'rbf';
        const sigma = settings.sigma ?? 1.0;
        const alpha = settings.alpha ?? 0.5;
        const beta = settings.beta ?? 0.3;
        const ridge = settings.ridge ?? 0.001;
        const headingW = settings.headingW ?? 0.2;
        const useStem = settings.useStem ?? true;
        const expandQuery = settings.expandQuery ?? false;
        const prefilter = settings.prefilter ?? 100;
        const retrievalResult = hybridRetrieve({
            query,
            chunks: this.modelState.chunks,
            vocabMap: this.modelState.vocabMap,
            idf: this.modelState.idf,
            tfidfDocs: this.modelState.tfidfDocs,
            denseDocs: this.modelState.denseDocs,
            landmarksIdx: this.modelState.landmarksIdx,
            landmarkMat: this.modelState.landmarkMat,
            vocabSize: this.modelState.vocabMap.size,
            kernel: kernel,
            sigma,
            alpha,
            beta,
            ridge,
            headingW,
            useStem,
            expandQuery,
            topK: k,
            prefilter
        });
        return retrievalResult.items.map((item, idx) => ({
            heading: item.heading,
            content: item.content,
            rich: item.rich,
            score: retrievalResult.scores[idx]
        }));
    }
    /**
     * Classify query type for better handling
     */
    classifyQuery(query) {
        const lowerQuery = query.toLowerCase();
        const words = lowerQuery.split(/\s+/);
        // Simple keyword-based classification
        const factualKeywords = ['what', 'who', 'when', 'where', 'which', 'is', 'are', 'was', 'were'];
        const proceduralKeywords = ['how', 'steps', 'guide', 'tutorial', 'process', 'procedure'];
        const conceptualKeywords = ['why', 'explain', 'describe', 'meaning', 'definition', 'concept'];
        const navigationKeywords = ['go to', 'navigate', 'find', 'page', 'link', 'take me', 'show me'];
        let type = 'unknown';
        let confidence = 0.5;
        const keywords = [];
        // Check navigation first (phrase matching)
        for (const phrase of navigationKeywords) {
            if (lowerQuery.includes(phrase)) {
                type = 'navigational';
                confidence = 0.8;
                keywords.push(phrase);
                break;
            }
        }
        if (type === 'unknown') {
            // Check other types
            const firstWord = words[0];
            if (proceduralKeywords.includes(firstWord) || proceduralKeywords.some(k => lowerQuery.includes(k))) {
                type = 'procedural';
                confidence = 0.7;
                keywords.push(...proceduralKeywords.filter(k => lowerQuery.includes(k)));
            }
            else if (conceptualKeywords.includes(firstWord) || conceptualKeywords.some(k => lowerQuery.includes(k))) {
                type = 'conceptual';
                confidence = 0.7;
                keywords.push(...conceptualKeywords.filter(k => lowerQuery.includes(k)));
            }
            else if (factualKeywords.includes(firstWord)) {
                type = 'factual';
                confidence = 0.7;
                keywords.push(firstWord);
            }
        }
        return { type, confidence, keywords };
    }
    /**
     * Determine confidence level based on retrieval scores
     */
    determineConfidence(topScore, sourceCount) {
        if (sourceCount === 0)
            return 'none';
        if (topScore >= 0.7)
            return 'medium'; // Never 'high' for offline
        if (topScore >= 0.4)
            return 'medium';
        if (topScore >= 0.2)
            return 'low';
        return 'low';
    }
    /**
     * Clear loaded model
     */
    clear() {
        this.modelState = null;
        this.model = null;
    }
}

var OmegaOfflineRAG$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    OmegaOfflineRAG: OmegaOfflineRAG
});

// src/index.ts
// Main package exports (core + tree-shakeable agentic)
// Core exports
function createClient(config) {
    return new CyberneticClient(config);
}

export { ApiClient, CyberneticAgent, CyberneticCache, CyberneticClient, CyberneticIntentClassifier, CyberneticLocalRAG, CyberneticOfflineStorage, CyberneticSessionStorage, LicenseManager, OmegaOfflineRAG, REQUIRED_FEATURES, SalesApiClient, SiteMapDiscovery, WebSocketTransport, configLoaders, createClient, createDiscoveryConfig, createLicenseManager, deriveWsUrl, detectEnvironment, getEnforcementMode, getTokenExpiration, isValidJWTFormat, loadConfig, registerAgenticCapabilities, resolveWsUrl, useSiteMapDiscovery, validateConfig, verifyLicenseToken };
//# sourceMappingURL=cybernetic-chatbot-client.esm.js.map
